/**
 * Main entry — sets up canvas, engine loop, scene switching.
 *
 * Per-frame:
 *   1. Snapshot prev transforms (velocity source).
 *   2. Tick CSS keyframe animations on each node.
 *   3. Run node.update() for ad-hoc animated nodes.
 *   4. Apply inertia — physics-style fling after pointer release.
 *   5. Compile scene → instance buffer.
 *   6. Pass 1: render to scene FBO.
 *   7. Pass 2: postprocess → default framebuffer.
 */

import { compile, type CompiledScene } from "./compiler";
import { createFBO, createSceneFBO, createVelocityFBO, type FBO, type VelocityInfo } from "./gl/fbo";
import { initGL, resizeCanvas } from "./gl/glContext";
import {
  createRectPipeline,
  drawRects,
  ensureWhiteTex,
  NO_BACKDROP,
  NO_MIX_BLEND,
  type RectPipeline,
} from "./gl/rectPipeline";
import {
  createCosmicDustComposite,
  drawCosmicDust,
  type CosmicDustComposite,
} from "./gl/cosmicDustPipeline";
import {
  createPostProcessPipeline,
  drawPostProcess,
  type PostProcessPipeline,
} from "./gl/postProcess";
import {
  createBackdropCompositePipeline,
  drawBackdropComposite,
  type BackdropCompositePipeline,
} from "./gl/backdropComposite";
import { bindHud, updateHud } from "./hud";
import {
  computeReleaseVelocity,
  createPointer,
  hitTest,
  recordPointerSample,
  setViewportSize,
  stepInertia,
} from "./input";
import { tickAnimation } from "./animation";
import { tickScroll, resetScrollState } from "./scroll";
import {
  createScene,
  snapshotPrev,
  sortedNodes,
  setHovered,
  type NodeId,
  type Scene,
  type SceneNode,
} from "./sceneGraph";
import { SCENES } from "./scenes/demos";
import { PAGE_SCENES } from "./scenes/pageDemos";
import { sceneAdvanced } from "./scenes/advancedDemo";
import { sceneLanding } from "./scenes/landingDemo";
import { sceneIntro } from "./scenes/intro";
import { sceneLab } from "./scenes/lab";
import { sceneAllLayers } from "./scenes/showcase";
import { sceneMaisonVerre } from "./scenes/maisonVerre";
import { sceneGallery } from "./scenes/gallery";
import { sceneConstellation } from "./scenes/constellation";
import { sceneLiveVideo } from "./scenes/liveVideo";
import {
  createMyceliumDomPipeline,
  drawMyceliumDom,
  computeMyceliumDomBands,
  computeMyceliumDomRealtimeBands,
  type MyceliumDomPipeline,
} from "./gl/myceliumDomPipeline";
import {
  createDislocationFieldPipeline,
  drawDislocationField,
  computeDislocationFieldBands,
  computeDislocationFieldRealtimeBands,
  type DislocationFieldPipeline,
} from "./gl/dislocationFieldPipeline";
import {
  createDislocationFieldLogosPipeline,
  drawDislocationFieldLogos,
  computeDislocationFieldLogosBands,
  computeDislocationFieldLogosRealtimeBands,
  type DislocationFieldLogosPipeline,
} from "./gl/dislocationFieldLogosPipeline";
import {
  createMarangoniInkPipeline,
  drawMarangoniInk,
  computeMarangoniInkBands,
  computeMarangoniInkRealtimeBands,
  type MarangoniInkPipeline,
} from "./gl/marangoniInkPipeline";
import {
  createMcColloughPipeline,
  drawMcCollough,
  computeMcColloughBands,
  computeMcColloughRealtimeBands,
  type McColloughPipeline,
} from "./gl/mcColloughPipeline";
import {
  createHopfFibrationPipeline,
  drawHopfFibration,
  computeHopfFibrationBands,
  computeHopfFibrationRealtimeBands,
  type HopfFibrationPipeline,
} from "./gl/hopfFibrationPipeline";
import {
  createSlitScanChronoPipeline,
  drawSlitScanChrono,
  computeSlitScanChronoBands,
  computeSlitScanChronoRealtimeBands,
  type SlitScanChronoPipeline,
} from "./gl/slitScanChronoPipeline";
import {
  createGlassPipeline,
  drawGlass,
  computeGlassBands,
  computeGlassRealtimeBands,
  type GlassPipeline,
} from "./gl/glassPipeline";
import { sceneWomensFashion } from "./scenes/womensfashion";
import { sceneAtticEase } from "./scenes/atticEase";

/**
 * sceneChain — empty scene graph; the actual rendering is a fullscreen
 * 3D raymarcher (IQ's chain link SDF, MIT) drawn through a dedicated
 * pipeline registered as `customDraw` on the engine. See
 * `gl/chainPipeline.ts` for the shader.
 */
function sceneChain(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMorph — empty scene graph. The actual rendering is a morphing
 * iridescent 3D shape (sphere → torus → box → octahedron → soft crystal
 * with smin blending) plus a slow-moving nebula background. See
 * `gl/morphPipeline.ts` for the shader.
 */
function sceneMorph(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCine — empty scene graph. The actual rendering is a single
 * fullscreen fragment shader that raymarches a 3D blob with concentric
 * ring detail on a deep purple background, with strong magenta/pink rim
 * lighting and animated motion. See `gl/cinePipeline.ts`.
 */
function sceneCine(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBeat — empty scene graph. The actual rendering is a volumetric
 * raymarcher (Noztol's "Beat Nebula" Shadertoy, MIT) — 9 frequency
 * bands drive 3-color spatial patterns, with nested sin(dot) detail,
 * space folding, and tanh tone mapping. See `gl/beatNebulaPipeline.ts`.
 */
function sceneBeat(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneHex — empty scene graph. The actual rendering is a hex-grid
 * shader (Shadertoy "Bioluminescent Hex Cells — Audio Reactive") that
 * draws bioluminescent hexagonal tiles with body + inner ring + core +
 * sparkle, all driven by bass/mid/high audio bands. See
 * `gl/hexPipeline.ts`.
 */
function sceneHex(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCoral — empty scene graph. The actual rendering is a branching
 * wireframe coral colony with bioluminescent tips and twinkling
 * organisms advected by underwater current. See `gl/coralPipeline.ts`.
 */
function sceneCoral(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneVines — empty scene graph. The actual rendering is 6 helix
 * strands wrapping an unseen trunk, with pulsing leaves at fixed
 * arc-length intervals. See `gl/vinesPipeline.ts`.
 */
function sceneVines(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneNeuralNet — empty scene graph. The actual rendering is a
 * sparse dynamic graph of 48 firing neurons whose connections are
 * drawn between near-neighbors with a traveling pulse. See
 * `gl/neuralNetPipeline.ts`.
 */
function sceneNeuralNet(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneVoronoiCrystal — empty scene graph. The actual rendering is a
 * 3D-faceted voronoi field with dancing internal caustics and
 * iridescent edge glow. See `gl/voronoiCrystalPipeline.ts`.
 */
function sceneVoronoiCrystal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMetaballVol — empty scene graph. The actual rendering is 24
 * metaballs orbiting in 2D-projected Lissajous paths, iso-surfaced
 * for a smooth body with specular + colored rim glow. See
 * `gl/metaballVolPipeline.ts`.
 */
function sceneMetaballVol(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSmokeColumns — empty scene graph. The actual rendering is 6
 * colorful smoke columns rising from a fire, advected by curl-noise
 * turbulence, with twinkling embers. See `gl/smokeColumnsPipeline.ts`.
 */
function sceneSmokeColumns(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLiquidMetalVol — empty scene graph. The actual rendering is
 * 12 mercury blobs with deep environment reflections, specular
 * highlights from a moving light, and a dark slate stage. See
 * `gl/liquidMetalVolPipeline.ts`.
 */
function sceneLiquidMetalVol(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneAurora — empty scene graph. The actual rendering is a vertical
 * aurora-band shader: 3 layered ribbons of cyan/violet/pink flowing
 * horizontally with FBM distortion, plus a star field and high-audio
 * sparkles. See `gl/auroraPipeline.ts`.
 */
function sceneAurora(_w: number, _h: number): Scene {
  return createScene();
}


/**
 * sceneAurora3D — empty scene graph. Volumetric 3D aurora ribbons.
 * See `gl/aurora3DPipeline.ts`.
 */
/**
 * sceneBeatNebula — empty scene graph. Beat-driven nebula.
 */
function sceneBeatNebula(_w: number, _h: number): Scene {
  return createScene();
}

function sceneAurora3D(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCloth — empty scene graph. GPU cloth simulation with spring-
 * mass constraints and self-collision. See `gl/clothPipeline.ts`.
 */
function sceneCloth(_w: number, _h: number): Scene {
  return createScene();
}
function sceneLissajousSymph(_w:number,_h:number):Scene{return createScene();}
function sceneRibbonTracer(_w:number,_h:number):Scene{return createScene();}
function sceneGalacticCore(_w:number,_h:number):Scene{return createScene();}
function scenePendulumCasc(_w:number,_h:number):Scene{return createScene();}
function sceneBifrost(_w:number,_h:number):Scene{return createScene();}
function sceneKaleidoTunnel(_w:number,_h:number):Scene{return createScene();}

/**
 * sceneVortex — empty scene graph. The actual rendering is a radial
 * spiral converging on a bright central core, with 5 rotating arms in
 * cyan/violet/cool palette and audio-reactive sparkles. See
 * `gl/vortexPipeline.ts`.
 */
function sceneVortex(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSupernova — empty scene graph. The actual rendering is an
 * expanding shockwave from a central point with hot white → orange →
 * red → violet color gradient, secondary shockwaves, and ejecta
 * filaments. See `gl/supernovaPipeline.ts`.
 */
function sceneSupernova(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePlasma — empty scene graph. The actual rendering is a wall of
 * flame: domain-warped FBM with yellow → orange → red → dark red
 * palette, embers rising, and audio-reactive flicker. See
 * `gl/plasmaPipeline.ts`.
 */
function scenePlasma(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDrift — empty scene graph. The actual rendering is a flow-field
 * particle simulation: 64 particles drifting along a curl-noise vector
 * field, drawn as soft Gaussian glows. See `gl/driftPipeline.ts`.
 */
function sceneDrift(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLatticePulse — empty scene graph. The actual rendering is a
 * square lattice of nodes whose amplitudes propagate as a
 * dispersion-like wavefront. See `gl/latticePulsePipeline.ts`.
 */

/**
 * sceneChronoStrand — empty scene graph. The actual rendering is a
 * braided set of 4D worldlines (3D position + time) projected as
 * age-graded strands. See `gl/chronoStrandPipeline.ts`.
 */
function sceneChronoStrand(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneRiverPaths — empty scene graph. The actual rendering is in
 * `gl/riverPathsPipeline.ts`.
 */
function sceneRiverPaths(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneEmbroidery — empty scene graph. The actual rendering is in
 * `gl/embroideryPipeline.ts`.
 */
function sceneEmbroidery(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneKaleidoscopeTouch — empty scene graph. The actual rendering is in
 * `gl/kaleidoscopeTouchPipeline.ts`.
 */
function sceneKaleidoscopeTouch(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneCracklePattern — empty scene graph. The actual rendering is in
 * `gl/cracklePatternPipeline.ts`.
 */
function sceneCracklePattern(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneVineGrowth — empty scene graph. The actual rendering is in
 * `gl/vineGrowthPipeline.ts`.
 */
function sceneVineGrowth(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * scenePaperFold — empty scene graph. The actual rendering is in
 * `gl/paperFoldPipeline.ts`.
 */
function scenePaperFold(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneLichenGrowth — empty scene graph. The actual rendering is in
 * `gl/lichenGrowthPipeline.ts`.
 */
function sceneLichenGrowth(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneWaveInterference — empty scene graph. The actual rendering is in
 * `gl/waveInterferencePipeline.ts`.
 */
function sceneWaveInterference(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneStainedGlassTouch — empty scene graph. The actual rendering is in
 * `gl/stainedGlassTouchPipeline.ts`.
 */
function sceneStainedGlassTouch(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneWebSpinning — empty scene graph. The actual rendering is in
 * `gl/webSpinningPipeline.ts`.
 */
function sceneWebSpinning(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * scenePlasmaTouch — empty scene graph. The actual rendering is in
 * `gl/plasmaTouchPipeline.ts`.
 */
function scenePlasmaTouch(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneRustGrowth — empty scene graph. The actual rendering is in
 * `gl/rustGrowthPipeline.ts`.
 */
function sceneRustGrowth(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneBubbleChamber — empty scene graph. The actual rendering is in
 * `gl/bubbleChamberPipeline.ts`.
 */
function sceneBubbleChamber(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneTidePool — empty scene graph. The actual rendering is in
 * `gl/tidePoolPipeline.ts`.
 */
function sceneTidePool(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneSugarCrystal — empty scene graph. The actual rendering is in
 * `gl/sugarCrystalPipeline.ts`.
 */
function sceneSugarCrystal(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneWindBlownGrass — empty scene graph. The actual rendering is in
 * `gl/windBlownGrassPipeline.ts`.
 */
function sceneWindBlownGrass(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneAuroraTouch — empty scene graph. The actual rendering is in
 * `gl/auroraTouchPipeline.ts`.
 */
function sceneAuroraTouch(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneSandPile — empty scene graph. The actual rendering is in
 * `gl/sandPilePipeline.ts`.
 */
function sceneSandPile(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneBubbleBlow — empty scene graph. The actual rendering is in
 * `gl/bubbleBlowPipeline.ts`.
 */
function sceneBubbleBlow(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneSmokeRing — empty scene graph. The actual rendering is in
 * `gl/smokeRingPipeline.ts`.
 */
function sceneSmokeRing(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneCrystalGrowth — empty scene graph. The actual rendering is in
 * `gl/crystalGrowthPipeline.ts`.
 */
function sceneCrystalGrowth(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneLightningWeb — empty scene graph. The actual rendering is in
 * `gl/lightningWebPipeline.ts`.
 */
function sceneLightningWeb(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneCrackedGlass — empty scene graph. The actual rendering is in
 * `gl/crackedGlassPipeline.ts`.
 */
function sceneCrackedGlass(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * scenePetrifiedBloom — empty scene graph. The actual rendering is in
 * `gl/petrifiedBloomPipeline.ts`.
 */
function scenePetrifiedBloom(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneMagneticFilings — empty scene graph. The actual rendering is in
 * `gl/magneticFilingsPipeline.ts`.
 */
function sceneMagneticFilings(_w: number, _h: number): Scene {
  const scene = createScene();
  let pointerX = 0;
  let pointerY = 0;
  let pointerDown = false;
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pointerX = x;
      pointerY = y;
      pointerDown = down;
    };
  return scene;
}

/**
 * sceneHalo — empty scene graph. The actual rendering is in
 * `gl/haloPipeline.ts`.
 */
function sceneHalo(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLissajousTube — empty scene graph. The actual rendering is in
 * `gl/lissajousTubePipeline.ts`.
 */
function sceneLissajousTube(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSnowflake — empty scene graph. The actual rendering is in
 * `gl/snowflakePipeline.ts`.
 */
function sceneSnowflake(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneNegativeSpace — empty scene graph. The actual rendering is in
 * `gl/negativeSpacePipeline.ts`.
 */
function sceneNegativeSpace(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTrichromatic — empty scene graph. The actual rendering is in
 * `gl/trichromaticPipeline.ts`.
 */
function sceneTrichromatic(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSolarCorona — empty scene graph. The actual rendering is in
 * `gl/solarCoronaPipeline.ts`.
 */
function sceneSolarCorona(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDendriticCrystal — empty scene graph. The actual rendering is in
 * `gl/dendriticCrystalPipeline.ts`.
 */
function sceneDendriticCrystal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTyndallBeam — empty scene graph. The actual rendering is in
 * `gl/tyndallBeamPipeline.ts`.
 */
function sceneTyndallBeam(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBaroclinicVortex — empty scene graph. The actual rendering is in
 * `gl/baroclinicVortexPipeline.ts`.
 */
function sceneBaroclinicVortex(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDislocationWeb — empty scene graph. The actual rendering is in
 * `gl/dislocationWebPipeline.ts`.
 */
function sceneDislocationWeb(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneChladniCells — empty scene graph. The actual rendering is in
 * `gl/chladniCellsPipeline.ts`.
 */
function sceneChladniCells(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCrookesRadiometer — empty scene graph. The actual rendering is in
 * `gl/crookesRadiometerPipeline.ts`.
 */
function sceneCrookesRadiometer(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneGranularFlow — empty scene graph. The actual rendering is in
 * `gl/granularFlowPipeline.ts`.
 */
function sceneGranularFlow(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSavartWheel — empty scene graph. The actual rendering is in
 * `gl/savartWheelPipeline.ts`.
 */
function sceneSavartWheel(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneRijkeTube — empty scene graph. The actual rendering is in
 * `gl/rijkeTubePipeline.ts`.
 */
function sceneRijkeTube(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneFerrofluidSculpture — empty scene graph. The actual rendering is in
 * `gl/ferrofluidSculpturePipeline.ts`.
 */
function sceneFerrofluidSculpture(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSigils — empty scene graph. The actual rendering is in
 * `gl/sigilsPipeline.ts`.
 */
function sceneSigils(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneUmagneticMonopole — empty scene graph. The actual rendering is in
 * `gl/magneticMonopolePipeline.ts`.
 */
function sceneUmagneticMonopole(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneUtimeCrystal — empty scene graph. The actual rendering is in
 * `gl/timeCrystalPipeline.ts`.
 */
function sceneUtimeCrystal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneUpenroseStairs — empty scene graph. The actual rendering is in
 * `gl/penroseStairsPipeline.ts`.
 */
function sceneUpenroseStairs(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneUquantumFoam — empty scene graph. The actual rendering is in
 * `gl/quantumFoamPipeline.ts`.
 */
function sceneUquantumFoam(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneUfractalSmoke — empty scene graph. The actual rendering is in
 * `gl/fractalSmokePipeline.ts`.
 */
function sceneUfractalSmoke(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneAnyonicBraiding — empty scene graph. The actual rendering is in
 * `gl/anyonicBraidingPipeline.ts`.
 */
function sceneAnyonicBraiding(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneHydrodynamicShock — empty scene graph. The actual rendering is in
 * `gl/hydrodynamicShockPipeline.ts`.
 */
function sceneHydrodynamicShock(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSchwarzschildCrossing — empty scene graph. The actual rendering is in
 * `gl/schwarzschildCrossingPipeline.ts`.
 */
function sceneSchwarzschildCrossing(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDynamo — empty scene graph. The actual rendering is in
 * `gl/dynamoPipeline.ts`.
 */
function sceneDynamo(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePhaseConjugate — empty scene graph. The actual rendering is in
 * `gl/phaseConjugatePipeline.ts`.
 */
function scenePhaseConjugate(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSpectralDecomposition — empty scene graph. The actual rendering is in
 * `gl/spectralDecompositionPipeline.ts`.
 */
function sceneSpectralDecomposition(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneAnamorphic — empty scene graph. The actual rendering is in
 * `gl/anamorphicPipeline.ts`.
 */
function sceneAnamorphic(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneFeynmanPath — empty scene graph. The actual rendering is in
 * `gl/feynmanPathPipeline.ts`.
 */
function sceneFeynmanPath(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBec — empty scene graph. The actual rendering is in
 * `gl/becPipeline.ts`.
 */
function sceneBec(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePhotonicCrystal — empty scene graph. The actual rendering is in
 * `gl/photonicCrystalPipeline.ts`.
 */
function scenePhotonicCrystal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTokamakPlasma — empty scene graph. The actual rendering is in
 * `gl/tokamakPlasmaPipeline.ts`.
 */
function sceneTokamakPlasma(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMobiusLattice — empty scene graph. The actual rendering is in
 * `gl/mobiusLatticePipeline.ts`.
 */
function sceneMobiusLattice(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTesseractShadow — empty scene graph. The actual rendering is in
 * `gl/tesseractShadowPipeline.ts`.
 */
function sceneTesseractShadow(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTwinflame — empty scene graph. The actual rendering is in
 * `gl/twinflamePipeline.ts`.
 */
function sceneTwinflame(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMulberry — empty scene graph. The actual rendering is in
 * `gl/mulberryPipeline.ts`.
 */
function sceneMulberry(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBifrostMirror — empty scene graph. The actual rendering is in
 * `gl/bifrostMirrorPipeline.ts`.
 */
function sceneBifrostMirror(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneStalactitePulse — empty scene graph. The actual rendering is in
 * `gl/stalactitePulsePipeline.ts`.
 */
function sceneStalactitePulse(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCatenaryLoom — empty scene graph. The actual rendering is in
 * `gl/catenaryLoomPipeline.ts`.
 */
function sceneCatenaryLoom(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLightwing — empty scene graph. The actual rendering is in
 * `gl/lightwingPipeline.ts`.
 */
function sceneLightwing(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCataract — empty scene graph. The actual rendering is in
 * `gl/cataractPipeline.ts`.
 */
function sceneCataract(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneAspic — empty scene graph. The actual rendering is in
 * `gl/aspicPipeline.ts`.
 */
function sceneAspic(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMoirefall — empty scene graph. The actual rendering is in
 * `gl/moirefallPipeline.ts`.
 */
function sceneMoirefall(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTideglass — empty scene graph. The actual rendering is in
 * `gl/tideglassPipeline.ts`.
 */
function sceneTideglass(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSunTongue — empty scene graph. The actual rendering is in
 * `gl/sunTonguePipeline.ts`.
 */
function sceneSunTongue(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTessellationMelt — empty scene graph. The actual rendering is in
 * `gl/tessellationMeltPipeline.ts`.
 */
function sceneTessellationMelt(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTessellationMeltTouch — empty scene graph. The actual rendering
 * is in `gl/tessellationMeltTouchPipeline.ts`. Like the original, but
 * the cells are alive to the pointer: hover deforms the grid toward
 * the cursor, clicks send out shockwaves. Driven by the engine's
 * pointer state every frame.
 */
function sceneTessellationMeltTouch(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMagneticMonopoleTouch — empty scene graph. The actual rendering
 * is in `gl/magneticMonopoleTouchPipeline.ts`. The monopole is a
 * particle you can grab: the pointer moves the monopole around,
 * clicks flip the polarity.
 */
function sceneMagneticMonopoleTouch(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneFractalSmokeTouch — empty scene graph. The actual rendering is
 * in `gl/fractalSmokeTouchPipeline.ts`. The smoke reacts to your finger:
 * hover blows the smoke aside, click adds a turbulence burst.
 */
function sceneFractalSmokeTouch(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTesseractShadowTouch — empty scene graph. The actual rendering is
 * in `gl/tesseractShadowTouchPipeline.ts`. The 4D hypercube rotates
 * toward the cursor, click adds a breathe pulse.
 */
function sceneTesseractShadowTouch(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneResonance — empty scene graph. The actual rendering is in
 * `gl/resonancePipeline.ts`.
 */
function sceneResonance(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneFontainefeed — empty scene graph. The actual rendering is in
 * `gl/fontainefeedPipeline.ts`.
 */
function sceneFontainefeed(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePendantBloom — empty scene graph. The actual rendering is in
 * `gl/pendantBloomPipeline.ts`.
 */
function scenePendantBloom(_w: number, _h: number): Scene {
  return createScene();
}

function sceneLatticePulse(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBloom — empty scene graph. The actual rendering is a real
 * Gray-Scott reaction-diffusion simulation on a 256x256 RGBA16F texture,
 * ping-ponged through 12 update steps per frame. Patterns emerge
 * automatically based on (F, k) parameters that cycle over time.
 * See `gl/bloomPipeline.ts`.
 */
function sceneBloom(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMesh — empty scene graph. The actual rendering is an animated
 * network graph: 32 nodes drifting in lissajous orbits, each connected
 * to its K=3 nearest neighbors with glowing edges. See
 * `gl/meshPipeline.ts`.
 */
function sceneMesh(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneOrbits — empty scene graph. The actual rendering is a solar
 * system simulation: a central sun with 5 planets on elliptical orbits
 * (Kepler's equation), each leaving a fading trail. See
 * `gl/orbitsPipeline.ts`.
 */
function sceneOrbits(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCymatics — empty scene graph. The actual rendering is Chladni
 * figures: sin(n*pi*x)*sin(m*pi*y) wave interference on a square
 * plate, with (n, m) cycling over time. See `gl/cymaticsPipeline.ts`.
 */
function sceneCymatics(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMandelbrot — empty scene graph. The actual rendering is a real-
 * time zoom into the Mandelbrot fractal with smooth escape-time
 * coloring. See `gl/mandelbrotPipeline.ts`.
 */
function sceneMandelbrot(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneKaleidoscope — empty scene graph. 6-fold symmetric mirror fold
 * of a procedural pattern. See `gl/kaleidoscopePipeline.ts`.
 */
function sceneKaleidoscope(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLorenz — empty scene graph. Lorenz attractor integrated per
 * pixel, projected to 2D, colored by z-coordinate. See
 * `gl/lorenzPipeline.ts`.
 */
function sceneLorenz(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePhyllotaxis — empty scene graph. Fibonacci sunflower spirals
 * (golden angle). See `gl/phyllotaxisPipeline.ts`.
 */
function scenePhyllotaxis(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneQuasicrystal — empty scene graph. Penrose-style 5-direction
 * wave interference producing a quasi-periodic pattern. See
 * `gl/quasicrystalPipeline.ts`.
 */
function sceneQuasicrystal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTunnel — empty scene graph. Tunnel of light rings receding
 * into infinity. See `gl/tunnelPipeline.ts`.
 */
function sceneTunnel(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneFerrofluid — empty scene graph. Spiky liquid-metal patterns
 * simulating ferrofluid response to a magnetic field. See
 * `gl/ferrofluidPipeline.ts`.
 */
function sceneFerrofluid(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePolyhedron — empty scene graph. Raymarched morphing 3D
 * polyhedra (sphere → box → octahedron → torus → dodecahedron). See
 * `gl/polyhedronPipeline.ts`.
 */
function scenePolyhedron(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLightning — empty scene graph. Recursive fractal lightning
 * bolts flashing across a stormy sky. See `gl/lightningPipeline.ts`.
 */
function sceneLightning(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCaustics — empty scene graph. Refracted light through wavy
 * water surface, producing the characteristic curved caustic lines.
 * See `gl/causticsPipeline.ts`.
 */
function sceneCaustics(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneWorleyTerrain — empty scene graph. Voronoi-based mountain
 * landscape with simple Lambert lighting. See
 * `gl/worleyTerrainPipeline.ts`.
 */
function sceneWorleyTerrain(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMagnetic — empty scene graph. Iron filings around two
 * magnetic poles, drawn as field-line integrals. See
 * `gl/magneticPipeline.ts`.
 */
function sceneMagnetic(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLSystem — empty scene graph. Recursive plant growth with
 * branching and leaves. See `gl/lSystemPipeline.ts`.
 */
function sceneLSystem(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneTruchet — empty scene graph. Morphing Truchet tile pattern
 * with quarter-circle arcs. See `gl/truchetPipeline.ts`.
 */
function sceneTruchet(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSunburst — empty scene graph. Radiating sunburst with morphing
 * rays. See `gl/sunburstPipeline.ts`.
 */
function sceneSunburst(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSpirograph — empty scene graph. Hypotrochoid curves (spirograph)
 * layered at different parameters. See `gl/spirographPipeline.ts`.
 */
function sceneSpirograph(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDNA — empty scene graph. Rotating double helix with pulsing
 * rungs. See `gl/dnaPipeline.ts`.
 */
function sceneDNA(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLavaLamp — empty scene graph. Lava lamp with metaballs
 * (smooth-blended blobs). See `gl/lavaLampPipeline.ts`.
 */
function sceneLavaLamp(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBezier — empty scene graph. Flowing 3D bezier ribbon
 * twisting through space. See `gl/bezierPipeline.ts`.
 */
function sceneBezier(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneStereographic — empty scene graph. Stereographic projection
 * of a sphere with noise pattern. See `gl/stereographicPipeline.ts`.
 */
function sceneStereographic(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneContour — empty scene graph. Topographic contour lines
 * rendered from a noise height field. See `gl/contourPipeline.ts`.
 */
function sceneContour(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCosmos — empty scene graph. 3D volumetric nebula raymarcher
 * with stars and lens flare. See `gl/cosmosPipeline.ts`.
 */
function sceneCosmos(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneGalaxy — empty scene graph. 4-arm spiral galaxy with
 * hundreds of glowing stars and a bright nucleus. See
 * `gl/galaxyPipeline.ts`.
 */
function sceneGalaxy(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneOceanStorm — empty scene graph. 3D stormy ocean with
 * multi-octave Gerstner waves, foam, and lightning flashes. See
 * `gl/oceanStormPipeline.ts`.
 */
function sceneOceanStorm(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCyberpunk — empty scene graph. 3D raymarched cyberpunk street
 * with neon-lit buildings and wet reflective road. See
 * `gl/cyberpunkPipeline.ts`.
 */
function sceneCyberpunk(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCrystalCave — empty scene graph. 3D raymarched cluster of
 * octahedral crystals with iridescent lighting. See
 * `gl/crystalCavePipeline.ts`.
 */
function sceneCrystalCave(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSoapBubble — empty scene graph. Soap bubble with thin-film
 * interference creating iridescent rainbow. See
 * `gl/soapBubblePipeline.ts`.
 */
function sceneSoapBubble(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneVoronoiGlass — empty scene graph. Voronoi cells of translucent
 * colored glass with light shining through. See
 * `gl/voronoiGlassPipeline.ts`.
 */
function sceneVoronoiGlass(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePixelSort — empty scene graph. Pixel sorting by brightness,
 * glitch art aesthetic. See `gl/pixelSortPipeline.ts`.
 */
function scenePixelSort(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneWireframeSphere — empty scene graph. 3D raymarched sphere with
 * audio-driven displacement and lat/lon wireframe lines. See
 * `gl/wireframeSpherePipeline.ts`.
 */
function sceneWireframeSphere(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBlackHole — empty scene graph. Black hole with gravitational
 * lensing approximation, accretion disk, Einstein ring. See
 * `gl/blackHolePipeline.ts`.
 */
function sceneBlackHole(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLiquidMetal — empty scene graph. Mercury-like flowing metal
 * blob with mirror reflections and Fresnel rim. See
 * `gl/liquidMetalPipeline.ts`.
 */
function sceneLiquidMetal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneStainedGlass — empty scene graph. Circular stained-glass window
 * with concentric rings and radial slices. See
 * `gl/stainedGlassPipeline.ts`.
 */
function sceneStainedGlass(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMarbleStatue — empty scene graph. Marble sphere with
 * turbulence-driven veining pattern, soft shadows. See
 * `gl/marbleStatuePipeline.ts`.
 */
function sceneMarbleStatue(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePsychedelic — empty scene graph. Domain-warped FBM with RGB
 * chromatic aberration. See `gl/psychedelicPipeline.ts`.
 */
function scenePsychedelic(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneGlitch — empty scene graph. Datamosh-style horizontal slice
 * shifting + RGB split + block corruption. See
 * `gl/glitchPipeline.ts`.
 */
function sceneGlitch(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneQuantumField — empty scene graph. 2D quantum harmonic
 * oscillator probability density visualization. See
 * `gl/quantumFieldPipeline.ts`.
 */
function sceneQuantumField(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneParticleConstellation — empty scene graph. 60 particles with
 * trails orbiting around the center. See
 * `gl/particleConstellationPipeline.ts`.
 */
function sceneParticleConstellation(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneInkInWater — empty scene graph. Five plumes of colored ink
 * dispersing in water with FBM distortion. See
 * `gl/inkInWaterPipeline.ts`.
 */
function sceneInkInWater(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneWormholeTunnel — empty scene graph. 3D raymarched tunnel of
 * light with chromatic aberration. See
 * `gl/wormholeTunnelPipeline.ts`.
 */
function sceneWormholeTunnel(_w: number, _h: number): Scene {
  return createScene();
}
/**
 * sceneTeslaCoil — empty scene graph. The actual rendering is a sparks + magnetic field fullscreen
 * fragment shader. See `gl/teslaCoilPipeline.ts`.
 */
function sceneTeslaCoil(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePlasmaGlobe — empty scene graph. The actual rendering is a filament tendrils fullscreen
 * fragment shader. See `gl/plasmaGlobePipeline.ts`.
 */
function scenePlasmaGlobe(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSolarFlare — empty scene graph. The actual rendering is a cme eruption fullscreen
 * fragment shader. See `gl/solarFlarePipeline.ts`.
 */
function sceneSolarFlare(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneKleinFilm — empty scene graph. The actual rendering is a thin-film interference fullscreen
 * fragment shader. See `gl/kleinFilmPipeline.ts`.
 */
function sceneKleinFilm(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneStarForge — empty scene graph. The actual rendering is a stellar nursery fullscreen
 * fragment shader. See `gl/starForgePipeline.ts`.
 */
function sceneStarForge(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneVoronoiBubbles — empty scene graph. The actual rendering is a cellular foam fullscreen
 * fragment shader. See `gl/voronoiBubblesPipeline.ts`.
 */
function sceneVoronoiBubbles(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneWindstream — empty scene graph. The actual rendering is a trailing flow fullscreen
 * fragment shader. See `gl/windstreamPipeline.ts`.
 */
function sceneWindstream(_w: number, _h: number): Scene {
  return createScene();
}
function sceneSiliconSkin(_w: number, _h: number): Scene {
  return createScene();
}

function sceneViscoelastic(_w: number, _h: number): Scene {
  return createScene();
}

function sceneBalloonPress(_w: number, _h: number): Scene {
  return createScene();
}

function sceneMembraneDrum(_w: number, _h: number): Scene {
  return createScene();
}

function scenePunchBag(_w: number, _h: number): Scene {
  return createScene();
}

function sceneSiliconeFace(_w: number, _h: number): Scene {
  return createScene();
}

function sceneLiquidSkin(_w: number, _h: number): Scene {
  return createScene();
}

function sceneClayPress(_w: number, _h: number): Scene {
  return createScene();
}

function sceneDrumHead3D(_w: number, _h: number): Scene {
  return createScene();
}

function sceneSiliconeVeil(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLiveVideo — NSPP-native video playback. Uses a fullscreen image
 * node whose imageUrl cycles through 201 frames extracted from the selfie
 * video at 12fps. Built entirely on NSPP scene-graph primitives:
 * makeImage + makeRect (vignette) + makeText (title, frame counter, REC).
 * See `scenes/liveVideo.ts` for the factory.
 */
function sceneLiveVideoLocal(w: number, h: number): Scene {
  return sceneLiveVideo(w, h);
}

/**
 * sceneMyceliumDom — empty stub. See `gl/myceliumDomPipeline.ts` for the actual shader.
 */
function sceneMyceliumDom(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDislocationField — empty stub. See `gl/dislocationFieldPipeline.ts` for the actual shader.
 */
function sceneDislocationField(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDislocationFieldLogos — empty stub. Same shader family as
 * sceneDislocationField but with social-media logos stamped into each
 * grain's core. See `gl/dislocationFieldLogosPipeline.ts`.
 */
function sceneDislocationFieldLogos(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMarangoniInk — empty stub. See `gl/marangoniInkPipeline.ts` for the actual shader.
 */
function sceneMarangoniInk(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMcCollough — empty stub. See `gl/mcColloughPipeline.ts` for the actual shader.
 */
function sceneMcCollough(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneHopfFibration — empty stub. See `gl/hopfFibrationPipeline.ts` for the actual shader.
 */
function sceneHopfFibration(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSlitScanChrono — empty stub. See `gl/slitScanChronoPipeline.ts` for the actual shader.
 */
function sceneSlitScanChrono(_w: number, _h: number): Scene {
  return createScene();
}
function sceneGlass(_w: number, _h: number): Scene {
  return createScene();
}



/**
 * sceneAscii — empty scene graph. The entire scene rendered as ASCII
 * characters with terminal aesthetic. See `gl/asciiPipeline.ts`.
 */
function sceneAscii(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneInkDrop — empty scene graph. Falling ink drop with expanding
 * ripples on impact. See `gl/inkDropPipeline.ts`.
 */
function sceneInkDrop(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneGyroid — empty scene graph. 3D raymarched gyroid surface
 * with iridescent lighting. See `gl/gyroidPipeline.ts`.
 */
function sceneGyroid(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSchwarzP — empty scene graph. 3D raymarched Schwarz P
 * minimal surface. See `gl/schwarzPPipeline.ts`.
 */
function sceneSchwarzP(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMengerSponge — empty scene graph. 3D raymarched Menger
 * sponge fractal. See `gl/mengerSpongePipeline.ts`.
 */
function sceneMengerSponge(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneKleinBottle — empty scene graph. Klein bottle rendered with
 * surface point lookup. See `gl/kleinBottlePipeline.ts`.
 */
function sceneKleinBottle(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneConwayLife — empty scene graph. Conway's Game of Life
 * running on the GPU. See `gl/conwayLifePipeline.ts`.
 */
function sceneConwayLife(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneStableFluid — empty scene graph. Stam's stable 2D fluid
 * simulation on the GPU. See `gl/stableFluidPipeline.ts`.
 */
function sceneStableFluid(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneDoublePendulum — empty scene graph. Chaotic double pendulum
 * with traced trajectory. See `gl/doublePendulumPipeline.ts`.
 */
function sceneDoublePendulum(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSphericalHarmonics — empty scene graph. Real spherical
 * harmonic visualization. See `gl/sphericalHarmonicsPipeline.ts`.
 */
function sceneSphericalHarmonics(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBEC — empty scene graph. Bose-Einstein condensate wave
 * function. See `gl/becPipeline.ts`.
 */
function sceneBEC(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePhononLattice — empty scene graph. 2D spring-mass phonon
 * lattice. See `gl/phononLatticePipeline.ts`.
 */
function scenePhononLattice(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneMagneticPendulum — empty scene graph. Magnetic pendulum
 * chaos with 3 magnets. See `gl/magneticPendulumPipeline.ts`.
 */
function sceneMagneticPendulum(_w: number, _h: number): Scene {
  return createScene();
}



/**
 * sceneSchwarzD — empty scene graph. Schwarz Diamond minimal
 * surface. See `gl/schwarzDPipeline.ts`.
 */
function sceneSchwarzD(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneLiquidCrystal — empty scene graph. Nematic liquid crystal
 * director field. See `gl/liquidCrystalPipeline.ts`.
 */
function sceneLiquidCrystal(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSincWaveField — empty scene graph. sinc(x)*sinc(y) field
 * visualization. See `gl/sincWaveFieldPipeline.ts`.
 */
function sceneSincWaveField(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSineGordon — empty scene graph. Sine-Gordon soliton PDE
 * simulation. See `gl/sineGordonPipeline.ts`.
 */
function sceneSineGordon(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneSkyrmion — empty scene graph. Magnetic skyrmion topological
 * spin texture. See `gl/skyrmionPipeline.ts`.
 */
function sceneSkyrmion(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneHessianEigenvector — empty scene graph. Eigenvector field of
 * the Hessian of an FBM field. See `gl/hessianEigenvectorPipeline.ts`.
 */
function sceneHessianEigenvector(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneBraggDiffraction — empty scene graph. X-ray diffraction
 * pattern of a crystal lattice. See `gl/braggDiffractionPipeline.ts`.
 */
function sceneBraggDiffraction(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCurvatureFlow — empty scene graph. Mean curvature flow on a
 * 2D level set. See `gl/curvatureFlowPipeline.ts`.
 */
function sceneCurvatureFlow(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneCellularPotts — empty scene graph. Cellular Potts Model
 * simulation. See `gl/cellularPottsPipeline.ts`.
 */
function sceneCellularPotts(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * sceneWaveFunctionCollapse — empty scene graph. Wave Function
 * Collapse procedural generation. See `gl/waveFunctionCollapsePipeline.ts`.
 */
function sceneWaveFunctionCollapse(_w: number, _h: number): Scene {
  return createScene();
}

/**
 * scenePhaseFieldCrystal — empty scene graph. Phase Field Crystal
 * simulation. See `gl/phaseFieldCrystalPipeline.ts`.
 */
function scenePhaseFieldCrystal(_w: number, _h: number): Scene {
  return createScene();
}

function sceneMandala(_w: number, _h: number): Scene { return createScene(); }
function scenePhantasm(_w: number, _h: number): Scene { return createScene(); }
function sceneHoneycombTide(_w: number, _h: number): Scene { return createScene(); }
function sceneEqualCathedral(_w: number, _h: number): Scene { return createScene(); }
function sceneSoundWave3D(_w: number, _h: number): Scene { return createScene(); }
function scenePhonograph(_w: number, _h: number): Scene { return createScene(); }
function sceneCymaticsPlate(_w: number, _h: number): Scene { return createScene(); }
import { sdfLibraryScene } from "./scenes/sdfLibrary";
import {
  paintDemoRoute,
  type PaintRefs,
} from "./scenes/paintvisualizer";
import {
  chaseDemoRoute,
  type ChaseRefs,
} from "./scenes/chase";
import { recordScene, downloadBlob, extensionForRecordOpts } from "./recordMp4";
import { createChainPipeline, drawChain, type ChainPipeline } from "./gl/chainPipeline";
import { createMorphPipeline, drawMorph, type MorphPipeline } from "./gl/morphPipeline";
import { createCinePipeline, drawCine, type CinePipeline } from "./gl/cinePipeline";
import {
  createBeatNebulaPipeline,
  drawBeatNebula,
  computeSyntheticBands,
  computeRealtimeBands,
  type BeatNebulaPipeline,
} from "./gl/beatNebulaPipeline";
import {
  createHexPipeline,
  drawHex,
  computeHexBands,
  computeHexRealtimeBands,
  type HexPipeline,
} from "./gl/hexPipeline";
import {
  createCoralPipeline,
  drawCoral,
  computeCoralBands,
  computeCoralRealtimeBands,
  type CoralPipeline,
} from "./gl/coralPipeline";
import {
  createVinesPipeline,
  drawVines,
  computeVinesBands,
  computeVinesRealtimeBands,
  type VinesPipeline,
} from "./gl/vinesPipeline";
import {
  createNeuralNetPipeline,
  drawNeuralNet,
  computeNeuralNetBands,
  computeNeuralNetRealtimeBands,
  type NeuralNetPipeline,
} from "./gl/neuralNetPipeline";
import {
  createVoronoiCrystalPipeline,
  drawVoronoiCrystal,
  computeVoronoiCrystalBands,
  computeVoronoiCrystalRealtimeBands,
  type VoronoiCrystalPipeline,
} from "./gl/voronoiCrystalPipeline";
import {
  createMetaballVolPipeline,
  drawMetaballVol,
  computeMetaballVolBands,
  computeMetaballVolRealtimeBands,
  type MetaballVolPipeline,
} from "./gl/metaballVolPipeline";
import {
  createSmokeColumnsPipeline,
  drawSmokeColumns,
  computeSmokeColumnsBands,
  computeSmokeColumnsRealtimeBands,
  type SmokeColumnsPipeline,
} from "./gl/smokeColumnsPipeline";
import {
  createLiquidMetalVolPipeline,
  drawLiquidMetalVol,
  computeLiquidMetalVolBands,
  computeLiquidMetalVolRealtimeBands,
  type LiquidMetalVolPipeline,
} from "./gl/liquidMetalVolPipeline";
import {
  createAuroraPipeline,
  drawAurora,
  computeAuroraBands,
  computeAuroraRealtimeBands,
  type AuroraPipeline,
} from "./gl/auroraPipeline";
import {
  createVortexPipeline,
  drawVortex,
  computeVortexBands,
  computeVortexRealtimeBands,
  type VortexPipeline,
} from "./gl/vortexPipeline";
import {
  createSupernovaPipeline,
  drawSupernova,
  computeSupernovaBands,
  computeSupernovaRealtimeBands,
  type SupernovaPipeline,
} from "./gl/supernovaPipeline";
import {
  createPlasmaPipeline,
  drawPlasma,
  computePlasmaBands,
  computePlasmaRealtimeBands,
  type PlasmaPipeline,
} from "./gl/plasmaPipeline";
import {
  createDriftPipeline,
  drawDrift,
  computeDriftBands,
  computeDriftRealtimeBands,
  type DriftPipeline,
} from "./gl/driftPipeline";
import {
  createLatticePulsePipeline,
  drawLatticePulse,
  computeLatticePulseBands,
  computeLatticePulseRealtimeBands,
  type LatticePulsePipeline,
} from "./gl/latticePulsePipeline";
import {
  createChronoStrandPipeline,
  drawChronoStrand,
  computeChronoStrandBands,
  computeChronoStrandRealtimeBands,
  type ChronoStrandPipeline,
} from "./gl/chronoStrandPipeline";
import {
  createBeatNebulaPipeline,
  drawBeatNebula,
  computeSyntheticBands,
  computeRealtimeBands,
  type BeatNebulaPipeline,
} from "./gl/beatNebulaPipeline";

import {
  createBECPipeline,
  drawBEC,
  computeBECBands,
  computeBECRealtimeBands,
  type BECPipeline,
} from "./gl/becPipeline";


import {
  createBloomPipeline,
  initBloom,
  drawBloom,
  type BloomPipeline,
} from "./gl/bloomPipeline";
import {
  createMeshPipeline,
  drawMesh,
  computeMeshBands,
  computeMeshRealtimeBands,
  type MeshPipeline,
} from "./gl/meshPipeline";
import {
  createOrbitsPipeline,
  drawOrbits,
  computeOrbitsBands,
  computeOrbitsRealtimeBands,
  type OrbitsPipeline,
} from "./gl/orbitsPipeline";
import {
  createCymaticsPipeline,
  drawCymatics,
  computeCymaticsBands,
  computeCymaticsRealtimeBands,
  type CymaticsPipeline,
} from "./gl/cymaticsPipeline";
import {
  createMandelbrotPipeline,
  drawMandelbrot,
  computeMandelbrotBands,
  computeMandelbrotRealtimeBands,
  type MandelbrotPipeline,
} from "./gl/mandelbrotPipeline";
import {
  createKaleidoscopePipeline,
  drawKaleidoscope,
  computeKaleidoscopeBands,
  computeKaleidoscopeRealtimeBands,
  type KaleidoscopePipeline,
} from "./gl/kaleidoscopePipeline";
import {
  createLorenzPipeline,
  drawLorenz,
  computeLorenzBands,
  computeLorenzRealtimeBands,
  type LorenzPipeline,
} from "./gl/lorenzPipeline";
import {
  createPhyllotaxisPipeline,
  drawPhyllotaxis,
  computePhyllotaxisBands,
  computePhyllotaxisRealtimeBands,
  type PhyllotaxisPipeline,
} from "./gl/phyllotaxisPipeline";
import {
  createQuasicrystalPipeline,
  drawQuasicrystal,
  computeQuasicrystalBands,
  computeQuasicrystalRealtimeBands,
  type QuasicrystalPipeline,
} from "./gl/quasicrystalPipeline";
import {
  createTunnelPipeline,
  drawTunnel,
  computeTunnelBands,
  computeTunnelRealtimeBands,
  type TunnelPipeline,
} from "./gl/tunnelPipeline";
import {
  createFerrofluidPipeline,
  drawFerrofluid,
  computeFerrofluidBands,
  computeFerrofluidRealtimeBands,
  type FerrofluidPipeline,
} from "./gl/ferrofluidPipeline";
import {
  createPolyhedronPipeline,
  drawPolyhedron,
  computePolyhedronBands,
  computePolyhedronRealtimeBands,
  type PolyhedronPipeline,
} from "./gl/polyhedronPipeline";
import {
  createLightningPipeline,
  drawLightning,
  computeLightningBands,
  computeLightningRealtimeBands,
  type LightningPipeline,
} from "./gl/lightningPipeline";
import {
  createCausticsPipeline,
  drawCaustics,
  computeCausticsBands,
  computeCausticsRealtimeBands,
  type CausticsPipeline,
} from "./gl/causticsPipeline";
import {
  createWorleyTerrainPipeline,
  drawWorleyTerrain,
  computeWorleyTerrainBands,
  computeWorleyTerrainRealtimeBands,
  type WorleyTerrainPipeline,
} from "./gl/worleyTerrainPipeline";
import {
  createMagneticPipeline,
  drawMagnetic,
  computeMagneticBands,
  computeMagneticRealtimeBands,
  type MagneticPipeline,
} from "./gl/magneticPipeline";
import {
  createLSystemPipeline,
  drawLSystem,
  computeLSystemBands,
  computeLSystemRealtimeBands,
  type LSystemPipeline,
} from "./gl/lSystemPipeline";
import {
  createTruchetPipeline,
  drawTruchet,
  computeTruchetBands,
  computeTruchetRealtimeBands,
  type TruchetPipeline,
} from "./gl/truchetPipeline";
import {
  createSunburstPipeline,
  drawSunburst,
  computeSunburstBands,
  computeSunburstRealtimeBands,
  type SunburstPipeline,
} from "./gl/sunburstPipeline";
import {
  createSpirographPipeline,
  drawSpirograph,
  computeSpirographBands,
  computeSpirographRealtimeBands,
  type SpirographPipeline,
} from "./gl/spirographPipeline";
import {
  createSiliconSkinPipeline,
  drawSiliconSkin,
  computeSiliconSkinBands,
  computeSiliconSkinRealtimeBands,
  type SiliconSkinPipeline,
} from "./gl/siliconSkinPipeline";
import {
  createLiveVideoPipeline,
  loadLiveVideoElement,
  drawLiveVideo,
  computeLiveVideoBands,
  computeLiveVideoRealtimeBands,
  type LiveVideoPipeline,
} from "./gl/liveVideoPipeline";
import {
  createViscoelasticPipeline,
  drawViscoelastic,
  computeViscoelasticBands,
  computeViscoelasticRealtimeBands,
  type ViscoelasticPipeline,
} from "./gl/viscoelasticPipeline";
import {
  createBalloonPressPipeline,
  drawBalloonPress,
  computeBalloonPressBands,
  computeBalloonPressRealtimeBands,
  type BalloonPressPipeline,
} from "./gl/balloonPressPipeline";
import {
  createMembraneDrumPipeline,
  drawMembraneDrum,
  computeMembraneDrumBands,
  computeMembraneDrumRealtimeBands,
  type MembraneDrumPipeline,
} from "./gl/membraneDrumPipeline";
import {
  createPunchBagPipeline,
  drawPunchBag,
  computePunchBagBands,
  computePunchBagRealtimeBands,
  type PunchBagPipeline,
} from "./gl/punchBagPipeline";
import {
  createSiliconeFacePipeline,
  drawSiliconeFace,
  computeSiliconeFaceBands,
  computeSiliconeFaceRealtimeBands,
  type SiliconeFacePipeline,
} from "./gl/siliconeFacePipeline";
import {
  createLiquidSkinPipeline,
  drawLiquidSkin,
  computeLiquidSkinBands,
  computeLiquidSkinRealtimeBands,
  type LiquidSkinPipeline,
} from "./gl/liquidSkinPipeline";
import {
  createClayPressPipeline,
  drawClayPress,
  computeClayPressBands,
  computeClayPressRealtimeBands,
  type ClayPressPipeline,
} from "./gl/clayPressPipeline";
import {
  createDrumHead3DPipeline,
  drawDrumHead3D,
  computeDrumHead3DBands,
  computeDrumHead3DRealtimeBands,
  type DrumHead3DPipeline,
} from "./gl/drumHead3DPipeline";
import {
  createSiliconeVeilPipeline,
  drawSiliconeVeil,
  computeSiliconeVeilBands,
  computeSiliconeVeilRealtimeBands,
  type SiliconeVeilPipeline,
} from "./gl/siliconeVeilPipeline";
import {
  createLissajousSymphPipeline,
  drawLissajousSymph,
  computeLissajousSymphBands,
  computeLissajousSymphRealtimeBands,
  type LissajousSymphPipeline,
} from "./gl/lissajousSymphPipeline";
import {
  createRibbonTracerPipeline,
  drawRibbonTracer,
  computeRibbonTracerBands,
  computeRibbonTracerRealtimeBands,
  type RibbonTracerPipeline,
} from "./gl/ribbonTracerPipeline";
import {
  createGalacticCorePipeline,
  drawGalacticCore,
  computeGalacticCoreBands,
  computeGalacticCoreRealtimeBands,
  type GalacticCorePipeline,
} from "./gl/galacticCorePipeline";
import {
  createPendulumCascPipeline,
  drawPendulumCasc,
  computePendulumCascBands,
  computePendulumCascRealtimeBands,
  type PendulumCascPipeline,
} from "./gl/pendulumCascPipeline";
import {
  createBifrostPipeline,
  drawBifrost,
  computeBifrostBands,
  computeBifrostRealtimeBands,
  type BifrostPipeline,
} from "./gl/bifrostPipeline";
import {
  createKaleidoTunnelPipeline,
  drawKaleidoTunnel,
  computeKaleidoTunnelBands,
  computeKaleidoTunnelRealtimeBands,
  type KaleidoTunnelPipeline,
} from "./gl/kaleidoTunnelPipeline";
import {
  createMandalaPipeline,
  drawMandala,
  computeMandalaBands,
  computeMandalaRealtimeBands,
  type MandalaPipeline,
} from "./gl/mandalaPipeline";
import {
  createPhantasmPipeline,
  drawPhantasm,
  computePhantasmBands,
  computePhantasmRealtimeBands,
  type PhantasmPipeline,
} from "./gl/phantasmPipeline";
import {
  createHoneycombTidePipeline,
  drawHoneycombTide,
  computeHoneycombTideBands,
  computeHoneycombTideRealtimeBands,
  type HoneycombTidePipeline,
} from "./gl/honeycombTidePipeline";
import {
  createEqualCathedralPipeline,
  drawEqualCathedral,
  computeEqualCathedralBands,
  computeEqualCathedralRealtimeBands,
  type EqualCathedralPipeline,
} from "./gl/equalCathedralPipeline";
import {
  createPhonographPipeline,
  drawPhonograph,
  computePhonographBands,
  computePhonographRealtimeBands,
  type PhonographPipeline,
} from "./gl/phonographPipeline";
import {
  createCymaticsPlatePipeline,
  drawCymaticsPlate,
  computeCymaticsPlateBands,
  computeCymaticsPlateRealtimeBands,
  type CymaticsPlatePipeline,
} from "./gl/cymaticsPlatePipeline";
import {
  createAurora3DPipeline,
  drawAurora3D,
  computeAurora3DBands,
  computeAurora3DRealtimeBands,
  type Aurora3DPipeline,
} from "./gl/aurora3DPipeline";
import {
  createSoundWave3DPipeline,
  drawSoundWave3D,
  computeSoundWave3DBands,
  computeSoundWave3DRealtimeBands,
  type SoundWave3DPipeline,
} from "./gl/soundWave3DPipeline";
import {
  createClothPipeline,
  initCloth,
  stepCloth,
  type ClothPipeline,
} from "./gl/clothPipeline";

import {
  createDNAPipeline,
  drawDNA,
  computeDNABands,
  computeDNARealtimeBands,
  type DNAPipeline,
} from "./gl/dnaPipeline";
import {
  createLavaLampPipeline,
  drawLavaLamp,
  computeLavaLampBands,
  computeLavaLampRealtimeBands,
  type LavaLampPipeline,
} from "./gl/lavaLampPipeline";
import {
  createBezierPipeline,
  drawBezier,
  computeBezierBands,
  computeBezierRealtimeBands,
  type BezierPipeline,
} from "./gl/bezierPipeline";
import {
  createStereographicPipeline,
  drawStereographic,
  computeStereographicBands,
  computeStereographicRealtimeBands,
  type StereographicPipeline,
} from "./gl/stereographicPipeline";
import {
  createContourPipeline,
  drawContour,
  computeContourBands,
  computeContourRealtimeBands,
  type ContourPipeline,
} from "./gl/contourPipeline";
import {
  createCosmosPipeline,
  drawCosmos,
  computeCosmosBands,
  computeCosmosRealtimeBands,
  type CosmosPipeline,
} from "./gl/cosmosPipeline";
import {
  createGalaxyPipeline,
  drawGalaxy,
  computeGalaxyBands,
  computeGalaxyRealtimeBands,
  type GalaxyPipeline,
} from "./gl/galaxyPipeline";
import {
  createOceanStormPipeline,
  drawOceanStorm,
  computeOceanStormBands,
  computeOceanStormRealtimeBands,
  type OceanStormPipeline,
} from "./gl/oceanStormPipeline";
import {
  createCyberpunkPipeline,
  drawCyberpunk,
  computeCyberpunkBands,
  computeCyberpunkRealtimeBands,
  type CyberpunkPipeline,
} from "./gl/cyberpunkPipeline";
import {
  createCrystalCavePipeline,
  drawCrystalCave,
  computeCrystalCaveBands,
  computeCrystalCaveRealtimeBands,
  type CrystalCavePipeline,
} from "./gl/crystalCavePipeline";
import {
  createSoapBubblePipeline,
  drawSoapBubble,
  computeSoapBubbleBands,
  computeSoapBubbleRealtimeBands,
  type SoapBubblePipeline,
} from "./gl/soapBubblePipeline";
import {
  createVoronoiGlassPipeline,
  drawVoronoiGlass,
  computeVoronoiGlassBands,
  computeVoronoiGlassRealtimeBands,
  type VoronoiGlassPipeline,
} from "./gl/voronoiGlassPipeline";
import {
  createPixelSortPipeline,
  drawPixelSort,
  computePixelSortBands,
  computePixelSortRealtimeBands,
  type PixelSortPipeline,
} from "./gl/pixelSortPipeline";
import {
  createWireframeSpherePipeline,
  drawWireframeSphere,
  computeWireframeSphereBands,
  computeWireframeSphereRealtimeBands,
  type WireframeSpherePipeline,
} from "./gl/wireframeSpherePipeline";
import {
  createBlackHolePipeline,
  drawBlackHole,
  computeBlackHoleBands,
  computeBlackHoleRealtimeBands,
  type BlackHolePipeline,
} from "./gl/blackHolePipeline";
import {
  createLiquidMetalPipeline,
  drawLiquidMetal,
  computeLiquidMetalBands,
  computeLiquidMetalRealtimeBands,
  type LiquidMetalPipeline,
} from "./gl/liquidMetalPipeline";
import {
  createStainedGlassPipeline,
  drawStainedGlass,
  computeStainedGlassBands,
  computeStainedGlassRealtimeBands,
  type StainedGlassPipeline,
} from "./gl/stainedGlassPipeline";
import {
  createMarbleStatuePipeline,
  drawMarbleStatue,
  computeMarbleStatueBands,
  computeMarbleStatueRealtimeBands,
  type MarbleStatuePipeline,
} from "./gl/marbleStatuePipeline";
import {
  createPsychedelicPipeline,
  drawPsychedelic,
  computePsychedelicBands,
  computePsychedelicRealtimeBands,
  type PsychedelicPipeline,
} from "./gl/psychedelicPipeline";
import {
  createGlitchPipeline,
  drawGlitch,
  computeGlitchBands,
  computeGlitchRealtimeBands,
  type GlitchPipeline,
} from "./gl/glitchPipeline";
import {
  createQuantumFieldPipeline,
  drawQuantumField,
  computeQuantumFieldBands,
  computeQuantumFieldRealtimeBands,
  type QuantumFieldPipeline,
} from "./gl/quantumFieldPipeline";
import {
  createParticleConstellationPipeline,
  drawParticleConstellation,
  computeParticleConstellationBands,
  computeParticleConstellationRealtimeBands,
  type ParticleConstellationPipeline,
} from "./gl/particleConstellationPipeline";
import {
  createInkInWaterPipeline,
  drawInkInWater,
  computeInkInWaterBands,
  computeInkInWaterRealtimeBands,
  type InkInWaterPipeline,
} from "./gl/inkInWaterPipeline";
import {
  createWormholeTunnelPipeline,
  drawWormholeTunnel,
  computeWormholeTunnelBands,
  computeWormholeTunnelRealtimeBands,
  type WormholeTunnelPipeline,
} from "./gl/wormholeTunnelPipeline";
import {
  createAsciiPipeline,
  drawAscii,
  computeAsciiBands,
  computeAsciiRealtimeBands,
  type AsciiPipeline,
} from "./gl/asciiPipeline";
import {
  createInkDropPipeline,
  drawInkDrop,
  computeInkDropBands,
  computeInkDropRealtimeBands,
  type InkDropPipeline,
} from "./gl/inkDropPipeline";
import {
  createGyroidPipeline,
  drawGyroid,
  computeGyroidBands,
  computeGyroidRealtimeBands,
  type GyroidPipeline,
} from "./gl/gyroidPipeline";
import {
  createSchwarzPPipeline,
  drawSchwarzP,
  computeSchwarzPBands,
  computeSchwarzPRealtimeBands,
  type SchwarzPPipeline,
} from "./gl/schwarzPPipeline";
import {
  createMengerSpongePipeline,
  drawMengerSponge,
  computeMengerSpongeBands,
  computeMengerSpongeRealtimeBands,
  type MengerSpongePipeline,
} from "./gl/mengerSpongePipeline";
import {
  createKleinBottlePipeline,
  drawKleinBottle,
  computeKleinBottleBands,
  computeKleinBottleRealtimeBands,
  type KleinBottlePipeline,
} from "./gl/kleinBottlePipeline";
import {
  createConwayLifePipeline,
  drawConwayLife,
  computeConwayLifeBands,
  computeConwayLifeRealtimeBands,
  type ConwayLifePipeline,
} from "./gl/conwayLifePipeline";
import {
  createStableFluidPipeline,
  drawStableFluid,
  computeStableFluidBands,
  computeStableFluidRealtimeBands,
  type StableFluidPipeline,
} from "./gl/stableFluidPipeline";
import {
  createDoublePendulumPipeline,
  drawDoublePendulum,
  computeDoublePendulumBands,
  computeDoublePendulumRealtimeBands,
  type DoublePendulumPipeline,
} from "./gl/doublePendulumPipeline";
import {
  createSphericalHarmonicsPipeline,
  drawSphericalHarmonics,
  computeSphericalHarmonicsBands,
  computeSphericalHarmonicsRealtimeBands,
  type SphericalHarmonicsPipeline,
} from "./gl/sphericalHarmonicsPipeline";
import {
  createBECPipeline,
  drawBEC,
  computeBECBands,
  computeBECRealtimeBands,
  type BECPipeline,
} from "./gl/becPipeline";
import {
  createPhononLatticePipeline,
  drawPhononLattice,
  computePhononLatticeBands,
  computePhononLatticeRealtimeBands,
  type PhononLatticePipeline,
} from "./gl/phononLatticePipeline";
import {
  createMagneticPendulumPipeline,
  drawMagneticPendulum,
  computeMagneticPendulumBands,
  computeMagneticPendulumRealtimeBands,
  type MagneticPendulumPipeline,
} from "./gl/magneticPendulumPipeline";
import {
  createSchwarzDPipeline,
  drawSchwarzD,
  computeSchwarzDBands,
  computeSchwarzDRealtimeBands,
  type SchwarzDPipeline,
} from "./gl/schwarzDPipeline";
import {
  createLiquidCrystalPipeline,
  drawLiquidCrystal,
  computeLiquidCrystalBands,
  computeLiquidCrystalRealtimeBands,
  type LiquidCrystalPipeline,
} from "./gl/liquidCrystalPipeline";
import {
  createSincWaveFieldPipeline,
  drawSincWaveField,
  computeSincWaveFieldBands,
  computeSincWaveFieldRealtimeBands,
  type SincWaveFieldPipeline,
} from "./gl/sincWaveFieldPipeline";
import {
  createSineGordonPipeline,
  drawSineGordon,
  computeSineGordonBands,
  computeSineGordonRealtimeBands,
  type SineGordonPipeline,
} from "./gl/sineGordonPipeline";
import {
  createSkyrmionPipeline,
  drawSkyrmion,
  computeSkyrmionBands,
  computeSkyrmionRealtimeBands,
  type SkyrmionPipeline,
} from "./gl/skyrmionPipeline";
import {
  createHessianEigenvectorPipeline,
  drawHessianEigenvector,
  computeHessianEigenvectorBands,
  computeHessianEigenvectorRealtimeBands,
  type HessianEigenvectorPipeline,
} from "./gl/hessianEigenvectorPipeline";
import {
  createBraggDiffractionPipeline,
  drawBraggDiffraction,
  computeBraggDiffractionBands,
  computeBraggDiffractionRealtimeBands,
  type BraggDiffractionPipeline,
} from "./gl/braggDiffractionPipeline";
import {
  createCurvatureFlowPipeline,
  drawCurvatureFlow,
  computeCurvatureFlowBands,
  computeCurvatureFlowRealtimeBands,
  type CurvatureFlowPipeline,
} from "./gl/curvatureFlowPipeline";
import {
  createCellularPottsPipeline,
  drawCellularPotts,
  computeCellularPottsBands,
  computeCellularPottsRealtimeBands,
  type CellularPottsPipeline,
} from "./gl/cellularPottsPipeline";
import {
  createWaveFunctionCollapsePipeline,
  drawWaveFunctionCollapse,
  computeWaveFunctionCollapseBands,
  computeWaveFunctionCollapseRealtimeBands,
  type WaveFunctionCollapsePipeline,
} from "./gl/waveFunctionCollapsePipeline";
import {
  createPhaseFieldCrystalPipeline,
  drawPhaseFieldCrystal,
  computePhaseFieldCrystalBands,
  computePhaseFieldCrystalRealtimeBands,
  type PhaseFieldCrystalPipeline,
} from "./gl/phaseFieldCrystalPipeline";
import {
  createTeslaCoilPipeline,
  drawTeslaCoil,
  computeTeslaCoilBands,
  computeTeslaCoilRealtimeBands,
  type TeslaCoilPipeline,
} from "./gl/teslaCoilPipeline";
import {
  createPlasmaGlobePipeline,
  drawPlasmaGlobe,
  computePlasmaGlobeBands,
  computePlasmaGlobeRealtimeBands,
  type PlasmaGlobePipeline,
} from "./gl/plasmaGlobePipeline";
import {
  createSolarFlarePipeline,
  drawSolarFlare,
  computeSolarFlareBands,
  computeSolarFlareRealtimeBands,
  type SolarFlarePipeline,
} from "./gl/solarFlarePipeline";
import {
  createKleinFilmPipeline,
  drawKleinFilm,
  computeKleinFilmBands,
  computeKleinFilmRealtimeBands,
  type KleinFilmPipeline,
} from "./gl/kleinFilmPipeline";
import {
  createStarForgePipeline,
  drawStarForge,
  computeStarForgeBands,
  computeStarForgeRealtimeBands,
  type StarForgePipeline,
} from "./gl/starForgePipeline";
import {
  createVoronoiBubblesPipeline,
  drawVoronoiBubbles,
  computeVoronoiBubblesBands,
  computeVoronoiBubblesRealtimeBands,
  type VoronoiBubblesPipeline,
} from "./gl/voronoiBubblesPipeline";
import {
  createWindstreamPipeline,
  drawWindstream,
  computeWindstreamBands,
  computeWindstreamRealtimeBands,
  type WindstreamPipeline,
} from "./gl/windstreamPipeline";
import { getTextTexture, getPathTexture } from "./textLayout";
import {
  createStudioState,
  hitTestStudio,
  applyHandleResize,
  dampenForMass,
  persistScene,
  loadSceneState,
  applyPersistedState,
  clearPersistedState,
} from "./studio";
import { getImageTexture } from "./imageTexture";
import { createTiming, tickTiming, type TimingState } from "./timing";

/**
 * Map CSS mix-blend-mode strings to the int encoding the rect FS expects.
 * Unknown / unrecognized values fall back to "normal" (0) per CSS spec.
 */
const BLEND_MODE_INT: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7> = {
  normal: 0,
  multiply: 1,
  screen: 2,
  overlay: 3,
  darken: 4,
  lighten: 5,
  difference: 6,
  exclusion: 7,
};

import {
  createRiverPathsPipeline,
  drawRiverPaths,
  computeRiverPathsBands,
  computeRiverPathsRealtimeBands,
  type RiverPathsPipeline,
} from "./gl/riverPathsPipeline";

import {
  createEmbroideryPipeline,
  drawEmbroidery,
  computeEmbroideryBands,
  computeEmbroideryRealtimeBands,
  type EmbroideryPipeline,
} from "./gl/embroideryPipeline";

import {
  createKaleidoscopeTouchPipeline,
  drawKaleidoscopeTouch,
  computeKaleidoscopeTouchBands,
  computeKaleidoscopeTouchRealtimeBands,
  type KaleidoscopeTouchPipeline,
} from "./gl/kaleidoscopeTouchPipeline";

import {
  createCracklePatternPipeline,
  drawCracklePattern,
  computeCracklePatternBands,
  computeCracklePatternRealtimeBands,
  type CracklePatternPipeline,
} from "./gl/cracklePatternPipeline";

import {
  createVineGrowthPipeline,
  drawVineGrowth,
  computeVineGrowthBands,
  computeVineGrowthRealtimeBands,
  type VineGrowthPipeline,
} from "./gl/vineGrowthPipeline";

import {
  createPaperFoldPipeline,
  drawPaperFold,
  computePaperFoldBands,
  computePaperFoldRealtimeBands,
  type PaperFoldPipeline,
} from "./gl/paperFoldPipeline";

import {
  createLichenGrowthPipeline,
  drawLichenGrowth,
  computeLichenGrowthBands,
  computeLichenGrowthRealtimeBands,
  type LichenGrowthPipeline,
} from "./gl/lichenGrowthPipeline";

import {
  createWaveInterferencePipeline,
  drawWaveInterference,
  computeWaveInterferenceBands,
  computeWaveInterferenceRealtimeBands,
  type WaveInterferencePipeline,
} from "./gl/waveInterferencePipeline";

import {
  createStainedGlassTouchPipeline,
  drawStainedGlassTouch,
  computeStainedGlassTouchBands,
  computeStainedGlassTouchRealtimeBands,
  type StainedGlassTouchPipeline,
} from "./gl/stainedGlassTouchPipeline";

import {
  createWebSpinningPipeline,
  drawWebSpinning,
  computeWebSpinningBands,
  computeWebSpinningRealtimeBands,
  type WebSpinningPipeline,
} from "./gl/webSpinningPipeline";

import {
  createPlasmaTouchPipeline,
  drawPlasmaTouch,
  computePlasmaTouchBands,
  computePlasmaTouchRealtimeBands,
  type PlasmaTouchPipeline,
} from "./gl/plasmaTouchPipeline";

import {
  createRustGrowthPipeline,
  drawRustGrowth,
  computeRustGrowthBands,
  computeRustGrowthRealtimeBands,
  type RustGrowthPipeline,
} from "./gl/rustGrowthPipeline";

import {
  createBubbleChamberPipeline,
  drawBubbleChamber,
  computeBubbleChamberBands,
  computeBubbleChamberRealtimeBands,
  type BubbleChamberPipeline,
} from "./gl/bubbleChamberPipeline";

import {
  createTidePoolPipeline,
  drawTidePool,
  computeTidePoolBands,
  computeTidePoolRealtimeBands,
  type TidePoolPipeline,
} from "./gl/tidePoolPipeline";

import {
  createSugarCrystalPipeline,
  drawSugarCrystal,
  computeSugarCrystalBands,
  computeSugarCrystalRealtimeBands,
  type SugarCrystalPipeline,
} from "./gl/sugarCrystalPipeline";

import {
  createWindBlownGrassPipeline,
  drawWindBlownGrass,
  computeWindBlownGrassBands,
  computeWindBlownGrassRealtimeBands,
  type WindBlownGrassPipeline,
} from "./gl/windBlownGrassPipeline";

import {
  createAuroraTouchPipeline,
  drawAuroraTouch,
  computeAuroraTouchBands,
  computeAuroraTouchRealtimeBands,
  type AuroraTouchPipeline,
} from "./gl/auroraTouchPipeline";

import {
  createSandPilePipeline,
  drawSandPile,
  computeSandPileBands,
  computeSandPileRealtimeBands,
  type SandPilePipeline,
} from "./gl/sandPilePipeline";

import {
  createBubbleBlowPipeline,
  drawBubbleBlow,
  computeBubbleBlowBands,
  computeBubbleBlowRealtimeBands,
  type BubbleBlowPipeline,
} from "./gl/bubbleBlowPipeline";

import {
  createSmokeRingPipeline,
  drawSmokeRing,
  computeSmokeRingBands,
  computeSmokeRingRealtimeBands,
  type SmokeRingPipeline,
} from "./gl/smokeRingPipeline";

import {
  createCrystalGrowthPipeline,
  drawCrystalGrowth,
  computeCrystalGrowthBands,
  computeCrystalGrowthRealtimeBands,
  type CrystalGrowthPipeline,
} from "./gl/crystalGrowthPipeline";

import {
  createLightningWebPipeline,
  drawLightningWeb,
  computeLightningWebBands,
  computeLightningWebRealtimeBands,
  type LightningWebPipeline,
} from "./gl/lightningWebPipeline";

import {
  createCrackedGlassPipeline,
  drawCrackedGlass,
  computeCrackedGlassBands,
  computeCrackedGlassRealtimeBands,
  type CrackedGlassPipeline,
} from "./gl/crackedGlassPipeline";

import {
  createPetrifiedBloomPipeline,
  drawPetrifiedBloom,
  computePetrifiedBloomBands,
  computePetrifiedBloomRealtimeBands,
  type PetrifiedBloomPipeline,
} from "./gl/petrifiedBloomPipeline";

import {
  createMagneticFilingsPipeline,
  drawMagneticFilings,
  computeMagneticFilingsBands,
  computeMagneticFilingsRealtimeBands,
  type MagneticFilingsPipeline,
} from "./gl/magneticFilingsPipeline";

import {
  createHaloPipeline,
  drawHalo,
  computeHaloBands,
  computeHaloRealtimeBands,
  type HaloPipeline,
} from "./gl/haloPipeline";

import {
  createLissajousTubePipeline,
  drawLissajousTube,
  computeLissajousTubeBands,
  computeLissajousTubeRealtimeBands,
  type LissajousTubePipeline,
} from "./gl/lissajousTubePipeline";

import {
  createSnowflakePipeline,
  drawSnowflake,
  computeSnowflakeBands,
  computeSnowflakeRealtimeBands,
  type SnowflakePipeline,
} from "./gl/snowflakePipeline";

import {
  createNegativeSpacePipeline,
  drawNegativeSpace,
  computeNegativeSpaceBands,
  computeNegativeSpaceRealtimeBands,
  type NegativeSpacePipeline,
} from "./gl/negativeSpacePipeline";

import {
  createTrichromaticPipeline,
  drawTrichromatic,
  computeTrichromaticBands,
  computeTrichromaticRealtimeBands,
  type TrichromaticPipeline,
} from "./gl/trichromaticPipeline";

import {
  createSolarCoronaPipeline,
  drawSolarCorona,
  computeSolarCoronaBands,
  computeSolarCoronaRealtimeBands,
  type SolarCoronaPipeline,
} from "./gl/solarCoronaPipeline";

import {
  createDendriticCrystalPipeline,
  drawDendriticCrystal,
  computeDendriticCrystalBands,
  computeDendriticCrystalRealtimeBands,
  type DendriticCrystalPipeline,
} from "./gl/dendriticCrystalPipeline";

import {
  createTyndallBeamPipeline,
  drawTyndallBeam,
  computeTyndallBeamBands,
  computeTyndallBeamRealtimeBands,
  type TyndallBeamPipeline,
} from "./gl/tyndallBeamPipeline";

import {
  createBaroclinicVortexPipeline,
  drawBaroclinicVortex,
  computeBaroclinicVortexBands,
  computeBaroclinicVortexRealtimeBands,
  type BaroclinicVortexPipeline,
} from "./gl/baroclinicVortexPipeline";

import {
  createDislocationWebPipeline,
  drawDislocationWeb,
  computeDislocationWebBands,
  computeDislocationWebRealtimeBands,
  type DislocationWebPipeline,
} from "./gl/dislocationWebPipeline";

import {
  createChladniCellsPipeline,
  drawChladniCells,
  computeChladniCellsBands,
  computeChladniCellsRealtimeBands,
  type ChladniCellsPipeline,
} from "./gl/chladniCellsPipeline";

import {
  createCrookesRadiometerPipeline,
  drawCrookesRadiometer,
  computeCrookesRadiometerBands,
  computeCrookesRadiometerRealtimeBands,
  type CrookesRadiometerPipeline,
} from "./gl/crookesRadiometerPipeline";

import {
  createGranularFlowPipeline,
  drawGranularFlow,
  computeGranularFlowBands,
  computeGranularFlowRealtimeBands,
  type GranularFlowPipeline,
} from "./gl/granularFlowPipeline";

import {
  createSavartWheelPipeline,
  drawSavartWheel,
  computeSavartWheelBands,
  computeSavartWheelRealtimeBands,
  type SavartWheelPipeline,
} from "./gl/savartWheelPipeline";

import {
  createRijkeTubePipeline,
  drawRijkeTube,
  computeRijkeTubeBands,
  computeRijkeTubeRealtimeBands,
  type RijkeTubePipeline,
} from "./gl/rijkeTubePipeline";

import {
  createFerrofluidSculpturePipeline,
  drawFerrofluidSculpture,
  computeFerrofluidSculptureBands,
  computeFerrofluidSculptureRealtimeBands,
  type FerrofluidSculpturePipeline,
} from "./gl/ferrofluidSculpturePipeline";

import {
  createSigilsPipeline,
  drawSigils,
  computeSigilsBands,
  computeSigilsRealtimeBands,
  type SigilsPipeline,
} from "./gl/sigilsPipeline";

import {
  createMagneticMonopolePipeline,
  drawMagneticMonopole,
  computeMagneticMonopoleBands,
  computeMagneticMonopoleRealtimeBands,
  type MagneticMonopolePipeline,
} from "./gl/magneticMonopolePipeline";

import {
  createTimeCrystalPipeline,
  drawTimeCrystal,
  computeTimeCrystalBands,
  computeTimeCrystalRealtimeBands,
  type TimeCrystalPipeline,
} from "./gl/timeCrystalPipeline";

import {
  createPenroseStairsPipeline,
  drawPenroseStairs,
  computePenroseStairsBands,
  computePenroseStairsRealtimeBands,
  type PenroseStairsPipeline,
} from "./gl/penroseStairsPipeline";

import {
  createQuantumFoamPipeline,
  drawQuantumFoam,
  computeQuantumFoamBands,
  computeQuantumFoamRealtimeBands,
  type QuantumFoamPipeline,
} from "./gl/quantumFoamPipeline";

import {
  createFractalSmokePipeline,
  drawFractalSmoke,
  computeFractalSmokeBands,
  computeFractalSmokeRealtimeBands,
  type FractalSmokePipeline,
} from "./gl/fractalSmokePipeline";

import {
  createAnyonicBraidingPipeline,
  drawAnyonicBraiding,
  computeAnyonicBraidingBands,
  computeAnyonicBraidingRealtimeBands,
  type AnyonicBraidingPipeline,
} from "./gl/anyonicBraidingPipeline";

import {
  createHydrodynamicShockPipeline,
  drawHydrodynamicShock,
  computeHydrodynamicShockBands,
  computeHydrodynamicShockRealtimeBands,
  type HydrodynamicShockPipeline,
} from "./gl/hydrodynamicShockPipeline";

import {
  createSchwarzschildCrossingPipeline,
  drawSchwarzschildCrossing,
  computeSchwarzschildCrossingBands,
  computeSchwarzschildCrossingRealtimeBands,
  type SchwarzschildCrossingPipeline,
} from "./gl/schwarzschildCrossingPipeline";

import {
  createDynamoPipeline,
  drawDynamo,
  computeDynamoBands,
  computeDynamoRealtimeBands,
  type DynamoPipeline,
} from "./gl/dynamoPipeline";

import {
  createPhaseConjugatePipeline,
  drawPhaseConjugate,
  computePhaseConjugateBands,
  computePhaseConjugateRealtimeBands,
  type PhaseConjugatePipeline,
} from "./gl/phaseConjugatePipeline";

import {
  createSpectralDecompositionPipeline,
  drawSpectralDecomposition,
  computeSpectralDecompositionBands,
  computeSpectralDecompositionRealtimeBands,
  type SpectralDecompositionPipeline,
} from "./gl/spectralDecompositionPipeline";

import {
  createAnamorphicPipeline,
  drawAnamorphic,
  computeAnamorphicBands,
  computeAnamorphicRealtimeBands,
  type AnamorphicPipeline,
} from "./gl/anamorphicPipeline";

import {
  createFeynmanPathPipeline,
  drawFeynmanPath,
  computeFeynmanPathBands,
  computeFeynmanPathRealtimeBands,
  type FeynmanPathPipeline,
} from "./gl/feynmanPathPipeline";

import {
  createPhotonicCrystalPipeline,
  drawPhotonicCrystal,
  computePhotonicCrystalBands,
  computePhotonicCrystalRealtimeBands,
  type PhotonicCrystalPipeline,
} from "./gl/photonicCrystalPipeline";

import {
  createTokamakPlasmaPipeline,
  drawTokamakPlasma,
  computeTokamakPlasmaBands,
  computeTokamakPlasmaRealtimeBands,
  type TokamakPlasmaPipeline,
} from "./gl/tokamakPlasmaPipeline";

import {
  createMobiusLatticePipeline,
  drawMobiusLattice,
  computeMobiusLatticeBands,
  computeMobiusLatticeRealtimeBands,
  type MobiusLatticePipeline,
} from "./gl/mobiusLatticePipeline";

import {
  createTesseractShadowPipeline,
  drawTesseractShadow,
  computeTesseractShadowBands,
  computeTesseractShadowRealtimeBands,
  type TesseractShadowPipeline,
} from "./gl/tesseractShadowPipeline";

import {
  createTwinflamePipeline,
  drawTwinflame,
  computeTwinflameBands,
  computeTwinflameRealtimeBands,
  type TwinflamePipeline,
} from "./gl/twinflamePipeline";

import {
  createMulberryPipeline,
  drawMulberry,
  computeMulberryBands,
  computeMulberryRealtimeBands,
  type MulberryPipeline,
} from "./gl/mulberryPipeline";

import {
  createBifrostMirrorPipeline,
  drawBifrostMirror,
  computeBifrostMirrorBands,
  computeBifrostMirrorRealtimeBands,
  type BifrostMirrorPipeline,
} from "./gl/bifrostMirrorPipeline";

import {
  createStalactitePulsePipeline,
  drawStalactitePulse,
  computeStalactitePulseBands,
  computeStalactitePulseRealtimeBands,
  type StalactitePulsePipeline,
} from "./gl/stalactitePulsePipeline";

import {
  createCatenaryLoomPipeline,
  drawCatenaryLoom,
  computeCatenaryLoomBands,
  computeCatenaryLoomRealtimeBands,
  type CatenaryLoomPipeline,
} from "./gl/catenaryLoomPipeline";

import {
  createLightwingPipeline,
  drawLightwing,
  computeLightwingBands,
  computeLightwingRealtimeBands,
  type LightwingPipeline,
} from "./gl/lightwingPipeline";

import {
  createCataractPipeline,
  drawCataract,
  computeCataractBands,
  computeCataractRealtimeBands,
  type CataractPipeline,
} from "./gl/cataractPipeline";

import {
  createAspicPipeline,
  drawAspic,
  computeAspicBands,
  computeAspicRealtimeBands,
  type AspicPipeline,
} from "./gl/aspicPipeline";

import {
  createMoirefallPipeline,
  drawMoirefall,
  computeMoirefallBands,
  computeMoirefallRealtimeBands,
  type MoirefallPipeline,
} from "./gl/moirefallPipeline";

import {
  createTideglassPipeline,
  drawTideglass,
  computeTideglassBands,
  computeTideglassRealtimeBands,
  type TideglassPipeline,
} from "./gl/tideglassPipeline";

import {
  createSunTonguePipeline,
  drawSunTongue,
  computeSunTongueBands,
  computeSunTongueRealtimeBands,
  type SunTonguePipeline,
} from "./gl/sunTonguePipeline";

import {
  createTessellationMeltPipeline,
  drawTessellationMelt,
  computeTessellationMeltBands,
  computeTessellationMeltRealtimeBands,
  type TessellationMeltPipeline,
} from "./gl/tessellationMeltPipeline";
import {
  createMagneticMonopoleTouchPipeline,
  drawMagneticMonopoleTouch,
  computeMagneticMonopoleTouchBands,
  computeMagneticMonopoleTouchRealtimeBands,
  type MagneticMonopoleTouchPipeline,
} from "./gl/magneticMonopoleTouchPipeline";
import {
  createFractalSmokeTouchPipeline,
  drawFractalSmokeTouch,
  computeFractalSmokeTouchBands,
  computeFractalSmokeTouchRealtimeBands,
  type FractalSmokeTouchPipeline,
} from "./gl/fractalSmokeTouchPipeline";
import {
  createTesseractShadowTouchPipeline,
  drawTesseractShadowTouch,
  computeTesseractShadowTouchBands,
  computeTesseractShadowTouchRealtimeBands,
  type TesseractShadowTouchPipeline,
} from "./gl/tesseractShadowTouchPipeline";
import {
  createTessellationMeltTouchPipeline,
  drawTessellationMeltTouch,
  touchSetPointer,
  touchPushClick,
  computeTessellationMeltTouchBands,
  computeTessellationMeltTouchRealtimeBands,
  type TessellationMeltTouchPipeline,
} from "./gl/tessellationMeltTouchPipeline";
import {
  createMagneticFilingsPipeline,
  drawMagneticFilings,
  magneticFilingsUpdatePointer,
  magneticFilingsPushClick,
  computeMagneticFilingsBands,
  computeMagneticFilingsRealtimeBands,
  type MagneticFilingsPipeline,
} from "./gl/magneticFilingsPipeline";

import {
  createPetrifiedBloomPipeline,
  drawPetrifiedBloom,
  petrifiedBloomUpdatePointer,
  petrifiedBloomPushClick,
  computePetrifiedBloomBands,
  computePetrifiedBloomRealtimeBands,
  type PetrifiedBloomPipeline,
} from "./gl/petrifiedBloomPipeline";

import {
  createCrackedGlassPipeline,
  drawCrackedGlass,
  crackedGlassUpdatePointer,
  crackedGlassPushClick,
  computeCrackedGlassBands,
  computeCrackedGlassRealtimeBands,
  type CrackedGlassPipeline,
} from "./gl/crackedGlassPipeline";

import {
  createLightningWebPipeline,
  drawLightningWeb,
  lightningWebUpdatePointer,
  lightningWebPushClick,
  computeLightningWebBands,
  computeLightningWebRealtimeBands,
  type LightningWebPipeline,
} from "./gl/lightningWebPipeline";

import {
  createCrystalGrowthPipeline,
  drawCrystalGrowth,
  crystalGrowthUpdatePointer,
  crystalGrowthPushClick,
  computeCrystalGrowthBands,
  computeCrystalGrowthRealtimeBands,
  type CrystalGrowthPipeline,
} from "./gl/crystalGrowthPipeline";

import {
  createSmokeRingPipeline,
  drawSmokeRing,
  smokeRingUpdatePointer,
  smokeRingPushClick,
  computeSmokeRingBands,
  computeSmokeRingRealtimeBands,
  type SmokeRingPipeline,
} from "./gl/smokeRingPipeline";

import {
  createBubbleBlowPipeline,
  drawBubbleBlow,
  bubbleBlowUpdatePointer,
  bubbleBlowPushClick,
  computeBubbleBlowBands,
  computeBubbleBlowRealtimeBands,
  type BubbleBlowPipeline,
} from "./gl/bubbleBlowPipeline";

import {
  createSandPilePipeline,
  drawSandPile,
  sandPileUpdatePointer,
  sandPilePushClick,
  computeSandPileBands,
  computeSandPileRealtimeBands,
  type SandPilePipeline,
} from "./gl/sandPilePipeline";

import {
  createAuroraTouchPipeline,
  drawAuroraTouch,
  auroraTouchUpdatePointer,
  auroraTouchPushClick,
  computeAuroraTouchBands,
  computeAuroraTouchRealtimeBands,
  type AuroraTouchPipeline,
} from "./gl/auroraTouchPipeline";

import {
  createWindBlownGrassPipeline,
  drawWindBlownGrass,
  windBlownGrassUpdatePointer,
  windBlownGrassPushClick,
  computeWindBlownGrassBands,
  computeWindBlownGrassRealtimeBands,
  type WindBlownGrassPipeline,
} from "./gl/windBlownGrassPipeline";

import {
  createSugarCrystalPipeline,
  drawSugarCrystal,
  sugarCrystalUpdatePointer,
  sugarCrystalPushClick,
  computeSugarCrystalBands,
  computeSugarCrystalRealtimeBands,
  type SugarCrystalPipeline,
} from "./gl/sugarCrystalPipeline";

import {
  createTidePoolPipeline,
  drawTidePool,
  tidePoolUpdatePointer,
  tidePoolPushClick,
  computeTidePoolBands,
  computeTidePoolRealtimeBands,
  type TidePoolPipeline,
} from "./gl/tidePoolPipeline";

import {
  createBubbleChamberPipeline,
  drawBubbleChamber,
  bubbleChamberUpdatePointer,
  bubbleChamberPushClick,
  computeBubbleChamberBands,
  computeBubbleChamberRealtimeBands,
  type BubbleChamberPipeline,
} from "./gl/bubbleChamberPipeline";

import {
  createRustGrowthPipeline,
  drawRustGrowth,
  rustGrowthUpdatePointer,
  rustGrowthPushClick,
  computeRustGrowthBands,
  computeRustGrowthRealtimeBands,
  type RustGrowthPipeline,
} from "./gl/rustGrowthPipeline";

import {
  createPlasmaTouchPipeline,
  drawPlasmaTouch,
  plasmaTouchUpdatePointer,
  plasmaTouchPushClick,
  computePlasmaTouchBands,
  computePlasmaTouchRealtimeBands,
  type PlasmaTouchPipeline,
} from "./gl/plasmaTouchPipeline";

import {
  createWebSpinningPipeline,
  drawWebSpinning,
  webSpinningUpdatePointer,
  webSpinningPushClick,
  computeWebSpinningBands,
  computeWebSpinningRealtimeBands,
  type WebSpinningPipeline,
} from "./gl/webSpinningPipeline";

import {
  createStainedGlassTouchPipeline,
  drawStainedGlassTouch,
  stainedGlassTouchUpdatePointer,
  stainedGlassTouchPushClick,
  computeStainedGlassTouchBands,
  computeStainedGlassTouchRealtimeBands,
  type StainedGlassTouchPipeline,
} from "./gl/stainedGlassTouchPipeline";

import {
  createWaveInterferencePipeline,
  drawWaveInterference,
  waveInterferenceUpdatePointer,
  waveInterferencePushClick,
  computeWaveInterferenceBands,
  computeWaveInterferenceRealtimeBands,
  type WaveInterferencePipeline,
} from "./gl/waveInterferencePipeline";

import {
  createLichenGrowthPipeline,
  drawLichenGrowth,
  lichenGrowthUpdatePointer,
  lichenGrowthPushClick,
  computeLichenGrowthBands,
  computeLichenGrowthRealtimeBands,
  type LichenGrowthPipeline,
} from "./gl/lichenGrowthPipeline";

import {
  createPaperFoldPipeline,
  drawPaperFold,
  paperFoldUpdatePointer,
  paperFoldPushClick,
  computePaperFoldBands,
  computePaperFoldRealtimeBands,
  type PaperFoldPipeline,
} from "./gl/paperFoldPipeline";

import {
  createVineGrowthPipeline,
  drawVineGrowth,
  vineGrowthUpdatePointer,
  vineGrowthPushClick,
  computeVineGrowthBands,
  computeVineGrowthRealtimeBands,
  type VineGrowthPipeline,
} from "./gl/vineGrowthPipeline";

import {
  createCracklePatternPipeline,
  drawCracklePattern,
  cracklePatternUpdatePointer,
  cracklePatternPushClick,
  computeCracklePatternBands,
  computeCracklePatternRealtimeBands,
  type CracklePatternPipeline,
} from "./gl/cracklePatternPipeline";

import {
  createKaleidoscopeTouchPipeline,
  drawKaleidoscopeTouch,
  kaleidoscopeTouchUpdatePointer,
  kaleidoscopeTouchPushClick,
  computeKaleidoscopeTouchBands,
  computeKaleidoscopeTouchRealtimeBands,
  type KaleidoscopeTouchPipeline,
} from "./gl/kaleidoscopeTouchPipeline";

import {
  createEmbroideryPipeline,
  drawEmbroidery,
  embroideryUpdatePointer,
  embroideryPushClick,
  computeEmbroideryBands,
  computeEmbroideryRealtimeBands,
  type EmbroideryPipeline,
} from "./gl/embroideryPipeline";

import {
  createRiverPathsPipeline,
  drawRiverPaths,
  riverPathsUpdatePointer,
  riverPathsPushClick,
  computeRiverPathsBands,
  computeRiverPathsRealtimeBands,
  type RiverPathsPipeline,
} from "./gl/riverPathsPipeline";



import {
  createResonancePipeline,
  drawResonance,
  computeResonanceBands,
  computeResonanceRealtimeBands,
  type ResonancePipeline,
} from "./gl/resonancePipeline";

import {
  createFontainefeedPipeline,
  drawFontainefeed,
  computeFontainefeedBands,
  computeFontainefeedRealtimeBands,
  type FontainefeedPipeline,
} from "./gl/fontainefeedPipeline";

import {
  createPendantBloomPipeline,
  drawPendantBloom,
  computePendantBloomBands,
  computePendantBloomRealtimeBands,
  type PendantBloomPipeline,
} from "./gl/pendantBloomPipeline";
function blendModeOf(n: SceneNode): 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 {
  return BLEND_MODE_INT[n.mixBlendMode] ?? 0;
}

export interface Engine {
  canvas: HTMLCanvasElement;
  ctx: ReturnType<typeof initGL>;
  rectPipe: RectPipeline | null;
  postPipe: PostProcessPipeline | null;
  /**
   * Macro-Cosmic Dust and Optics cinematic camera pipeline. When
   * `cinematicEnabled` is true, this replaces the standard
   * drawPostProcess call with a two-pass render: hot-pixel locator +
   * cinematic grade (filmic tone map, halation, volumetric light shaft,
   * 3-layer parallax dust, anchored lens flare, chromatic aberration,
   * film grain). When false, the plain velocity-blur post pass runs.
   */
  cosmicDust: CosmicDustComposite | null;
  /** Toggle the cinematic camera pipeline. Default true. */
  cinematicEnabled: boolean;
  /**
   * Fullscreen-quad pipeline used by scenes that opt into a custom
   * render (currently just ★ Chain). Created on demand when the
   * active scene registers a customDraw hook.
   */
  chainPipe: ChainPipeline | null;
  morphPipe: MorphPipeline | null;
  cinePipe: CinePipeline | null;
  beatPipe: BeatNebulaPipeline | null;
  hexPipe: HexPipeline | null;
  coralPipe: CoralPipeline | null;
  vinesPipe: VinesPipeline | null;
  neuralNetPipe: NeuralNetPipeline | null;
  voronoiCrystalPipe: VoronoiCrystalPipeline | null;
  metaballVolPipe: MetaballVolPipeline | null;
  smokeColumnsPipe: SmokeColumnsPipeline | null;
  liquidMetalVolPipe: LiquidMetalVolPipeline | null;
  auroraPipe: AuroraPipeline | null;
  lissajousSymphPipe: LissajousSymphPipeline | null;
  ribbonTracerPipe: RibbonTracerPipeline | null;
  galacticCorePipe: GalacticCorePipeline | null;
  pendulumCascPipe: PendulumCascPipeline | null;
  bifrostPipe: BifrostPipeline | null;
  kaleidoTunnelPipe: KaleidoTunnelPipeline | null;
  mandalaPipe: MandalaPipeline | null;
  phantasmPipe: PhantasmPipeline | null;
  honeycombTidePipe: HoneycombTidePipeline | null;
  equalCathedralPipe: EqualCathedralPipeline | null;
  phonographPipe: PhonographPipeline | null;
  cymaticsPlatePipe: CymaticsPlatePipeline | null;
  aurora3DPipe: Aurora3DPipeline | null;
  soundWave3DPipe: SoundWave3DPipeline | null;
  clothPipe: ClothPipeline | null;
  teslaCoilPipe: TeslaCoilPipeline | null;
  plasmaGlobePipe: PlasmaGlobePipeline | null;
  solarFlarePipe: SolarFlarePipeline | null;
  kleinFilmPipe: KleinFilmPipeline | null;
  starForgePipe: StarForgePipeline | null;
  voronoiBubblesPipe: VoronoiBubblesPipeline | null;
  windstreamPipe: WindstreamPipeline | null;
  siliconSkinPipe: SiliconSkinPipeline | null;
  viscoelasticPipe: ViscoelasticPipeline | null;
  balloonPressPipe: BalloonPressPipeline | null;
  membraneDrumPipe: MembraneDrumPipeline | null;
  punchBagPipe: PunchBagPipeline | null;
  siliconeFacePipe: SiliconeFacePipeline | null;
  liquidSkinPipe: LiquidSkinPipeline | null;
  clayPressPipe: ClayPressPipeline | null;
  drumHead3DPipe: DrumHead3DPipeline | null;
  siliconeVeilPipe: SiliconeVeilPipeline | null;
  liveVideoPipe: LiveVideoPipeline | null;
  myceliumDomPipe: MyceliumDomPipeline | null;
  dislocationFieldPipe: DislocationFieldPipeline | null;
  dislocationFieldLogosPipe: DislocationFieldLogosPipeline | null;
  marangoniInkPipe: MarangoniInkPipeline | null;
  mcColloughPipe: McColloughPipeline | null;
  hopfFibrationPipe: HopfFibrationPipeline | null;
  slitScanChronoPipe: SlitScanChronoPipeline | null;
  glassPipe: GlassPipeline | null;
  vortexPipe: VortexPipeline | null;
  supernovaPipe: SupernovaPipeline | null;
  plasmaPipe: PlasmaPipeline | null;
  driftPipe: DriftPipeline | null;
  latticePulsePipe: LatticePulsePipeline | null;
  chronoStrandPipe: ChronoStrandPipeline | null;
  riverPathsPipe: RiverPathsPipeline | null;
  embroideryPipe: EmbroideryPipeline | null;
  kaleidoscopeTouchPipe: KaleidoscopeTouchPipeline | null;
  cracklePatternPipe: CracklePatternPipeline | null;
  vineGrowthPipe: VineGrowthPipeline | null;
  paperFoldPipe: PaperFoldPipeline | null;
  lichenGrowthPipe: LichenGrowthPipeline | null;
  waveInterferencePipe: WaveInterferencePipeline | null;
  stainedGlassTouchPipe: StainedGlassTouchPipeline | null;
  webSpinningPipe: WebSpinningPipeline | null;
  plasmaTouchPipe: PlasmaTouchPipeline | null;
  rustGrowthPipe: RustGrowthPipeline | null;
  bubbleChamberPipe: BubbleChamberPipeline | null;
  tidePoolPipe: TidePoolPipeline | null;
  sugarCrystalPipe: SugarCrystalPipeline | null;
  windBlownGrassPipe: WindBlownGrassPipeline | null;
  auroraTouchPipe: AuroraTouchPipeline | null;
  sandPilePipe: SandPilePipeline | null;
  bubbleBlowPipe: BubbleBlowPipeline | null;
  smokeRingPipe: SmokeRingPipeline | null;
  crystalGrowthPipe: CrystalGrowthPipeline | null;
  lightningWebPipe: LightningWebPipeline | null;
  crackedGlassPipe: CrackedGlassPipeline | null;
  petrifiedBloomPipe: PetrifiedBloomPipeline | null;
  magneticFilingsPipe: MagneticFilingsPipeline | null;
  haloPipe: HaloPipeline | null;
  lissajousTubePipe: LissajousTubePipeline | null;
  snowflakePipe: SnowflakePipeline | null;
  negativeSpacePipe: NegativeSpacePipeline | null;
  trichromaticPipe: TrichromaticPipeline | null;
  solarCoronaPipe: SolarCoronaPipeline | null;
  dendriticCrystalPipe: DendriticCrystalPipeline | null;
  tyndallBeamPipe: TyndallBeamPipeline | null;
  baroclinicVortexPipe: BaroclinicVortexPipeline | null;
  dislocationWebPipe: DislocationWebPipeline | null;
  chladniCellsPipe: ChladniCellsPipeline | null;
  crookesRadiometerPipe: CrookesRadiometerPipeline | null;
  granularFlowPipe: GranularFlowPipeline | null;
  savartWheelPipe: SavartWheelPipeline | null;
  rijkeTubePipe: RijkeTubePipeline | null;
  ferrofluidSculpturePipe: FerrofluidSculpturePipeline | null;
  sigilsPipe: SigilsPipeline | null;
  magneticMonopolePipe: MagneticMonopolePipeline | null;
  timeCrystalPipe: TimeCrystalPipeline | null;
  penroseStairsPipe: PenroseStairsPipeline | null;
  quantumFoamPipe: QuantumFoamPipeline | null;
  fractalSmokePipe: FractalSmokePipeline | null;
  anyonicBraidingPipe: AnyonicBraidingPipeline | null;
  hydrodynamicShockPipe: HydrodynamicShockPipeline | null;
  schwarzschildCrossingPipe: SchwarzschildCrossingPipeline | null;
  dynamoPipe: DynamoPipeline | null;
  phaseConjugatePipe: PhaseConjugatePipeline | null;
  spectralDecompositionPipe: SpectralDecompositionPipeline | null;
  anamorphicPipe: AnamorphicPipeline | null;
  feynmanPathPipe: FeynmanPathPipeline | null;
  becPipe: BECPipeline | null;
  beatNebulaPipe: BeatNebulaPipeline | null;
  photonicCrystalPipe: PhotonicCrystalPipeline | null;
  tokamakPlasmaPipe: TokamakPlasmaPipeline | null;
  mobiusLatticePipe: MobiusLatticePipeline | null;
  tesseractShadowPipe: TesseractShadowPipeline | null;
  twinflamePipe: TwinflamePipeline | null;
  mulberryPipe: MulberryPipeline | null;
  bifrostMirrorPipe: BifrostMirrorPipeline | null;
  stalactitePulsePipe: StalactitePulsePipeline | null;
  catenaryLoomPipe: CatenaryLoomPipeline | null;
  lightwingPipe: LightwingPipeline | null;
  cataractPipe: CataractPipeline | null;
  aspicPipe: AspicPipeline | null;
  moirefallPipe: MoirefallPipeline | null;
  tideglassPipe: TideglassPipeline | null;
  sunTonguePipe: SunTonguePipeline | null;
  tessellationMeltPipe: TessellationMeltPipeline | null;
  tessellationMeltTouchPipe: TessellationMeltTouchPipeline | null;
  magneticMonopoleTouchPipe: MagneticMonopoleTouchPipeline | null;
  fractalSmokeTouchPipe: FractalSmokeTouchPipeline | null;
  tesseractShadowTouchPipe: TesseractShadowTouchPipeline | null;
  resonancePipe: ResonancePipeline | null;
  fontainefeedPipe: FontainefeedPipeline | null;
  pendantBloomPipe: PendantBloomPipeline | null;
  bloomPipe: BloomPipeline | null;
  meshPipe: MeshPipeline | null;
  orbitsPipe: OrbitsPipeline | null;
  cymaticsPipe: CymaticsPipeline | null;
  mandelbrotPipe: MandelbrotPipeline | null;
  kaleidoscopePipe: KaleidoscopePipeline | null;
  lorenzPipe: LorenzPipeline | null;
  phyllotaxisPipe: PhyllotaxisPipeline | null;
  quasicrystalPipe: QuasicrystalPipeline | null;
  tunnelPipe: TunnelPipeline | null;
  ferrofluidPipe: FerrofluidPipeline | null;
  polyhedronPipe: PolyhedronPipeline | null;
  lightningPipe: LightningPipeline | null;
  causticsPipe: CausticsPipeline | null;
  worleyTerrainPipe: WorleyTerrainPipeline | null;
  magneticPipe: MagneticPipeline | null;
  lSystemPipe: LSystemPipeline | null;
  truchetPipe: TruchetPipeline | null;
  sunburstPipe: SunburstPipeline | null;
  spirographPipe: SpirographPipeline | null;
  dnaPipe: DNAPipeline | null;
  lavaLampPipe: LavaLampPipeline | null;
  bezierPipe: BezierPipeline | null;
  stereographicPipe: StereographicPipeline | null;
  contourPipe: ContourPipeline | null;
  cosmosPipe: CosmosPipeline | null;
  galaxyPipe: GalaxyPipeline | null;
  oceanStormPipe: OceanStormPipeline | null;
  cyberpunkPipe: CyberpunkPipeline | null;
  crystalCavePipe: CrystalCavePipeline | null;
  soapBubblePipe: SoapBubblePipeline | null;
  voronoiGlassPipe: VoronoiGlassPipeline | null;
  pixelSortPipe: PixelSortPipeline | null;
  wireframeSpherePipe: WireframeSpherePipeline | null;
  blackHolePipe: BlackHolePipeline | null;
  liquidMetalPipe: LiquidMetalPipeline | null;
  stainedGlassPipe: StainedGlassPipeline | null;
  marbleStatuePipe: MarbleStatuePipeline | null;
  psychedelicPipe: PsychedelicPipeline | null;
  glitchPipe: GlitchPipeline | null;
  quantumFieldPipe: QuantumFieldPipeline | null;
  particleConstellationPipe: ParticleConstellationPipeline | null;
  inkInWaterPipe: InkInWaterPipeline | null;
  wormholeTunnelPipe: WormholeTunnelPipeline | null;
  asciiPipe: AsciiPipeline | null;
  inkDropPipe: InkDropPipeline | null;
  gyroidPipe: GyroidPipeline | null;
  schwarzPPipe: SchwarzPPipeline | null;
  mengerSpongePipe: MengerSpongePipeline | null;
  kleinBottlePipe: KleinBottlePipeline | null;
  conwayLifePipe: ConwayLifePipeline | null;
  stableFluidPipe: StableFluidPipeline | null;
  doublePendulumPipe: DoublePendulumPipeline | null;
  sphericalHarmonicsPipe: SphericalHarmonicsPipeline | null;
  becPipe: BECPipeline | null;
  phononLatticePipe: PhononLatticePipeline | null;
  magneticPendulumPipe: MagneticPendulumPipeline | null;
  schwarzDPipe: SchwarzDPipeline | null;
  liquidCrystalPipe: LiquidCrystalPipeline | null;
  sincWaveFieldPipe: SincWaveFieldPipeline | null;
  sineGordonPipe: SineGordonPipeline | null;
  skyrmionPipe: SkyrmionPipeline | null;
  hessianEigenvectorPipe: HessianEigenvectorPipeline | null;
  braggDiffractionPipe: BraggDiffractionPipeline | null;
  curvatureFlowPipe: CurvatureFlowPipeline | null;
  cellularPottsPipe: CellularPottsPipeline | null;
  waveFunctionCollapsePipe: WaveFunctionCollapsePipeline | null;
  phaseFieldCrystalPipe: PhaseFieldCrystalPipeline | null;
  /** Audio capture state for the beat scene. Null when mic not active. */
  beatAudio: {
    ctx: AudioContext;
    analyser: AnalyserNode;
    stream: MediaStream;
    freqData: Uint8Array;
  } | null;
  /** Layer 5: fullscreen-quad pipeline for compositing backdropFbo onto sceneFbo. */
  backdropPipe: BackdropCompositePipeline | null;
  sceneFbo: FBO | null;
  /** Layer 6: scratch FBO used for mix-blend-mode segments. Copy of sceneFbo
   *  at segment start, draw the blend rects into it, blit back to sceneFbo at
   *  segment end. Always same size as sceneFbo. Null when sceneFbo is null. */
  scratchFbo: FBO | null;
  /** Layer 5 (refactor): dedicated FBO for backdrop-filter nodes. Drawn after
   *  the scene base pass (sceneFbo) and composited on top. Reading sceneFbo
   *  as a sampler while writing into a SEPARATE FBO is well-defined per WebGL
   *  spec; reading while writing to the same FBO is undefined and stalls on
   *  some drivers (this was the original symptom). Same size as sceneFbo. */
  backdropFbo: FBO | null;
  velocityFbo: FBO | null;
  velocityInfo: VelocityInfo | null;
  scene: Scene;
  compiled: CompiledScene | null;
  sceneName: string;
  /**
   * Optional per-frame custom draw hook. When set, the frame loop calls
   * this instead of the sceneFbo+postprocess path. Used by the chain
   * link raymarcher (★ Chain) which doesn't fit the 2D-SDF pipeline.
   * The hook is responsible for clearing + drawing to the canvas itself.
   */
  customDraw?: (engine: Engine, t: number) => void;
  pointer: ReturnType<typeof createPointer>;
  timing: ReturnType<typeof createTiming>;
  hud: ReturnType<typeof bindHud>;
  /** Last compile time in ms (for HUD). */
  compileMs: number;
  /** 0 disables, 1 = full frame motion. */
  blurStrength: number;
  /** Live inertia state when post-release drag is flinging. */
  inertia: {
    nodeId: NodeId | null;
    vx: number;
    vy: number;
  } | null;
  /** Rolling-window frame-time EMA for dynamic DPR scaling. */
  longFrameCount: number;
  /** Last time we auto-adjusted render scale. */
  lastScaleAdjust: number;
  /** Studio — selection / resize / persistence state. */
  studio: ReturnType<typeof createStudioState>;
  /** Name of the currently-loaded scene (for persistence keying). */
  currentScene: string;
}

export function setupEngine(canvas: HTMLCanvasElement): Engine | null {
  const ctx = initGL(canvas);
  if (!ctx) return null;

  const rectPipe = createRectPipeline(ctx.gl);
  const postPipe = createPostProcessPipeline(ctx.gl);
  const backdropPipe = createBackdropCompositePipeline(ctx.gl);
  const cosmicDust = createCosmicDustComposite(ctx.gl);
  if (!rectPipe || !postPipe || !backdropPipe || !cosmicDust) return null;
  ensureWhiteTex(ctx.gl);

  return {
    canvas,
    ctx,
    rectPipe,
    postPipe,
    cosmicDust,
    cinematicEnabled: false, // default OFF — the lens-flare ghosts + halation
                                  // on the cinematic pass paint green/pink orbs all
                                  // over scene-graph content. Press C to toggle.
    backdropPipe,
    chainPipe: null,
    morphPipe: null,
    cinePipe: null,
    beatPipe: null,
    hexPipe: null,
    coralPipe: null,
    vinesPipe: null,
    neuralNetPipe: null,
    voronoiCrystalPipe: null,
    metaballVolPipe: null,
    smokeColumnsPipe: null,
    liquidMetalVolPipe: null,
    auroraPipe: null,
    lissajousSymphPipe: null,
    ribbonTracerPipe: null,
    galacticCorePipe: null,
    pendulumCascPipe: null,
    bifrostPipe: null,
    kaleidoTunnelPipe: null,
    mandalaPipe: null,
    phantasmPipe: null,
    honeycombTidePipe: null,
    equalCathedralPipe: null,
    phonographPipe: null,
    cymaticsPlatePipe: null,
    aurora3DPipe: null,
    soundWave3DPipe: null,
    clothPipe: null,
    teslaCoilPipe: null,
    plasmaGlobePipe: null,
    solarFlarePipe: null,
    kleinFilmPipe: null,
    starForgePipe: null,
    voronoiBubblesPipe: null,
    windstreamPipe: null,
    siliconSkinPipe: null,
    viscoelasticPipe: null,
    balloonPressPipe: null,
    membraneDrumPipe: null,
    punchBagPipe: null,
    siliconeFacePipe: null,
    liquidSkinPipe: null,
    clayPressPipe: null,
    drumHead3DPipe: null,
    siliconeVeilPipe: null,
    liveVideoPipe: null,
    myceliumDomPipe: null,
    dislocationFieldPipe: null,
    dislocationFieldLogosPipe: null,
    marangoniInkPipe: null,
    mcColloughPipe: null,
    hopfFibrationPipe: null,
    slitScanChronoPipe: null,
    glassPipe: null,
    vortexPipe: null,
    supernovaPipe: null,
    plasmaPipe: null,
    driftPipe: null,
    latticePulsePipe: null,
    chronoStrandPipe: null,
    riverPathsPipe: null,
    embroideryPipe: null,
    kaleidoscopeTouchPipe: null,
    cracklePatternPipe: null,
    vineGrowthPipe: null,
    paperFoldPipe: null,
    lichenGrowthPipe: null,
    waveInterferencePipe: null,
    stainedGlassTouchPipe: null,
    webSpinningPipe: null,
    plasmaTouchPipe: null,
    rustGrowthPipe: null,
    bubbleChamberPipe: null,
    tidePoolPipe: null,
    sugarCrystalPipe: null,
    windBlownGrassPipe: null,
    auroraTouchPipe: null,
    sandPilePipe: null,
    bubbleBlowPipe: null,
    smokeRingPipe: null,
    crystalGrowthPipe: null,
    lightningWebPipe: null,
    crackedGlassPipe: null,
    petrifiedBloomPipe: null,
    magneticFilingsPipe: null,
    haloPipe: null,
    lissajousTubePipe: null,
    snowflakePipe: null,
    negativeSpacePipe: null,
    trichromaticPipe: null,
    solarCoronaPipe: null,
    dendriticCrystalPipe: null,
    tyndallBeamPipe: null,
    baroclinicVortexPipe: null,
    dislocationWebPipe: null,
    chladniCellsPipe: null,
    crookesRadiometerPipe: null,
    granularFlowPipe: null,
    savartWheelPipe: null,
    rijkeTubePipe: null,
    ferrofluidSculpturePipe: null,
    sigilsPipe: null,
    magneticMonopolePipe: null,
    timeCrystalPipe: null,
    penroseStairsPipe: null,
    quantumFoamPipe: null,
    fractalSmokePipe: null,
    anyonicBraidingPipe: null,
    hydrodynamicShockPipe: null,
    schwarzschildCrossingPipe: null,
    dynamoPipe: null,
    phaseConjugatePipe: null,
    spectralDecompositionPipe: null,
    anamorphicPipe: null,
    feynmanPathPipe: null,
    photonicCrystalPipe: null,
    tokamakPlasmaPipe: null,
    mobiusLatticePipe: null,
    tesseractShadowPipe: null,
    twinflamePipe: null,
    mulberryPipe: null,
    bifrostMirrorPipe: null,
    stalactitePulsePipe: null,
    catenaryLoomPipe: null,
    lightwingPipe: null,
    cataractPipe: null,
    aspicPipe: null,
    moirefallPipe: null,
    tideglassPipe: null,
    sunTonguePipe: null,
    tessellationMeltPipe: null,
    tessellationMeltTouchPipe: null,
    magneticMonopoleTouchPipe: null,
    fractalSmokeTouchPipe: null,
    tesseractShadowTouchPipe: null,
    resonancePipe: null,
    fontainefeedPipe: null,
    pendantBloomPipe: null,
    bloomPipe: null,
    meshPipe: null,
    orbitsPipe: null,
    cymaticsPipe: null,
    mandelbrotPipe: null,
    kaleidoscopePipe: null,
    lorenzPipe: null,
    phyllotaxisPipe: null,
    quasicrystalPipe: null,
    tunnelPipe: null,
    ferrofluidPipe: null,
    polyhedronPipe: null,
    lightningPipe: null,
    causticsPipe: null,
    worleyTerrainPipe: null,
    magneticPipe: null,
    lSystemPipe: null,
    truchetPipe: null,
    sunburstPipe: null,
    spirographPipe: null,
    dnaPipe: null,
    lavaLampPipe: null,
    bezierPipe: null,
    stereographicPipe: null,
    contourPipe: null,
    cosmosPipe: null,
    galaxyPipe: null,
    oceanStormPipe: null,
    cyberpunkPipe: null,
    crystalCavePipe: null,
    soapBubblePipe: null,
    voronoiGlassPipe: null,
    pixelSortPipe: null,
    wireframeSpherePipe: null,
    blackHolePipe: null,
    liquidMetalPipe: null,
    stainedGlassPipe: null,
    marbleStatuePipe: null,
    psychedelicPipe: null,
    glitchPipe: null,
    quantumFieldPipe: null,
    particleConstellationPipe: null,
    inkInWaterPipe: null,
    wormholeTunnelPipe: null,
    asciiPipe: null,
    inkDropPipe: null,
    gyroidPipe: null,
    schwarzPPipe: null,
    mengerSpongePipe: null,
    kleinBottlePipe: null,
    conwayLifePipe: null,
    stableFluidPipe: null,
    doublePendulumPipe: null,
    sphericalHarmonicsPipe: null,
    becPipe: null,
    beatNebulaPipe: null,
    phononLatticePipe: null,
    magneticPendulumPipe: null,
    schwarzDPipe: null,
    liquidCrystalPipe: null,
    sincWaveFieldPipe: null,
    sineGordonPipe: null,
    skyrmionPipe: null,
    hessianEigenvectorPipe: null,
    braggDiffractionPipe: null,
    curvatureFlowPipe: null,
    cellularPottsPipe: null,
    waveFunctionCollapsePipe: null,
    phaseFieldCrystalPipe: null,
    beatAudio: null,
    sceneFbo: null,
    scratchFbo: null,
    backdropFbo: null,
    velocityFbo: null,
    velocityInfo: null,
    scene: createScene(),
    compiled: null,
    sceneName: "landing",
    pointer: createPointer(),
    timing: createTiming(),
    hud: bindHud(),
    compileMs: 0,
    blurStrength: 1.0,
    inertia: null,
    longFrameCount: 0,
    lastScaleAdjust: 0,
    studio: createStudioState(),
    currentScene: "advanced",
  };
}

/**
 * Walk the compiled instance buffer and emit one instanced draw per partition
 * of nodes that share a fill texture. Plain rect nodes (no texture) share a
 * 1×1 white sampler and are drawn in one pass.
 *
 * Layer 7: per-node filter chain (blur/brightness/contrast/saturate/hueRotate/
 * invert) is baked into the instance buffer (attr 10/11) — no per-filter
 * grouping required. We only partition on the fill texture (text nodes share
 * a glyph atlas), keeping the draw call count proportional to texture groups.
 *
 * Layer 6: also partition on mix-blend-mode (one mode per draw call). All
 * nodes in a partition share the same fill texture AND blend mode, so the
 * FS uniform path stays uniform within a draw call.
 */
function drawScenePartitioned(
  engine: Engine,
  gl: WebGL2RenderingContext,
  _velocityFormat: 0 | 1 | 2,
  _velocityScale: number,
  drawAll: (
    idx: number,
    count: number,
    tex: WebGLTexture | null,
    useFillTex: 0 | 1,
    blendMode: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7,
  ) => void,
  nodeFilter: (n: SceneNode) => boolean = () => true,
): void {
  if (!engine.rectPipe || !engine.compiled) return;
  const nodes = sortedNodes(engine.scene);
  if (nodes.length === 0) return;
  const total = nodes.length;

  interface Seg {
    start: number;
    count: number;
    tex: WebGLTexture | null;
    hasText: boolean;
    blendMode: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7,
  }
  const segments: Seg[] = [];
  let i = 0;
  while (i < total) {
    const n = nodes[i];
    if (!n) { i++; continue; }
    if (n.visibility === "hidden") { i++; continue; }
    if (!nodeFilter(n)) { i++; continue; }
    let tex: WebGLTexture | null = null;
    if (n.type === "text") {
      const entry = getTextTexture(gl, n);
      if (entry) tex = entry.texture;
      // If no entry, fall through to plain rect (white sampler).
    } else if (n.pathD) {
      // Path nodes reuse the same texture-sampling pipeline as text.
      const entry = getPathTexture(gl, n);
      if (entry) tex = entry.texture;
    } else if (n.imageUrl) {
      // <img> nodes and rects with `background-image: url(...)` both end up
      // here. imageTexture caches by (id, url, w, h) and decodes async.
      const entry = getImageTexture(gl, n);
      if (entry) tex = entry.texture;
      // If image hasn't decoded yet, falls through to plain rect (white sampler).
    }
    const isTextured = !!tex;
    const segBlend = blendModeOf(n);
    let j = i + 1;
    while (j < total) {
      const m = nodes[j];
      if (!m) break;
      if (m.visibility === "hidden") { j++; continue; }
      if (!nodeFilter(m)) { j++; continue; }
      if (isTextured) {
        // Textured segment: keep grouping with same-kind textured nodes
        // (text, path, or image) that share the same texture instance.
        // Solid-color nodes break the group.
        const mTextured = m.type === "text" || !!m.pathD || !!m.imageUrl;
        if (!mTextured) break;
        const mTex = m.pathD ? getPathTexture(gl, m)?.texture
                     : m.type === "text" ? getTextTexture(gl, m)?.texture
                     : m.imageUrl ? getImageTexture(gl, m)?.texture
                     : null;
        if (mTex !== tex) break;
      } else {
        // Solid-color segment: keep grouping only with same blend.
        if (m.type === "text" || m.pathD || m.imageUrl) break;
      }
      if (blendModeOf(m) !== segBlend) break;
      j++;
    }
    segments.push({ start: i, count: j - i, tex, hasText: isTextured, blendMode: segBlend });
    i = j;
  }

  for (const s of segments) {
    drawAll(s.start, s.count, s.tex, s.hasText ? 1 : 0, s.blendMode);
  }
}

function rebuildSceneFBO(engine: Engine): void {
  if (!engine.ctx) return;
  const gl = engine.ctx.gl;
  const w = engine.canvas.width;
  const h = engine.canvas.height;
  if (engine.sceneFbo && (engine.sceneFbo.width !== w || engine.sceneFbo.height !== h)) {
    engine.sceneFbo = null;
    engine.scratchFbo = null;
    engine.backdropFbo = null;
    engine.velocityFbo = null;
    engine.velocityInfo = null;
  }
  if (!engine.sceneFbo) {
    engine.sceneFbo = createSceneFBO(gl, w, h);
  }
  if (!engine.scratchFbo && engine.sceneFbo) {
    // Same format as sceneFbo (RGBA8 premultiplied) — same renderer path.
    engine.scratchFbo = createFBO(gl, w, h);
  }
  if (!engine.backdropFbo && engine.sceneFbo) {
    // Layer 5: dedicated FBO for backdrop-filter nodes. Same size as sceneFbo.
    // Written in pass B (backdrop pass), sampled as a texture and composited
    // on top of sceneFbo in pass C.
    engine.backdropFbo = createFBO(gl, w, h);
  }
  if (!engine.velocityFbo) {
    const v = createVelocityFBO(gl, w, h);
    if (v) {
      engine.velocityFbo = v.fbo;
      engine.velocityInfo = v.info;
    }
  }
}

function switchScene(engine: Engine, name: string): void {
  if (!engine.ctx) return;
  const w = engine.canvas.clientWidth;
  const h = engine.canvas.clientHeight;
  const factory = SCENES[name] ?? PAGE_SCENES[name] ?? ADVANCED_SCENES[name];
  if (!factory) return;
  let scene: Scene;
  try {
    scene = factory(w, h);
  } catch (err) {
    console.error(`[nspp] switchScene("${name}") threw:`, err);
    return;
  }
  engine.scene = scene;
  engine.sceneName = name;
  engine.currentScene = name;
  // Reset scroll momentum/inertia — it belongs to the previous scroll context,
  // not the new one. Without this, a fast flick on the previous scene's
  // animation would carry over and "snap" the new scene's scroll-tracked
  // nodes to the wrong position.
  resetScrollState();
  // If any node has a scroll-tracked animation, make the page scrollable
  // by setting document.body's min-height to 3× viewport height. The scene
  // content itself is on the canvas (fixed), so this only affects the page's
  // scroll height. Remove it when no scroll tracking is active.
  const hasScrollTrack = Array.from(engine.scene.nodes.values()).some(
    (n) => n.animation?.scrollTrack,
  );
  if (typeof document !== "undefined") {
    document.body.style.minHeight = hasScrollTrack
      ? `${(typeof window !== "undefined" ? window.innerHeight : 600) * 3}px`
      : "";
  }
  // Initialize prev = current so velocity is 0 on frame 0
  const nodes = Array.from(engine.scene.nodes.values());
  for (const n of nodes) {
    n.prevX = n.x;
    n.prevY = n.y;
    n.prevRotation = n.rotation;
  }
  // Clear any prior selection — it doesn't carry across scenes.
  engine.studio.selected = null;
  engine.studio.dragMode = null;
  engine.studio.dragHandle = null;
  // Apply persisted state if we have one for this scene (positions, sizes,
  // background toggles). Skip CSS / showpiece scenes to avoid stomping
  // keyframe-driven positions.
  if (name === "studio" || /^[A-G]$/.test(name)) {
    const saved = loadSceneState(name);
    if (saved) applyPersistedState(engine.scene, saved);
  }
  // Mount the studio toolbar only when actually on the studio scene.
  // It's a developer authoring tool — it shouldn't pollute the public
  // landing / showpiece / scene demos.
  if (name === "studio") {
    mountStudioToolbar(engine);
  } else {
    unmountStudioToolbar(engine);
  }
  // Toggle the body.landing class so CSS can letterbox the canvas for
  // portrait scenes on landscape viewports.
  if (name === "landing") {
    document.body.classList.add("landing");
  } else {
    document.body.classList.remove("landing");
  }
  // The lab scene provides its own bottom tab bar for mode switching.
  // Hide the global scene-picker DOM on lab so its 6 internal modes
  // (Chase / Paint / Orbit / Reel / Video Wall / Burst) are visible
  // instead of obscured by the global picker row.
  if (name === "lab") {
    document.body.classList.add("lab-mode");
  } else {
    document.body.classList.remove("lab-mode");
  }
  // The paint visualizer has its own DOM overlay with its own branding —
  // hide the top-left NSPP brand badge so they don't overlap.
  if (name === "paint-visualizer") {
    document.body.classList.add("paint-mode");
  } else {
    document.body.classList.remove("paint-mode");
  }
  // Paint Visualizer: 2-screen product configurator demo (Home Depot
  // pitch). Combines a NSPP-rendered photo panel (left) with a DOM
  // overlay (left) that exposes BEHR/GLIDDEN/PPG/KILZ paint swatches,
  // sheen slider, coat slider, custom hex input, and 6 room thumbnails.
  // Selecting this scene wires the DOM overlay into the scene graph so
  // selector changes mutate the heroImage's per-instance filter chain
  // in real time. Leaving this scene unmounts the overlay.
  if (name === "paint-visualizer") {
    const findTagged = (tag: string) => {
      for (const n of engine.scene.nodes.values()) {
        if (n.tagName === tag) return n;
      }
      return null;
    };
    const heroImage = findTagged("paint-hero-image");
    const tintOverlay = findTagged("paint-tint-overlay");
    const brandText = findTagged("paint-brand-text");
    const colorText = findTagged("paint-color-text");
    const hexText = findTagged("paint-hex-text");
    const sheenText = findTagged("paint-sheen-text");
    if (heroImage && tintOverlay && brandText && colorText && hexText && sheenText) {
      const refs: PaintRefs = {
        scene: engine.scene,
        heroImage, tintOverlay, brandText, colorText, hexText, sheenText,
      };
      paintDemoRoute.publish(refs);
    }
    paintDemoRoute.mount(engine.canvas.clientWidth, engine.canvas.clientHeight);
  } else {
    paintDemoRoute.unmount();
  }
  // Chase Demo: 2-screen product detail with LIVE DOM→scene graph
  // wiring (Thérèse dress product page). Left side is a DOM panel with
  // product name, description, price inputs and 6 thumbnail buttons;
  // right side is the canvas-rendered photo panel with continuous
  // Ken Burns zoom. Typing in the inputs mutates caption SceneNodes
  // in real time; clicking thumbnails swaps the hero image.
  if (name === "chase") {
    const findTagged = (tag: string) => {
      for (const n of engine.scene.nodes.values()) {
        if (n.tagName === tag) return n;
      }
      return null;
    };
    const heroImage = findTagged("chase-hero-image");
    const captionName = findTagged("chase-caption-name");
    const captionDesc = findTagged("chase-caption-desc");
    const captionPrice = findTagged("chase-caption-price");
    if (heroImage && captionName && captionDesc && captionPrice) {
      const refs: ChaseRefs = {
        scene: engine.scene,
        heroImage,
        captionName, captionDesc, captionPrice,
      };
      chaseDemoRoute.publish(refs);
    }
    chaseDemoRoute.mount(engine.canvas.clientWidth, engine.canvas.clientHeight);
    engine.customDraw = undefined;
  } else {
    chaseDemoRoute.unmount();
  }

  // Chain scene — fullscreen 3D raymarcher that bypasses the 2D SDF
  // pipeline entirely. Lazily create the chain pipeline on first use.
  if (name === "chain") {
    if (!engine.chainPipe && engine.ctx) {
      engine.chainPipe = createChainPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.chainPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      drawChain(gl, e.chainPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "morph") {
    // Morph scene — same pattern as chain, but renders a continuously
    // shape-shifting 3D form with iridescent material and moving lights.
    if (!engine.morphPipe && engine.ctx) {
      engine.morphPipe = createMorphPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.morphPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      drawMorph(gl, e.morphPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "cine") {
    // Cine scene — fullscreen fragment shader cinematic effect.
    if (!engine.cinePipe && engine.ctx) {
      engine.cinePipe = createCinePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.cinePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      drawCine(gl, e.cinePipe, e.canvas.width, e.canvas.height, t, 0.5, 0.5);
    };
  } else if (name === "beat") {
    // Beat Nebula — volumetric raymarcher, Noztol's Shadertoy ported.
    // Audio-reactive when engine.beatAudio is set (mic capture);
    // otherwise uses synthetic bands so the scene stays alive.
    if (!engine.beatPipe && engine.ctx) {
      engine.beatPipe = createBeatNebulaPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.beatPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      // Update audio bands: real if mic active, else synthetic.
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeRealtimeBands(e.beatAudio.freqData, e.beatPipe.bands);
      } else {
        computeSyntheticBands(t, e.beatPipe.bands);
      }
      drawBeatNebula(gl, e.beatPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "hex") {
    // Bioluminescent Hex Cells — Shadertoy port. Same audio-reactive
    // pattern as beat but rendering a structured hex grid instead of a
    // volumetric nebula.
    if (!engine.hexPipe && engine.ctx) {
      engine.hexPipe = createHexPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.hexPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeHexRealtimeBands(e.beatAudio.freqData, e.hexPipe.bands);
      } else {
        computeHexBands(t, e.hexPipe.bands);
      }
      drawHex(gl, e.hexPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "coral") {
    // Bioluminescent coral colony.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.coralPipe ?? (engine.coralPipe = createCoralPipeline(engine.ctx!.gl)),
      computeCoralBands,
      computeCoralRealtimeBands,
      drawCoral,
    );
  } else if (name === "vines") {
    // Helix vines + leaves.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.vinesPipe ?? (engine.vinesPipe = createVinesPipeline(engine.ctx!.gl)),
      computeVinesBands,
      computeVinesRealtimeBands,
      drawVines,
    );
  } else if (name === "neuralNet") {
    // 48-node firing neural net with traveling pulses.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.neuralNetPipe ?? (engine.neuralNetPipe = createNeuralNetPipeline(engine.ctx!.gl)),
      computeNeuralNetBands,
      computeNeuralNetRealtimeBands,
      drawNeuralNet,
    );
  } else if (name === "voronoiCrystal") {
    // 3D voronoi cells with caustics.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.voronoiCrystalPipe ?? (engine.voronoiCrystalPipe = createVoronoiCrystalPipeline(engine.ctx!.gl)),
      computeVoronoiCrystalBands,
      computeVoronoiCrystalRealtimeBands,
      drawVoronoiCrystal,
    );
  } else if (name === "metaballVol") {
    // 24 metaballs with specular + rim glow.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.metaballVolPipe ?? (engine.metaballVolPipe = createMetaballVolPipeline(engine.ctx!.gl)),
      computeMetaballVolBands,
      computeMetaballVolRealtimeBands,
      drawMetaballVol,
    );
  } else if (name === "smokeColumns") {
    // 6 columns + curl noise.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.smokeColumnsPipe ?? (engine.smokeColumnsPipe = createSmokeColumnsPipeline(engine.ctx!.gl)),
      computeSmokeColumnsBands,
      computeSmokeColumnsRealtimeBands,
      drawSmokeColumns,
    );
  } else if (name === "liquidMetalVol") {
    // Mercury blobs with reflective surface.
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.liquidMetalVolPipe ?? (engine.liquidMetalVolPipe = createLiquidMetalVolPipeline(engine.ctx!.gl)),
      computeLiquidMetalVolBands,
      computeLiquidMetalVolRealtimeBands,
      drawLiquidMetalVol,
    );
  } else if (name === "aurora") {
    if (!engine.auroraPipe && engine.ctx) {
      engine.auroraPipe = createAuroraPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.auroraPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeAuroraRealtimeBands(e.beatAudio.freqData, e.auroraPipe.bands);
      } else {
        computeAuroraBands(t, e.auroraPipe.bands);
      }
      drawAurora(gl, e.auroraPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "vortex") {
    if (!engine.vortexPipe && engine.ctx) {
      engine.vortexPipe = createVortexPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.vortexPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeVortexRealtimeBands(e.beatAudio.freqData, e.vortexPipe.bands);
      } else {
        computeVortexBands(t, e.vortexPipe.bands);
      }
      drawVortex(gl, e.vortexPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "supernova") {
    if (!engine.supernovaPipe && engine.ctx) {
      engine.supernovaPipe = createSupernovaPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.supernovaPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSupernovaRealtimeBands(e.beatAudio.freqData, e.supernovaPipe.bands);
      } else {
        computeSupernovaBands(t, e.supernovaPipe.bands);
      }
      drawSupernova(gl, e.supernovaPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "plasma") {
    if (!engine.plasmaPipe && engine.ctx) {
      engine.plasmaPipe = createPlasmaPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.plasmaPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePlasmaRealtimeBands(e.beatAudio.freqData, e.plasmaPipe.bands);
      } else {
        computePlasmaBands(t, e.plasmaPipe.bands);
      }
      drawPlasma(gl, e.plasmaPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "drift") {
    if (!engine.driftPipe && engine.ctx) {
      engine.driftPipe = createDriftPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.driftPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeDriftRealtimeBands(e.beatAudio.freqData, e.driftPipe.bands);
      } else {
        computeDriftBands(t, e.driftPipe.bands);
      }
      drawDrift(gl, e.driftPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "latticePulse") {
    if (!engine.latticePulsePipe && engine.ctx) {
      engine.latticePulsePipe = createLatticePulsePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.latticePulsePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeLatticePulseRealtimeBands(e.beatAudio.freqData, e.latticePulsePipe.bands);
      } else {
        computeLatticePulseBands(t, e.latticePulsePipe.bands);
      }
      drawLatticePulse(gl, e.latticePulsePipe, e.canvas.width, e.canvas.height, t);
    };

  } else if (name === "chronoStrand") {
    if (!engine.chronoStrandPipe && engine.ctx) {
      engine.chronoStrandPipe = createChronoStrandPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.chronoStrandPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeChronoStrandRealtimeBands(e.beatAudio.freqData, e.chronoStrandPipe.bands);
      } else {
        computeChronoStrandBands(t, e.chronoStrandPipe.bands);
      }
      drawChronoStrand(gl, e.chronoStrandPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "riverPaths") {
    if (!engine.riverPathsPipe && engine.ctx) {
      engine.riverPathsPipe = createRiverPathsPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.riverPathsPipe || !e.ctx) return;
      riverPathsUpdatePointer(e.riverPathsPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeRiverPathsRealtimeBands(e.beatAudio.freqData, e.riverPathsPipe.bands);
      } else {
        computeRiverPathsBands(t, e.riverPathsPipe.bands);
      }
      drawRiverPaths(gl, e.riverPathsPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "embroidery") {
    if (!engine.embroideryPipe && engine.ctx) {
      engine.embroideryPipe = createEmbroideryPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.embroideryPipe || !e.ctx) return;
      embroideryUpdatePointer(e.embroideryPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeEmbroideryRealtimeBands(e.beatAudio.freqData, e.embroideryPipe.bands);
      } else {
        computeEmbroideryBands(t, e.embroideryPipe.bands);
      }
      drawEmbroidery(gl, e.embroideryPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "kaleidoscopeTouch") {
    if (!engine.kaleidoscopeTouchPipe && engine.ctx) {
      engine.kaleidoscopeTouchPipe = createKaleidoscopeTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.kaleidoscopeTouchPipe || !e.ctx) return;
      kaleidoscopeTouchUpdatePointer(e.kaleidoscopeTouchPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeKaleidoscopeTouchRealtimeBands(e.beatAudio.freqData, e.kaleidoscopeTouchPipe.bands);
      } else {
        computeKaleidoscopeTouchBands(t, e.kaleidoscopeTouchPipe.bands);
      }
      drawKaleidoscopeTouch(gl, e.kaleidoscopeTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "cracklePattern") {
    if (!engine.cracklePatternPipe && engine.ctx) {
      engine.cracklePatternPipe = createCracklePatternPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.cracklePatternPipe || !e.ctx) return;
      cracklePatternUpdatePointer(e.cracklePatternPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeCracklePatternRealtimeBands(e.beatAudio.freqData, e.cracklePatternPipe.bands);
      } else {
        computeCracklePatternBands(t, e.cracklePatternPipe.bands);
      }
      drawCracklePattern(gl, e.cracklePatternPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "vineGrowth") {
    if (!engine.vineGrowthPipe && engine.ctx) {
      engine.vineGrowthPipe = createVineGrowthPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.vineGrowthPipe || !e.ctx) return;
      vineGrowthUpdatePointer(e.vineGrowthPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeVineGrowthRealtimeBands(e.beatAudio.freqData, e.vineGrowthPipe.bands);
      } else {
        computeVineGrowthBands(t, e.vineGrowthPipe.bands);
      }
      drawVineGrowth(gl, e.vineGrowthPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "paperFold") {
    if (!engine.paperFoldPipe && engine.ctx) {
      engine.paperFoldPipe = createPaperFoldPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.paperFoldPipe || !e.ctx) return;
      paperFoldUpdatePointer(e.paperFoldPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePaperFoldRealtimeBands(e.beatAudio.freqData, e.paperFoldPipe.bands);
      } else {
        computePaperFoldBands(t, e.paperFoldPipe.bands);
      }
      drawPaperFold(gl, e.paperFoldPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "lichenGrowth") {
    if (!engine.lichenGrowthPipe && engine.ctx) {
      engine.lichenGrowthPipe = createLichenGrowthPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.lichenGrowthPipe || !e.ctx) return;
      lichenGrowthUpdatePointer(e.lichenGrowthPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeLichenGrowthRealtimeBands(e.beatAudio.freqData, e.lichenGrowthPipe.bands);
      } else {
        computeLichenGrowthBands(t, e.lichenGrowthPipe.bands);
      }
      drawLichenGrowth(gl, e.lichenGrowthPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "waveInterference") {
    if (!engine.waveInterferencePipe && engine.ctx) {
      engine.waveInterferencePipe = createWaveInterferencePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.waveInterferencePipe || !e.ctx) return;
      waveInterferenceUpdatePointer(e.waveInterferencePipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeWaveInterferenceRealtimeBands(e.beatAudio.freqData, e.waveInterferencePipe.bands);
      } else {
        computeWaveInterferenceBands(t, e.waveInterferencePipe.bands);
      }
      drawWaveInterference(gl, e.waveInterferencePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "stainedGlassTouch") {
    if (!engine.stainedGlassTouchPipe && engine.ctx) {
      engine.stainedGlassTouchPipe = createStainedGlassTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.stainedGlassTouchPipe || !e.ctx) return;
      stainedGlassTouchUpdatePointer(e.stainedGlassTouchPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeStainedGlassTouchRealtimeBands(e.beatAudio.freqData, e.stainedGlassTouchPipe.bands);
      } else {
        computeStainedGlassTouchBands(t, e.stainedGlassTouchPipe.bands);
      }
      drawStainedGlassTouch(gl, e.stainedGlassTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "webSpinning") {
    if (!engine.webSpinningPipe && engine.ctx) {
      engine.webSpinningPipe = createWebSpinningPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.webSpinningPipe || !e.ctx) return;
      webSpinningUpdatePointer(e.webSpinningPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeWebSpinningRealtimeBands(e.beatAudio.freqData, e.webSpinningPipe.bands);
      } else {
        computeWebSpinningBands(t, e.webSpinningPipe.bands);
      }
      drawWebSpinning(gl, e.webSpinningPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "plasmaTouch") {
    if (!engine.plasmaTouchPipe && engine.ctx) {
      engine.plasmaTouchPipe = createPlasmaTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.plasmaTouchPipe || !e.ctx) return;
      plasmaTouchUpdatePointer(e.plasmaTouchPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePlasmaTouchRealtimeBands(e.beatAudio.freqData, e.plasmaTouchPipe.bands);
      } else {
        computePlasmaTouchBands(t, e.plasmaTouchPipe.bands);
      }
      drawPlasmaTouch(gl, e.plasmaTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "rustGrowth") {
    if (!engine.rustGrowthPipe && engine.ctx) {
      engine.rustGrowthPipe = createRustGrowthPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.rustGrowthPipe || !e.ctx) return;
      rustGrowthUpdatePointer(e.rustGrowthPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeRustGrowthRealtimeBands(e.beatAudio.freqData, e.rustGrowthPipe.bands);
      } else {
        computeRustGrowthBands(t, e.rustGrowthPipe.bands);
      }
      drawRustGrowth(gl, e.rustGrowthPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "bubbleChamber") {
    if (!engine.bubbleChamberPipe && engine.ctx) {
      engine.bubbleChamberPipe = createBubbleChamberPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.bubbleChamberPipe || !e.ctx) return;
      bubbleChamberUpdatePointer(e.bubbleChamberPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeBubbleChamberRealtimeBands(e.beatAudio.freqData, e.bubbleChamberPipe.bands);
      } else {
        computeBubbleChamberBands(t, e.bubbleChamberPipe.bands);
      }
      drawBubbleChamber(gl, e.bubbleChamberPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tidePool") {
    if (!engine.tidePoolPipe && engine.ctx) {
      engine.tidePoolPipe = createTidePoolPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tidePoolPipe || !e.ctx) return;
      tidePoolUpdatePointer(e.tidePoolPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTidePoolRealtimeBands(e.beatAudio.freqData, e.tidePoolPipe.bands);
      } else {
        computeTidePoolBands(t, e.tidePoolPipe.bands);
      }
      drawTidePool(gl, e.tidePoolPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "sugarCrystal") {
    if (!engine.sugarCrystalPipe && engine.ctx) {
      engine.sugarCrystalPipe = createSugarCrystalPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.sugarCrystalPipe || !e.ctx) return;
      sugarCrystalUpdatePointer(e.sugarCrystalPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSugarCrystalRealtimeBands(e.beatAudio.freqData, e.sugarCrystalPipe.bands);
      } else {
        computeSugarCrystalBands(t, e.sugarCrystalPipe.bands);
      }
      drawSugarCrystal(gl, e.sugarCrystalPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "windBlownGrass") {
    if (!engine.windBlownGrassPipe && engine.ctx) {
      engine.windBlownGrassPipe = createWindBlownGrassPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.windBlownGrassPipe || !e.ctx) return;
      windBlownGrassUpdatePointer(e.windBlownGrassPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeWindBlownGrassRealtimeBands(e.beatAudio.freqData, e.windBlownGrassPipe.bands);
      } else {
        computeWindBlownGrassBands(t, e.windBlownGrassPipe.bands);
      }
      drawWindBlownGrass(gl, e.windBlownGrassPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "auroraTouch") {
    if (!engine.auroraTouchPipe && engine.ctx) {
      engine.auroraTouchPipe = createAuroraTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.auroraTouchPipe || !e.ctx) return;
      auroraTouchUpdatePointer(e.auroraTouchPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeAuroraTouchRealtimeBands(e.beatAudio.freqData, e.auroraTouchPipe.bands);
      } else {
        computeAuroraTouchBands(t, e.auroraTouchPipe.bands);
      }
      drawAuroraTouch(gl, e.auroraTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "sandPile") {
    if (!engine.sandPilePipe && engine.ctx) {
      engine.sandPilePipe = createSandPilePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.sandPilePipe || !e.ctx) return;
      sandPileUpdatePointer(e.sandPilePipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSandPileRealtimeBands(e.beatAudio.freqData, e.sandPilePipe.bands);
      } else {
        computeSandPileBands(t, e.sandPilePipe.bands);
      }
      drawSandPile(gl, e.sandPilePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "bubbleBlow") {
    if (!engine.bubbleBlowPipe && engine.ctx) {
      engine.bubbleBlowPipe = createBubbleBlowPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.bubbleBlowPipe || !e.ctx) return;
      bubbleBlowUpdatePointer(e.bubbleBlowPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeBubbleBlowRealtimeBands(e.beatAudio.freqData, e.bubbleBlowPipe.bands);
      } else {
        computeBubbleBlowBands(t, e.bubbleBlowPipe.bands);
      }
      drawBubbleBlow(gl, e.bubbleBlowPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "smokeRing") {
    if (!engine.smokeRingPipe && engine.ctx) {
      engine.smokeRingPipe = createSmokeRingPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.smokeRingPipe || !e.ctx) return;
      smokeRingUpdatePointer(e.smokeRingPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSmokeRingRealtimeBands(e.beatAudio.freqData, e.smokeRingPipe.bands);
      } else {
        computeSmokeRingBands(t, e.smokeRingPipe.bands);
      }
      drawSmokeRing(gl, e.smokeRingPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "crystalGrowth") {
    if (!engine.crystalGrowthPipe && engine.ctx) {
      engine.crystalGrowthPipe = createCrystalGrowthPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.crystalGrowthPipe || !e.ctx) return;
      crystalGrowthUpdatePointer(e.crystalGrowthPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeCrystalGrowthRealtimeBands(e.beatAudio.freqData, e.crystalGrowthPipe.bands);
      } else {
        computeCrystalGrowthBands(t, e.crystalGrowthPipe.bands);
      }
      drawCrystalGrowth(gl, e.crystalGrowthPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "lightningWeb") {
    if (!engine.lightningWebPipe && engine.ctx) {
      engine.lightningWebPipe = createLightningWebPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.lightningWebPipe || !e.ctx) return;
      lightningWebUpdatePointer(e.lightningWebPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeLightningWebRealtimeBands(e.beatAudio.freqData, e.lightningWebPipe.bands);
      } else {
        computeLightningWebBands(t, e.lightningWebPipe.bands);
      }
      drawLightningWeb(gl, e.lightningWebPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "crackedGlass") {
    if (!engine.crackedGlassPipe && engine.ctx) {
      engine.crackedGlassPipe = createCrackedGlassPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.crackedGlassPipe || !e.ctx) return;
      crackedGlassUpdatePointer(e.crackedGlassPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeCrackedGlassRealtimeBands(e.beatAudio.freqData, e.crackedGlassPipe.bands);
      } else {
        computeCrackedGlassBands(t, e.crackedGlassPipe.bands);
      }
      drawCrackedGlass(gl, e.crackedGlassPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "petrifiedBloom") {
    if (!engine.petrifiedBloomPipe && engine.ctx) {
      engine.petrifiedBloomPipe = createPetrifiedBloomPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.petrifiedBloomPipe || !e.ctx) return;
      petrifiedBloomUpdatePointer(e.petrifiedBloomPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePetrifiedBloomRealtimeBands(e.beatAudio.freqData, e.petrifiedBloomPipe.bands);
      } else {
        computePetrifiedBloomBands(t, e.petrifiedBloomPipe.bands);
      }
      drawPetrifiedBloom(gl, e.petrifiedBloomPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "magneticFilings") {
    if (!engine.magneticFilingsPipe && engine.ctx) {
      engine.magneticFilingsPipe = createMagneticFilingsPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.magneticFilingsPipe || !e.ctx) return;
      magneticFilingsUpdatePointer(e.magneticFilingsPipe, e.pointer.x, e.pointer.y, e.pointer.active, performance.now() / 1000);
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMagneticFilingsRealtimeBands(e.beatAudio.freqData, e.magneticFilingsPipe.bands);
      } else {
        computeMagneticFilingsBands(t, e.magneticFilingsPipe.bands);
      }
      drawMagneticFilings(gl, e.magneticFilingsPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "halo") {
    if (!engine.haloPipe && engine.ctx) {
      engine.haloPipe = createHaloPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.haloPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeHaloRealtimeBands(e.beatAudio.freqData, e.haloPipe.bands);
      } else {
        computeHaloBands(t, e.haloPipe.bands);
      }
      drawHalo(gl, e.haloPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "lissajousTube") {
    if (!engine.lissajousTubePipe && engine.ctx) {
      engine.lissajousTubePipe = createLissajousTubePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.lissajousTubePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeLissajousTubeRealtimeBands(e.beatAudio.freqData, e.lissajousTubePipe.bands);
      } else {
        computeLissajousTubeBands(t, e.lissajousTubePipe.bands);
      }
      drawLissajousTube(gl, e.lissajousTubePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "snowflake") {
    if (!engine.snowflakePipe && engine.ctx) {
      engine.snowflakePipe = createSnowflakePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.snowflakePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSnowflakeRealtimeBands(e.beatAudio.freqData, e.snowflakePipe.bands);
      } else {
        computeSnowflakeBands(t, e.snowflakePipe.bands);
      }
      drawSnowflake(gl, e.snowflakePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "negativeSpace") {
    if (!engine.negativeSpacePipe && engine.ctx) {
      engine.negativeSpacePipe = createNegativeSpacePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.negativeSpacePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeNegativeSpaceRealtimeBands(e.beatAudio.freqData, e.negativeSpacePipe.bands);
      } else {
        computeNegativeSpaceBands(t, e.negativeSpacePipe.bands);
      }
      drawNegativeSpace(gl, e.negativeSpacePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "trichromatic") {
    if (!engine.trichromaticPipe && engine.ctx) {
      engine.trichromaticPipe = createTrichromaticPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.trichromaticPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTrichromaticRealtimeBands(e.beatAudio.freqData, e.trichromaticPipe.bands);
      } else {
        computeTrichromaticBands(t, e.trichromaticPipe.bands);
      }
      drawTrichromatic(gl, e.trichromaticPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "solarCorona") {
    if (!engine.solarCoronaPipe && engine.ctx) {
      engine.solarCoronaPipe = createSolarCoronaPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.solarCoronaPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSolarCoronaRealtimeBands(e.beatAudio.freqData, e.solarCoronaPipe.bands);
      } else {
        computeSolarCoronaBands(t, e.solarCoronaPipe.bands);
      }
      drawSolarCorona(gl, e.solarCoronaPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "dendriticCrystal") {
    if (!engine.dendriticCrystalPipe && engine.ctx) {
      engine.dendriticCrystalPipe = createDendriticCrystalPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.dendriticCrystalPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeDendriticCrystalRealtimeBands(e.beatAudio.freqData, e.dendriticCrystalPipe.bands);
      } else {
        computeDendriticCrystalBands(t, e.dendriticCrystalPipe.bands);
      }
      drawDendriticCrystal(gl, e.dendriticCrystalPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tyndallBeam") {
    if (!engine.tyndallBeamPipe && engine.ctx) {
      engine.tyndallBeamPipe = createTyndallBeamPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tyndallBeamPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTyndallBeamRealtimeBands(e.beatAudio.freqData, e.tyndallBeamPipe.bands);
      } else {
        computeTyndallBeamBands(t, e.tyndallBeamPipe.bands);
      }
      drawTyndallBeam(gl, e.tyndallBeamPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "baroclinicVortex") {
    if (!engine.baroclinicVortexPipe && engine.ctx) {
      engine.baroclinicVortexPipe = createBaroclinicVortexPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.baroclinicVortexPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeBaroclinicVortexRealtimeBands(e.beatAudio.freqData, e.baroclinicVortexPipe.bands);
      } else {
        computeBaroclinicVortexBands(t, e.baroclinicVortexPipe.bands);
      }
      drawBaroclinicVortex(gl, e.baroclinicVortexPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "dislocationWeb") {
    if (!engine.dislocationWebPipe && engine.ctx) {
      engine.dislocationWebPipe = createDislocationWebPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.dislocationWebPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeDislocationWebRealtimeBands(e.beatAudio.freqData, e.dislocationWebPipe.bands);
      } else {
        computeDislocationWebBands(t, e.dislocationWebPipe.bands);
      }
      drawDislocationWeb(gl, e.dislocationWebPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "chladniCells") {
    if (!engine.chladniCellsPipe && engine.ctx) {
      engine.chladniCellsPipe = createChladniCellsPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.chladniCellsPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeChladniCellsRealtimeBands(e.beatAudio.freqData, e.chladniCellsPipe.bands);
      } else {
        computeChladniCellsBands(t, e.chladniCellsPipe.bands);
      }
      drawChladniCells(gl, e.chladniCellsPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "crookesRadiometer") {
    if (!engine.crookesRadiometerPipe && engine.ctx) {
      engine.crookesRadiometerPipe = createCrookesRadiometerPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.crookesRadiometerPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeCrookesRadiometerRealtimeBands(e.beatAudio.freqData, e.crookesRadiometerPipe.bands);
      } else {
        computeCrookesRadiometerBands(t, e.crookesRadiometerPipe.bands);
      }
      drawCrookesRadiometer(gl, e.crookesRadiometerPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "granularFlow") {
    if (!engine.granularFlowPipe && engine.ctx) {
      engine.granularFlowPipe = createGranularFlowPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.granularFlowPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeGranularFlowRealtimeBands(e.beatAudio.freqData, e.granularFlowPipe.bands);
      } else {
        computeGranularFlowBands(t, e.granularFlowPipe.bands);
      }
      drawGranularFlow(gl, e.granularFlowPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "savartWheel") {
    if (!engine.savartWheelPipe && engine.ctx) {
      engine.savartWheelPipe = createSavartWheelPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.savartWheelPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSavartWheelRealtimeBands(e.beatAudio.freqData, e.savartWheelPipe.bands);
      } else {
        computeSavartWheelBands(t, e.savartWheelPipe.bands);
      }
      drawSavartWheel(gl, e.savartWheelPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "rijkeTube") {
    if (!engine.rijkeTubePipe && engine.ctx) {
      engine.rijkeTubePipe = createRijkeTubePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.rijkeTubePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeRijkeTubeRealtimeBands(e.beatAudio.freqData, e.rijkeTubePipe.bands);
      } else {
        computeRijkeTubeBands(t, e.rijkeTubePipe.bands);
      }
      drawRijkeTube(gl, e.rijkeTubePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "ferrofluidSculpture") {
    if (!engine.ferrofluidSculpturePipe && engine.ctx) {
      engine.ferrofluidSculpturePipe = createFerrofluidSculpturePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.ferrofluidSculpturePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeFerrofluidSculptureRealtimeBands(e.beatAudio.freqData, e.ferrofluidSculpturePipe.bands);
      } else {
        computeFerrofluidSculptureBands(t, e.ferrofluidSculpturePipe.bands);
      }
      drawFerrofluidSculpture(gl, e.ferrofluidSculpturePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "sigils") {
    if (!engine.sigilsPipe && engine.ctx) {
      engine.sigilsPipe = createSigilsPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.sigilsPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSigilsRealtimeBands(e.beatAudio.freqData, e.sigilsPipe.bands);
      } else {
        computeSigilsBands(t, e.sigilsPipe.bands);
      }
      drawSigils(gl, e.sigilsPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "magneticMonopole") {
    if (!engine.magneticMonopolePipe && engine.ctx) {
      engine.magneticMonopolePipe = createMagneticMonopolePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.magneticMonopolePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMagneticMonopoleRealtimeBands(e.beatAudio.freqData, e.magneticMonopolePipe.bands);
      } else {
        computeMagneticMonopoleBands(t, e.magneticMonopolePipe.bands);
      }
      drawMagneticMonopole(gl, e.magneticMonopolePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "timeCrystal") {
    if (!engine.timeCrystalPipe && engine.ctx) {
      engine.timeCrystalPipe = createTimeCrystalPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.timeCrystalPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTimeCrystalRealtimeBands(e.beatAudio.freqData, e.timeCrystalPipe.bands);
      } else {
        computeTimeCrystalBands(t, e.timeCrystalPipe.bands);
      }
      drawTimeCrystal(gl, e.timeCrystalPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "penroseStairs") {
    if (!engine.penroseStairsPipe && engine.ctx) {
      engine.penroseStairsPipe = createPenroseStairsPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.penroseStairsPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePenroseStairsRealtimeBands(e.beatAudio.freqData, e.penroseStairsPipe.bands);
      } else {
        computePenroseStairsBands(t, e.penroseStairsPipe.bands);
      }
      drawPenroseStairs(gl, e.penroseStairsPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "quantumFoam") {
    if (!engine.quantumFoamPipe && engine.ctx) {
      engine.quantumFoamPipe = createQuantumFoamPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.quantumFoamPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeQuantumFoamRealtimeBands(e.beatAudio.freqData, e.quantumFoamPipe.bands);
      } else {
        computeQuantumFoamBands(t, e.quantumFoamPipe.bands);
      }
      drawQuantumFoam(gl, e.quantumFoamPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "fractalSmoke") {
    if (!engine.fractalSmokePipe && engine.ctx) {
      engine.fractalSmokePipe = createFractalSmokePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.fractalSmokePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeFractalSmokeRealtimeBands(e.beatAudio.freqData, e.fractalSmokePipe.bands);
      } else {
        computeFractalSmokeBands(t, e.fractalSmokePipe.bands);
      }
      drawFractalSmoke(gl, e.fractalSmokePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "anyonicBraiding") {
    if (!engine.anyonicBraidingPipe && engine.ctx) {
      engine.anyonicBraidingPipe = createAnyonicBraidingPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.anyonicBraidingPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeAnyonicBraidingRealtimeBands(e.beatAudio.freqData, e.anyonicBraidingPipe.bands);
      } else {
        computeAnyonicBraidingBands(t, e.anyonicBraidingPipe.bands);
      }
      drawAnyonicBraiding(gl, e.anyonicBraidingPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "hydrodynamicShock") {
    if (!engine.hydrodynamicShockPipe && engine.ctx) {
      engine.hydrodynamicShockPipe = createHydrodynamicShockPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.hydrodynamicShockPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeHydrodynamicShockRealtimeBands(e.beatAudio.freqData, e.hydrodynamicShockPipe.bands);
      } else {
        computeHydrodynamicShockBands(t, e.hydrodynamicShockPipe.bands);
      }
      drawHydrodynamicShock(gl, e.hydrodynamicShockPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "schwarzschildCrossing") {
    if (!engine.schwarzschildCrossingPipe && engine.ctx) {
      engine.schwarzschildCrossingPipe = createSchwarzschildCrossingPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.schwarzschildCrossingPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSchwarzschildCrossingRealtimeBands(e.beatAudio.freqData, e.schwarzschildCrossingPipe.bands);
      } else {
        computeSchwarzschildCrossingBands(t, e.schwarzschildCrossingPipe.bands);
      }
      drawSchwarzschildCrossing(gl, e.schwarzschildCrossingPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "dynamo") {
    if (!engine.dynamoPipe && engine.ctx) {
      engine.dynamoPipe = createDynamoPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.dynamoPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeDynamoRealtimeBands(e.beatAudio.freqData, e.dynamoPipe.bands);
      } else {
        computeDynamoBands(t, e.dynamoPipe.bands);
      }
      drawDynamo(gl, e.dynamoPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "phaseConjugate") {
    if (!engine.phaseConjugatePipe && engine.ctx) {
      engine.phaseConjugatePipe = createPhaseConjugatePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.phaseConjugatePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePhaseConjugateRealtimeBands(e.beatAudio.freqData, e.phaseConjugatePipe.bands);
      } else {
        computePhaseConjugateBands(t, e.phaseConjugatePipe.bands);
      }
      drawPhaseConjugate(gl, e.phaseConjugatePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "spectralDecomposition") {
    if (!engine.spectralDecompositionPipe && engine.ctx) {
      engine.spectralDecompositionPipe = createSpectralDecompositionPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.spectralDecompositionPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSpectralDecompositionRealtimeBands(e.beatAudio.freqData, e.spectralDecompositionPipe.bands);
      } else {
        computeSpectralDecompositionBands(t, e.spectralDecompositionPipe.bands);
      }
      drawSpectralDecomposition(gl, e.spectralDecompositionPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "anamorphic") {
    if (!engine.anamorphicPipe && engine.ctx) {
      engine.anamorphicPipe = createAnamorphicPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.anamorphicPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeAnamorphicRealtimeBands(e.beatAudio.freqData, e.anamorphicPipe.bands);
      } else {
        computeAnamorphicBands(t, e.anamorphicPipe.bands);
      }
      drawAnamorphic(gl, e.anamorphicPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "feynmanPath") {
    if (!engine.feynmanPathPipe && engine.ctx) {
      engine.feynmanPathPipe = createFeynmanPathPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.feynmanPathPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeFeynmanPathRealtimeBands(e.beatAudio.freqData, e.feynmanPathPipe.bands);
      } else {
        computeFeynmanPathBands(t, e.feynmanPathPipe.bands);
      }
      drawFeynmanPath(gl, e.feynmanPathPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "bec") {
    if (!engine.becPipe && engine.ctx) {
      engine.becPipe = createBECPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.becPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeBECRealtimeBands(e.beatAudio.freqData, e.becPipe.bands);
      } else {
        computeBECBands(t, e.becPipe.bands);
      }
      drawBEC(gl, e.becPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "photonicCrystal") {
    if (!engine.photonicCrystalPipe && engine.ctx) {
      engine.photonicCrystalPipe = createPhotonicCrystalPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.photonicCrystalPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePhotonicCrystalRealtimeBands(e.beatAudio.freqData, e.photonicCrystalPipe.bands);
      } else {
        computePhotonicCrystalBands(t, e.photonicCrystalPipe.bands);
      }
      drawPhotonicCrystal(gl, e.photonicCrystalPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tokamakPlasma") {
    if (!engine.tokamakPlasmaPipe && engine.ctx) {
      engine.tokamakPlasmaPipe = createTokamakPlasmaPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tokamakPlasmaPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTokamakPlasmaRealtimeBands(e.beatAudio.freqData, e.tokamakPlasmaPipe.bands);
      } else {
        computeTokamakPlasmaBands(t, e.tokamakPlasmaPipe.bands);
      }
      drawTokamakPlasma(gl, e.tokamakPlasmaPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "mobiusLattice") {
    if (!engine.mobiusLatticePipe && engine.ctx) {
      engine.mobiusLatticePipe = createMobiusLatticePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.mobiusLatticePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMobiusLatticeRealtimeBands(e.beatAudio.freqData, e.mobiusLatticePipe.bands);
      } else {
        computeMobiusLatticeBands(t, e.mobiusLatticePipe.bands);
      }
      drawMobiusLattice(gl, e.mobiusLatticePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tesseractShadow") {
    if (!engine.tesseractShadowPipe && engine.ctx) {
      engine.tesseractShadowPipe = createTesseractShadowPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tesseractShadowPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTesseractShadowRealtimeBands(e.beatAudio.freqData, e.tesseractShadowPipe.bands);
      } else {
        computeTesseractShadowBands(t, e.tesseractShadowPipe.bands);
      }
      drawTesseractShadow(gl, e.tesseractShadowPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "twinflame") {
    if (!engine.twinflamePipe && engine.ctx) {
      engine.twinflamePipe = createTwinflamePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.twinflamePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTwinflameRealtimeBands(e.beatAudio.freqData, e.twinflamePipe.bands);
      } else {
        computeTwinflameBands(t, e.twinflamePipe.bands);
      }
      drawTwinflame(gl, e.twinflamePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "mulberry") {
    if (!engine.mulberryPipe && engine.ctx) {
      engine.mulberryPipe = createMulberryPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.mulberryPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMulberryRealtimeBands(e.beatAudio.freqData, e.mulberryPipe.bands);
      } else {
        computeMulberryBands(t, e.mulberryPipe.bands);
      }
      drawMulberry(gl, e.mulberryPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "bifrostMirror") {
    if (!engine.bifrostMirrorPipe && engine.ctx) {
      engine.bifrostMirrorPipe = createBifrostMirrorPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.bifrostMirrorPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeBifrostMirrorRealtimeBands(e.beatAudio.freqData, e.bifrostMirrorPipe.bands);
      } else {
        computeBifrostMirrorBands(t, e.bifrostMirrorPipe.bands);
      }
      drawBifrostMirror(gl, e.bifrostMirrorPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "stalactitePulse") {
    if (!engine.stalactitePulsePipe && engine.ctx) {
      engine.stalactitePulsePipe = createStalactitePulsePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.stalactitePulsePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeStalactitePulseRealtimeBands(e.beatAudio.freqData, e.stalactitePulsePipe.bands);
      } else {
        computeStalactitePulseBands(t, e.stalactitePulsePipe.bands);
      }
      drawStalactitePulse(gl, e.stalactitePulsePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "catenaryLoom") {
    if (!engine.catenaryLoomPipe && engine.ctx) {
      engine.catenaryLoomPipe = createCatenaryLoomPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.catenaryLoomPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeCatenaryLoomRealtimeBands(e.beatAudio.freqData, e.catenaryLoomPipe.bands);
      } else {
        computeCatenaryLoomBands(t, e.catenaryLoomPipe.bands);
      }
      drawCatenaryLoom(gl, e.catenaryLoomPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "lightwing") {
    if (!engine.lightwingPipe && engine.ctx) {
      engine.lightwingPipe = createLightwingPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.lightwingPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeLightwingRealtimeBands(e.beatAudio.freqData, e.lightwingPipe.bands);
      } else {
        computeLightwingBands(t, e.lightwingPipe.bands);
      }
      drawLightwing(gl, e.lightwingPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "cataract") {
    if (!engine.cataractPipe && engine.ctx) {
      engine.cataractPipe = createCataractPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.cataractPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeCataractRealtimeBands(e.beatAudio.freqData, e.cataractPipe.bands);
      } else {
        computeCataractBands(t, e.cataractPipe.bands);
      }
      drawCataract(gl, e.cataractPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "aspic") {
    if (!engine.aspicPipe && engine.ctx) {
      engine.aspicPipe = createAspicPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.aspicPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeAspicRealtimeBands(e.beatAudio.freqData, e.aspicPipe.bands);
      } else {
        computeAspicBands(t, e.aspicPipe.bands);
      }
      drawAspic(gl, e.aspicPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "moirefall") {
    if (!engine.moirefallPipe && engine.ctx) {
      engine.moirefallPipe = createMoirefallPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.moirefallPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMoirefallRealtimeBands(e.beatAudio.freqData, e.moirefallPipe.bands);
      } else {
        computeMoirefallBands(t, e.moirefallPipe.bands);
      }
      drawMoirefall(gl, e.moirefallPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tideglass") {
    if (!engine.tideglassPipe && engine.ctx) {
      engine.tideglassPipe = createTideglassPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tideglassPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTideglassRealtimeBands(e.beatAudio.freqData, e.tideglassPipe.bands);
      } else {
        computeTideglassBands(t, e.tideglassPipe.bands);
      }
      drawTideglass(gl, e.tideglassPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "sunTongue") {
    if (!engine.sunTonguePipe && engine.ctx) {
      engine.sunTonguePipe = createSunTonguePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.sunTonguePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeSunTongueRealtimeBands(e.beatAudio.freqData, e.sunTonguePipe.bands);
      } else {
        computeSunTongueBands(t, e.sunTonguePipe.bands);
      }
      drawSunTongue(gl, e.sunTonguePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tessellationMelt") {
    if (!engine.tessellationMeltPipe && engine.ctx) {
      engine.tessellationMeltPipe = createTessellationMeltPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tessellationMeltPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTessellationMeltRealtimeBands(e.beatAudio.freqData, e.tessellationMeltPipe.bands);
      } else {
        computeTessellationMeltBands(t, e.tessellationMeltPipe.bands);
      }
      drawTessellationMelt(gl, e.tessellationMeltPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tessellationMeltTouch") {
    // Touch-aware variant. Same draw path as the original, plus
    // every-frame pointer/clicks updates driven by the embed's
    // pointer forwarder (which mutates pipe.smoothX/smoothY/active
    // directly). In the embed, the iframe is `pointer-events: none`
    // so the engine's own pointer events never fire; the forwarder
    // is the only source of pointer state. We deliberately do NOT
    // call touchSetPointer here — it would overwrite the forwarder's
    // values with engine.pointer (which stays at 0,0 on the embed).
    if (!engine.tessellationMeltTouchPipe && engine.ctx) {
      engine.tessellationMeltTouchPipe = createTessellationMeltTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tessellationMeltTouchPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTessellationMeltTouchRealtimeBands(e.beatAudio.freqData, e.tessellationMeltTouchPipe.bands);
      } else {
        computeTessellationMeltTouchBands(t, e.tessellationMeltTouchPipe.bands);
      }
      drawTessellationMeltTouch(gl, e.tessellationMeltTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "magneticMonopoleTouch") {
    if (!engine.magneticMonopoleTouchPipe && engine.ctx) {
      engine.magneticMonopoleTouchPipe = createMagneticMonopoleTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.magneticMonopoleTouchPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMagneticMonopoleTouchRealtimeBands(e.beatAudio.freqData, e.magneticMonopoleTouchPipe.bands);
      } else {
        computeMagneticMonopoleTouchBands(t, e.magneticMonopoleTouchPipe.bands);
      }
      drawMagneticMonopoleTouch(gl, e.magneticMonopoleTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "fractalSmokeTouch") {
    if (!engine.fractalSmokeTouchPipe && engine.ctx) {
      engine.fractalSmokeTouchPipe = createFractalSmokeTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.fractalSmokeTouchPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeFractalSmokeTouchRealtimeBands(e.beatAudio.freqData, e.fractalSmokeTouchPipe.bands);
      } else {
        computeFractalSmokeTouchBands(t, e.fractalSmokeTouchPipe.bands);
      }
      drawFractalSmokeTouch(gl, e.fractalSmokeTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "tesseractShadowTouch") {
    if (!engine.tesseractShadowTouchPipe && engine.ctx) {
      engine.tesseractShadowTouchPipe = createTesseractShadowTouchPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.tesseractShadowTouchPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeTesseractShadowTouchRealtimeBands(e.beatAudio.freqData, e.tesseractShadowTouchPipe.bands);
      } else {
        computeTesseractShadowTouchBands(t, e.tesseractShadowTouchPipe.bands);
      }
      drawTesseractShadowTouch(gl, e.tesseractShadowTouchPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "resonance") {
    if (!engine.resonancePipe && engine.ctx) {
      engine.resonancePipe = createResonancePipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.resonancePipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeResonanceRealtimeBands(e.beatAudio.freqData, e.resonancePipe.bands);
      } else {
        computeResonanceBands(t, e.resonancePipe.bands);
      }
      drawResonance(gl, e.resonancePipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "fontainefeed") {
    if (!engine.fontainefeedPipe && engine.ctx) {
      engine.fontainefeedPipe = createFontainefeedPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.fontainefeedPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeFontainefeedRealtimeBands(e.beatAudio.freqData, e.fontainefeedPipe.bands);
      } else {
        computeFontainefeedBands(t, e.fontainefeedPipe.bands);
      }
      drawFontainefeed(gl, e.fontainefeedPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "pendantBloom") {
    if (!engine.pendantBloomPipe && engine.ctx) {
      engine.pendantBloomPipe = createPendantBloomPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.pendantBloomPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computePendantBloomRealtimeBands(e.beatAudio.freqData, e.pendantBloomPipe.bands);
      } else {
        computePendantBloomBands(t, e.pendantBloomPipe.bands);
      }
      drawPendantBloom(gl, e.pendantBloomPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "bloom") {
    // Bloom uses a real simulation; we need to initialize it on first
    // use, then step + render every frame.
    if (!engine.bloomPipe && engine.ctx) {
      engine.bloomPipe = createBloomPipeline(engine.ctx.gl);
      if (engine.bloomPipe) {
        initBloom(engine.ctx.gl, engine.bloomPipe);
      }
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.bloomPipe || !e.ctx) return;
      drawBloom(e.ctx.gl, e.bloomPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "mesh") {
    if (!engine.meshPipe && engine.ctx) {
      engine.meshPipe = createMeshPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.meshPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeMeshRealtimeBands(e.beatAudio.freqData, e.meshPipe.bands);
      } else {
        computeMeshBands(t, e.meshPipe.bands);
      }
      drawMesh(gl, e.meshPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "orbits") {
    if (!engine.orbitsPipe && engine.ctx) {
      engine.orbitsPipe = createOrbitsPipeline(engine.ctx.gl);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.orbitsPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      if (e.beatAudio) {
        e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
        computeOrbitsRealtimeBands(e.beatAudio.freqData, e.orbitsPipe.bands);
      } else {
        computeOrbitsBands(t, e.orbitsPipe.bands);
      }
      drawOrbits(gl, e.orbitsPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "cymatics") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.cymaticsPipe ?? (engine.cymaticsPipe = createCymaticsPipeline(engine.ctx!.gl)),
      computeCymaticsBands,
      computeCymaticsRealtimeBands,
      drawCymatics,
    );
  } else if (name === "mandelbrot") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.mandelbrotPipe ?? (engine.mandelbrotPipe = createMandelbrotPipeline(engine.ctx!.gl)),
      computeMandelbrotBands,
      computeMandelbrotRealtimeBands,
      drawMandelbrot,
    );
  } else if (name === "kaleidoscope") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.kaleidoscopePipe ?? (engine.kaleidoscopePipe = createKaleidoscopePipeline(engine.ctx!.gl)),
      computeKaleidoscopeBands,
      computeKaleidoscopeRealtimeBands,
      drawKaleidoscope,
    );
  } else if (name === "lorenz") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.lorenzPipe ?? (engine.lorenzPipe = createLorenzPipeline(engine.ctx!.gl)),
      computeLorenzBands,
      computeLorenzRealtimeBands,
      drawLorenz,
    );
  } else if (name === "phyllotaxis") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.phyllotaxisPipe ?? (engine.phyllotaxisPipe = createPhyllotaxisPipeline(engine.ctx!.gl)),
      computePhyllotaxisBands,
      computePhyllotaxisRealtimeBands,
      drawPhyllotaxis,
    );
  } else if (name === "quasicrystal") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.quasicrystalPipe ?? (engine.quasicrystalPipe = createQuasicrystalPipeline(engine.ctx!.gl)),
      computeQuasicrystalBands,
      computeQuasicrystalRealtimeBands,
      drawQuasicrystal,
    );
  } else if (name === "tunnel") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.tunnelPipe ?? (engine.tunnelPipe = createTunnelPipeline(engine.ctx!.gl)),
      computeTunnelBands,
      computeTunnelRealtimeBands,
      drawTunnel,
    );
  } else if (name === "ferrofluid") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.ferrofluidPipe ?? (engine.ferrofluidPipe = createFerrofluidPipeline(engine.ctx!.gl)),
      computeFerrofluidBands,
      computeFerrofluidRealtimeBands,
      drawFerrofluid,
    );
  } else if (name === "polyhedron") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.polyhedronPipe ?? (engine.polyhedronPipe = createPolyhedronPipeline(engine.ctx!.gl)),
      computePolyhedronBands,
      computePolyhedronRealtimeBands,
      drawPolyhedron,
    );
  } else if (name === "lightning") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.lightningPipe ?? (engine.lightningPipe = createLightningPipeline(engine.ctx!.gl)),
      computeLightningBands,
      computeLightningRealtimeBands,
      drawLightning,
    );
  } else if (name === "caustics") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.causticsPipe ?? (engine.causticsPipe = createCausticsPipeline(engine.ctx!.gl)),
      computeCausticsBands,
      computeCausticsRealtimeBands,
      drawCaustics,
    );
  } else if (name === "worleyTerrain") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.worleyTerrainPipe ?? (engine.worleyTerrainPipe = createWorleyTerrainPipeline(engine.ctx!.gl)),
      computeWorleyTerrainBands,
      computeWorleyTerrainRealtimeBands,
      drawWorleyTerrain,
    );
  } else if (name === "magnetic") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.magneticPipe ?? (engine.magneticPipe = createMagneticPipeline(engine.ctx!.gl)),
      computeMagneticBands,
      computeMagneticRealtimeBands,
      drawMagnetic,
    );
  } else if (name === "lSystem") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.lSystemPipe ?? (engine.lSystemPipe = createLSystemPipeline(engine.ctx!.gl)),
      computeLSystemBands,
      computeLSystemRealtimeBands,
      drawLSystem,
    );
  } else if (name === "truchet") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.truchetPipe ?? (engine.truchetPipe = createTruchetPipeline(engine.ctx!.gl)),
      computeTruchetBands,
      computeTruchetRealtimeBands,
      drawTruchet,
    );
  } else if (name === "sunburst") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.sunburstPipe ?? (engine.sunburstPipe = createSunburstPipeline(engine.ctx!.gl)),
      computeSunburstBands,
      computeSunburstRealtimeBands,
      drawSunburst,
    );
  } else if (name === "spirograph") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.spirographPipe ?? (engine.spirographPipe = createSpirographPipeline(engine.ctx!.gl)),
      computeSpirographBands,
      computeSpirographRealtimeBands,
      drawSpirograph,
    );
  } else if (name === "lissajousSymph") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.lissajousSymphPipe ?? (engine.lissajousSymphPipe = createLissajousSymphPipeline(engine.ctx!.gl)),
      computeLissajousSymphBands,
      computeLissajousSymphRealtimeBands,
      drawLissajousSymph,
    );
  } else if (name === "ribbonTracer") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.ribbonTracerPipe ?? (engine.ribbonTracerPipe = createRibbonTracerPipeline(engine.ctx!.gl)),
      computeRibbonTracerBands,
      computeRibbonTracerRealtimeBands,
      drawRibbonTracer,
    );
  } else if (name === "galacticCore") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.galacticCorePipe ?? (engine.galacticCorePipe = createGalacticCorePipeline(engine.ctx!.gl)),
      computeGalacticCoreBands,
      computeGalacticCoreRealtimeBands,
      drawGalacticCore,
    );
  } else if (name === "pendulumCasc") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.pendulumCascPipe ?? (engine.pendulumCascPipe = createPendulumCascPipeline(engine.ctx!.gl)),
      computePendulumCascBands,
      computePendulumCascRealtimeBands,
      drawPendulumCasc,
    );
  } else if (name === "bifrost") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.bifrostPipe ?? (engine.bifrostPipe = createBifrostPipeline(engine.ctx!.gl)),
      computeBifrostBands,
      computeBifrostRealtimeBands,
      drawBifrost,
    );
  } else if (name === "kaleidoTunnel") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.kaleidoTunnelPipe ?? (engine.kaleidoTunnelPipe = createKaleidoTunnelPipeline(engine.ctx!.gl)),
      computeKaleidoTunnelBands,
      computeKaleidoTunnelRealtimeBands,
      drawKaleidoTunnel,
    );
  } else if (name === "mandala") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.mandalaPipe ?? (engine.mandalaPipe = createMandalaPipeline(engine.ctx!.gl)),
      computeMandalaBands,
      computeMandalaRealtimeBands,
      drawMandala,
    );
  } else if (name === "phantasm") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.phantasmPipe ?? (engine.phantasmPipe = createPhantasmPipeline(engine.ctx!.gl)),
      computePhantasmBands,
      computePhantasmRealtimeBands,
      drawPhantasm,
    );
  } else if (name === "honeycombTide") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.honeycombTidePipe ?? (engine.honeycombTidePipe = createHoneycombTidePipeline(engine.ctx!.gl)),
      computeHoneycombTideBands,
      computeHoneycombTideRealtimeBands,
      drawHoneycombTide,
    );
  } else if (name === "equalCathedral") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.equalCathedralPipe ?? (engine.equalCathedralPipe = createEqualCathedralPipeline(engine.ctx!.gl)),
      computeEqualCathedralBands,
      computeEqualCathedralRealtimeBands,
      drawEqualCathedral,
    );
  } else if (name === "phonograph") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.phonographPipe ?? (engine.phonographPipe = createPhonographPipeline(engine.ctx!.gl)),
      computePhonographBands,
      computePhonographRealtimeBands,
      drawPhonograph,
    );
  } else if (name === "cymaticsPlate") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.cymaticsPlatePipe ?? (engine.cymaticsPlatePipe = createCymaticsPlatePipeline(engine.ctx!.gl)),
      computeCymaticsPlateBands,
      computeCymaticsPlateRealtimeBands,
      drawCymaticsPlate,
    );
  } else if (name === "aurora3D") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.aurora3DPipe ?? (engine.aurora3DPipe = createAurora3DPipeline(engine.ctx!.gl)),
      computeAurora3DBands,
      computeAurora3DRealtimeBands,
      drawAurora3D,
    );
  } else if (name === "soundWave3D") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.soundWave3DPipe ?? (engine.soundWave3DPipe = createSoundWave3DPipeline(engine.ctx!.gl)),
      computeSoundWave3DBands,
      computeSoundWave3DRealtimeBands,
      drawSoundWave3D,
    );
  } else if (name === "cloth") {
    if (!engine.clothPipe && engine.ctx) {
      engine.clothPipe = createClothPipeline(engine.ctx.gl);
      if (engine.clothPipe) initCloth(engine.ctx.gl, engine.clothPipe);
    }
    engine.customDraw = (e: Engine, t: number) => {
      if (!e.clothPipe || !e.ctx) return;
      const gl = e.ctx.gl;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, e.canvas.width, e.canvas.height);
      gl.drawBuffers([gl.BACK]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
      stepCloth(gl, e.clothPipe, e.canvas.width, e.canvas.height, t);
    };
  } else if (name === "teslaCoil") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.teslaCoilPipe ?? (engine.teslaCoilPipe = createTeslaCoilPipeline(engine.ctx!.gl)),
      computeTeslaCoilBands,
      computeTeslaCoilRealtimeBands,
      drawTeslaCoil,
    );
  } else if (name === "plasmaGlobe") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.plasmaGlobePipe ?? (engine.plasmaGlobePipe = createPlasmaGlobePipeline(engine.ctx!.gl)),
      computePlasmaGlobeBands,
      computePlasmaGlobeRealtimeBands,
      drawPlasmaGlobe,
    );
  } else if (name === "solarFlare") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.solarFlarePipe ?? (engine.solarFlarePipe = createSolarFlarePipeline(engine.ctx!.gl)),
      computeSolarFlareBands,
      computeSolarFlareRealtimeBands,
      drawSolarFlare,
    );
  } else if (name === "kleinFilm") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.kleinFilmPipe ?? (engine.kleinFilmPipe = createKleinFilmPipeline(engine.ctx!.gl)),
      computeKleinFilmBands,
      computeKleinFilmRealtimeBands,
      drawKleinFilm,
    );
  } else if (name === "starForge") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.starForgePipe ?? (engine.starForgePipe = createStarForgePipeline(engine.ctx!.gl)),
      computeStarForgeBands,
      computeStarForgeRealtimeBands,
      drawStarForge,
    );
  } else if (name === "voronoiBubbles") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.voronoiBubblesPipe ?? (engine.voronoiBubblesPipe = createVoronoiBubblesPipeline(engine.ctx!.gl)),
      computeVoronoiBubblesBands,
      computeVoronoiBubblesRealtimeBands,
      drawVoronoiBubbles,
    );
  } else if (name === "windstream") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.windstreamPipe ?? (engine.windstreamPipe = createWindstreamPipeline(engine.ctx!.gl)),
      computeWindstreamBands,
      computeWindstreamRealtimeBands,
      drawWindstream,
    );
  } else if (name === "siliconSkin") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.siliconSkinPipe ?? (engine.siliconSkinPipe = createSiliconSkinPipeline(engine.ctx!.gl)),
      computeSiliconSkinBands,
      computeSiliconSkinRealtimeBands,
      drawSiliconSkin,
    );
  } else if (name === "viscoelastic") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.viscoelasticPipe ?? (engine.viscoelasticPipe = createViscoelasticPipeline(engine.ctx!.gl)),
      computeViscoelasticBands,
      computeViscoelasticRealtimeBands,
      drawViscoelastic,
    );
  } else if (name === "balloonPress") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.balloonPressPipe ?? (engine.balloonPressPipe = createBalloonPressPipeline(engine.ctx!.gl)),
      computeBalloonPressBands,
      computeBalloonPressRealtimeBands,
      drawBalloonPress,
    );
  } else if (name === "membraneDrum") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.membraneDrumPipe ?? (engine.membraneDrumPipe = createMembraneDrumPipeline(engine.ctx!.gl)),
      computeMembraneDrumBands,
      computeMembraneDrumRealtimeBands,
      drawMembraneDrum,
    );
  } else if (name === "punchBag") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.punchBagPipe ?? (engine.punchBagPipe = createPunchBagPipeline(engine.ctx!.gl)),
      computePunchBagBands,
      computePunchBagRealtimeBands,
      drawPunchBag,
    );
  } else if (name === "siliconeFace") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.siliconeFacePipe ?? (engine.siliconeFacePipe = createSiliconeFacePipeline(engine.ctx!.gl)),
      computeSiliconeFaceBands,
      computeSiliconeFaceRealtimeBands,
      drawSiliconeFace,
    );
  } else if (name === "liquidSkin") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.liquidSkinPipe ?? (engine.liquidSkinPipe = createLiquidSkinPipeline(engine.ctx!.gl)),
      computeLiquidSkinBands,
      computeLiquidSkinRealtimeBands,
      drawLiquidSkin,
    );
  } else if (name === "clayPress") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.clayPressPipe ?? (engine.clayPressPipe = createClayPressPipeline(engine.ctx!.gl)),
      computeClayPressBands,
      computeClayPressRealtimeBands,
      drawClayPress,
    );
  } else if (name === "drumHead3D") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.drumHead3DPipe ?? (engine.drumHead3DPipe = createDrumHead3DPipeline(engine.ctx!.gl)),
      computeDrumHead3DBands,
      computeDrumHead3DRealtimeBands,
      drawDrumHead3D,
    );
  } else if (name === "siliconeVeil") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.siliconeVeilPipe ?? (engine.siliconeVeilPipe = createSiliconeVeilPipeline(engine.ctx!.gl)),
      computeSiliconeVeilBands,
      computeSiliconeVeilRealtimeBands,
      drawSiliconeVeil,
    );
  } else if (name === "liveVideo") {
    // NSPP-native: the scene graph itself has the video image node with
    // a per-frame imageUrl mutation. No customDraw needed — the standard
    // 2D pipeline (instanced quad, mix-blend-mode, filters, motion blur)
    // renders everything. Just clear any leftover customDraw.
    engine.customDraw = undefined;
    } else if (name === "myceliumDom") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.myceliumDomPipe ?? (engine.myceliumDomPipe = createMyceliumDomPipeline(engine.ctx!.gl)),
      computeMyceliumDomBands,
      computeMyceliumDomRealtimeBands,
      drawMyceliumDom,
    );
  } else if (name === "dislocationField") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.dislocationFieldPipe ?? (engine.dislocationFieldPipe = createDislocationFieldPipeline(engine.ctx!.gl)),
      computeDislocationFieldBands,
      computeDislocationFieldRealtimeBands,
      drawDislocationField,
    );
  } else if (name === "dislocationFieldLogos") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.dislocationFieldLogosPipe ?? (engine.dislocationFieldLogosPipe = createDislocationFieldLogosPipeline(engine.ctx!.gl)),
      computeDislocationFieldLogosBands,
      computeDislocationFieldLogosRealtimeBands,
      drawDislocationFieldLogos,
    );
  } else if (name === "marangoniInk") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.marangoniInkPipe ?? (engine.marangoniInkPipe = createMarangoniInkPipeline(engine.ctx!.gl)),
      computeMarangoniInkBands,
      computeMarangoniInkRealtimeBands,
      drawMarangoniInk,
    );
  } else if (name === "mcCollough") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.mcColloughPipe ?? (engine.mcColloughPipe = createMcColloughPipeline(engine.ctx!.gl)),
      computeMcColloughBands,
      computeMcColloughRealtimeBands,
      drawMcCollough,
    );
  } else if (name === "hopfFibration") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.hopfFibrationPipe ?? (engine.hopfFibrationPipe = createHopfFibrationPipeline(engine.ctx!.gl)),
      computeHopfFibrationBands,
      computeHopfFibrationRealtimeBands,
      drawHopfFibration,
    );
  } else if (name === "slitScanChrono") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.slitScanChronoPipe ?? (engine.slitScanChronoPipe = createSlitScanChronoPipeline(engine.ctx!.gl)),
      computeSlitScanChronoBands,
      computeSlitScanChronoRealtimeBands,
      drawSlitScanChrono,
    );
  } else if (name === "glass") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.glassPipe ?? (engine.glassPipe = createGlassPipeline(engine.ctx!.gl)),
      computeGlassBands,
      computeGlassRealtimeBands,
      drawGlass,
    );
  } else if (name === "dna") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.dnaPipe ?? (engine.dnaPipe = createDNAPipeline(engine.ctx!.gl)),
      computeDNABands,
      computeDNARealtimeBands,
      drawDNA,
    );
  } else if (name === "lavaLamp") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.lavaLampPipe ?? (engine.lavaLampPipe = createLavaLampPipeline(engine.ctx!.gl)),
      computeLavaLampBands,
      computeLavaLampRealtimeBands,
      drawLavaLamp,
    );
  } else if (name === "bezier") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.bezierPipe ?? (engine.bezierPipe = createBezierPipeline(engine.ctx!.gl)),
      computeBezierBands,
      computeBezierRealtimeBands,
      drawBezier,
    );
  } else if (name === "stereographic") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.stereographicPipe ?? (engine.stereographicPipe = createStereographicPipeline(engine.ctx!.gl)),
      computeStereographicBands,
      computeStereographicRealtimeBands,
      drawStereographic,
    );
  } else if (name === "contour") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.contourPipe ?? (engine.contourPipe = createContourPipeline(engine.ctx!.gl)),
      computeContourBands,
      computeContourRealtimeBands,
      drawContour,
    );
  } else if (name === "cosmos") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.cosmosPipe ?? (engine.cosmosPipe = createCosmosPipeline(engine.ctx!.gl)),
      computeCosmosBands,
      computeCosmosRealtimeBands,
      drawCosmos,
    );
  } else if (name === "galaxy") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.galaxyPipe ?? (engine.galaxyPipe = createGalaxyPipeline(engine.ctx!.gl)),
      computeGalaxyBands,
      computeGalaxyRealtimeBands,
      drawGalaxy,
    );
  } else if (name === "oceanStorm") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.oceanStormPipe ?? (engine.oceanStormPipe = createOceanStormPipeline(engine.ctx!.gl)),
      computeOceanStormBands,
      computeOceanStormRealtimeBands,
      drawOceanStorm,
    );
  } else if (name === "cyberpunk") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.cyberpunkPipe ?? (engine.cyberpunkPipe = createCyberpunkPipeline(engine.ctx!.gl)),
      computeCyberpunkBands,
      computeCyberpunkRealtimeBands,
      drawCyberpunk,
    );
  } else if (name === "crystalCave") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.crystalCavePipe ?? (engine.crystalCavePipe = createCrystalCavePipeline(engine.ctx!.gl)),
      computeCrystalCaveBands,
      computeCrystalCaveRealtimeBands,
      drawCrystalCave,
    );
  } else if (name === "soapBubble") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.soapBubblePipe ?? (engine.soapBubblePipe = createSoapBubblePipeline(engine.ctx!.gl)),
      computeSoapBubbleBands,
      computeSoapBubbleRealtimeBands,
      drawSoapBubble,
    );
  } else if (name === "voronoiGlass") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.voronoiGlassPipe ?? (engine.voronoiGlassPipe = createVoronoiGlassPipeline(engine.ctx!.gl)),
      computeVoronoiGlassBands,
      computeVoronoiGlassRealtimeBands,
      drawVoronoiGlass,
    );
  } else if (name === "pixelSort") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.pixelSortPipe ?? (engine.pixelSortPipe = createPixelSortPipeline(engine.ctx!.gl)),
      computePixelSortBands,
      computePixelSortRealtimeBands,
      drawPixelSort,
    );
  } else if (name === "wireframeSphere") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.wireframeSpherePipe ?? (engine.wireframeSpherePipe = createWireframeSpherePipeline(engine.ctx!.gl)),
      computeWireframeSphereBands,
      computeWireframeSphereRealtimeBands,
      drawWireframeSphere,
    );
  } else if (name === "blackHole") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.blackHolePipe ?? (engine.blackHolePipe = createBlackHolePipeline(engine.ctx!.gl)),
      computeBlackHoleBands,
      computeBlackHoleRealtimeBands,
      drawBlackHole,
    );
  } else if (name === "liquidMetal") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.liquidMetalPipe ?? (engine.liquidMetalPipe = createLiquidMetalPipeline(engine.ctx!.gl)),
      computeLiquidMetalBands,
      computeLiquidMetalRealtimeBands,
      drawLiquidMetal,
    );
  } else if (name === "stainedGlass") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.stainedGlassPipe ?? (engine.stainedGlassPipe = createStainedGlassPipeline(engine.ctx!.gl)),
      computeStainedGlassBands,
      computeStainedGlassRealtimeBands,
      drawStainedGlass,
    );
  } else if (name === "marbleStatue") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.marbleStatuePipe ?? (engine.marbleStatuePipe = createMarbleStatuePipeline(engine.ctx!.gl)),
      computeMarbleStatueBands,
      computeMarbleStatueRealtimeBands,
      drawMarbleStatue,
    );
  } else if (name === "psychedelic") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.psychedelicPipe ?? (engine.psychedelicPipe = createPsychedelicPipeline(engine.ctx!.gl)),
      computePsychedelicBands,
      computePsychedelicRealtimeBands,
      drawPsychedelic,
    );
  } else if (name === "glitch") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.glitchPipe ?? (engine.glitchPipe = createGlitchPipeline(engine.ctx!.gl)),
      computeGlitchBands,
      computeGlitchRealtimeBands,
      drawGlitch,
    );
  } else if (name === "quantumField") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.quantumFieldPipe ?? (engine.quantumFieldPipe = createQuantumFieldPipeline(engine.ctx!.gl)),
      computeQuantumFieldBands,
      computeQuantumFieldRealtimeBands,
      drawQuantumField,
    );
  } else if (name === "particleConstellation") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.particleConstellationPipe ?? (engine.particleConstellationPipe = createParticleConstellationPipeline(engine.ctx!.gl)),
      computeParticleConstellationBands,
      computeParticleConstellationRealtimeBands,
      drawParticleConstellation,
    );
  } else if (name === "inkInWater") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.inkInWaterPipe ?? (engine.inkInWaterPipe = createInkInWaterPipeline(engine.ctx!.gl)),
      computeInkInWaterBands,
      computeInkInWaterRealtimeBands,
      drawInkInWater,
    );
  } else if (name === "wormholeTunnel") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.wormholeTunnelPipe ?? (engine.wormholeTunnelPipe = createWormholeTunnelPipeline(engine.ctx!.gl)),
      computeWormholeTunnelBands,
      computeWormholeTunnelRealtimeBands,
      drawWormholeTunnel,
    );
  } else if (name === "ascii") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.asciiPipe ?? (engine.asciiPipe = createAsciiPipeline(engine.ctx!.gl)),
      computeAsciiBands,
      computeAsciiRealtimeBands,
      drawAscii,
    );
  } else if (name === "inkDrop") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.inkDropPipe ?? (engine.inkDropPipe = createInkDropPipeline(engine.ctx!.gl)),
      computeInkDropBands,
      computeInkDropRealtimeBands,
      drawInkDrop,
    );
  } else if (name === "gyroid") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.gyroidPipe ?? (engine.gyroidPipe = createGyroidPipeline(engine.ctx!.gl)),
      computeGyroidBands,
      computeGyroidRealtimeBands,
      drawGyroid,
    );
  } else if (name === "schwarzP") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.schwarzPPipe ?? (engine.schwarzPPipe = createSchwarzPPipeline(engine.ctx!.gl)),
      computeSchwarzPBands,
      computeSchwarzPRealtimeBands,
      drawSchwarzP,
    );
  } else if (name === "mengerSponge") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.mengerSpongePipe ?? (engine.mengerSpongePipe = createMengerSpongePipeline(engine.ctx!.gl)),
      computeMengerSpongeBands,
      computeMengerSpongeRealtimeBands,
      drawMengerSponge,
    );
  } else if (name === "kleinBottle") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.kleinBottlePipe ?? (engine.kleinBottlePipe = createKleinBottlePipeline(engine.ctx!.gl)),
      computeKleinBottleBands,
      computeKleinBottleRealtimeBands,
      drawKleinBottle,
    );
  } else if (name === "conwayLife") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.conwayLifePipe ?? (engine.conwayLifePipe = createConwayLifePipeline(engine.ctx!.gl)),
      computeConwayLifeBands,
      computeConwayLifeRealtimeBands,
      drawConwayLife,
    );
  } else if (name === "stableFluid") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.stableFluidPipe ?? (engine.stableFluidPipe = createStableFluidPipeline(engine.ctx!.gl)),
      computeStableFluidBands,
      computeStableFluidRealtimeBands,
      drawStableFluid,
    );
  } else if (name === "doublePendulum") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.doublePendulumPipe ?? (engine.doublePendulumPipe = createDoublePendulumPipeline(engine.ctx!.gl)),
      computeDoublePendulumBands,
      computeDoublePendulumRealtimeBands,
      drawDoublePendulum,
    );
  } else if (name === "sphericalHarmonics") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.sphericalHarmonicsPipe ?? (engine.sphericalHarmonicsPipe = createSphericalHarmonicsPipeline(engine.ctx!.gl)),
      computeSphericalHarmonicsBands,
      computeSphericalHarmonicsRealtimeBands,
      drawSphericalHarmonics,
    );
  } else if (name === "bec") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.becPipe ?? (engine.becPipe = createBECPipeline(engine.ctx!.gl)),
      computeBECBands,
      computeBECRealtimeBands,
      drawBEC,
    );
  } else if (name === "beatNebula") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.beatNebulaPipe ?? (engine.beatNebulaPipe = createBeatNebulaPipeline(engine.ctx!.gl)),
      computeSyntheticBands,
      computeRealtimeBands,
      drawBeatNebula,
    );
  } else if (name === "phononLattice") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.phononLatticePipe ?? (engine.phononLatticePipe = createPhononLatticePipeline(engine.ctx!.gl)),
      computePhononLatticeBands,
      computePhononLatticeRealtimeBands,
      drawPhononLattice,
    );
  } else if (name === "magneticPendulum") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.magneticPendulumPipe ?? (engine.magneticPendulumPipe = createMagneticPendulumPipeline(engine.ctx!.gl)),
      computeMagneticPendulumBands,
      computeMagneticPendulumRealtimeBands,
      drawMagneticPendulum,
    );
  } else if (name === "schwarzD") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.schwarzDPipe ?? (engine.schwarzDPipe = createSchwarzDPipeline(engine.ctx!.gl)),
      computeSchwarzDBands,
      computeSchwarzDRealtimeBands,
      drawSchwarzD,
    );
  } else if (name === "liquidCrystal") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.liquidCrystalPipe ?? (engine.liquidCrystalPipe = createLiquidCrystalPipeline(engine.ctx!.gl)),
      computeLiquidCrystalBands,
      computeLiquidCrystalRealtimeBands,
      drawLiquidCrystal,
    );
  } else if (name === "sincWaveField") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.sincWaveFieldPipe ?? (engine.sincWaveFieldPipe = createSincWaveFieldPipeline(engine.ctx!.gl)),
      computeSincWaveFieldBands,
      computeSincWaveFieldRealtimeBands,
      drawSincWaveField,
    );
  } else if (name === "sineGordon") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.sineGordonPipe ?? (engine.sineGordonPipe = createSineGordonPipeline(engine.ctx!.gl)),
      computeSineGordonBands,
      computeSineGordonRealtimeBands,
      drawSineGordon,
    );
  } else if (name === "skyrmion") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.skyrmionPipe ?? (engine.skyrmionPipe = createSkyrmionPipeline(engine.ctx!.gl)),
      computeSkyrmionBands,
      computeSkyrmionRealtimeBands,
      drawSkyrmion,
    );
  } else if (name === "hessianEigenvector") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.hessianEigenvectorPipe ?? (engine.hessianEigenvectorPipe = createHessianEigenvectorPipeline(engine.ctx!.gl)),
      computeHessianEigenvectorBands,
      computeHessianEigenvectorRealtimeBands,
      drawHessianEigenvector,
    );
  } else if (name === "braggDiffraction") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.braggDiffractionPipe ?? (engine.braggDiffractionPipe = createBraggDiffractionPipeline(engine.ctx!.gl)),
      computeBraggDiffractionBands,
      computeBraggDiffractionRealtimeBands,
      drawBraggDiffraction,
    );
  } else if (name === "curvatureFlow") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.curvatureFlowPipe ?? (engine.curvatureFlowPipe = createCurvatureFlowPipeline(engine.ctx!.gl)),
      computeCurvatureFlowBands,
      computeCurvatureFlowRealtimeBands,
      drawCurvatureFlow,
    );
  } else if (name === "cellularPotts") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.cellularPottsPipe ?? (engine.cellularPottsPipe = createCellularPottsPipeline(engine.ctx!.gl)),
      computeCellularPottsBands,
      computeCellularPottsRealtimeBands,
      drawCellularPotts,
    );
  } else if (name === "waveFunctionCollapse") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.waveFunctionCollapsePipe ?? (engine.waveFunctionCollapsePipe = createWaveFunctionCollapsePipeline(engine.ctx!.gl)),
      computeWaveFunctionCollapseBands,
      computeWaveFunctionCollapseRealtimeBands,
      drawWaveFunctionCollapse,
    );
  } else if (name === "phaseFieldCrystal") {
    engine.customDraw = makeAudioReactiveDraw(
      engine,
      () => engine.phaseFieldCrystalPipe ?? (engine.phaseFieldCrystalPipe = createPhaseFieldCrystalPipeline(engine.ctx!.gl)),
      computePhaseFieldCrystalBands,
      computePhaseFieldCrystalRealtimeBands,
      drawPhaseFieldCrystal,
    );
  } else {
    engine.customDraw = undefined;
  }
  // Show the mic toggle only when on the beat scene. (Toggle is created
  // at init in attachBeatMicToggle(); we just toggle its visibility.)
  showBeatMicToggle(engine, name === "beat");
}

/** Showpiece scene registry. Layer-spanning demos. */
const ADVANCED_SCENES: Record<string, (w: number, h: number) => Scene> = {
  landing: sceneLanding,
  advanced: sceneAdvanced,
  intro: sceneIntro,
  chase: chaseDemoRoute.scene as (w: number, h: number) => Scene,
  "paint-visualizer": paintDemoRoute.scene as (w: number, h: number) => Scene,
  "womens-fashion": sceneWomensFashion,
  gallery: sceneGallery,
  "maison-verre": sceneMaisonVerre,
  "sdf-library": sdfLibraryScene,
  constellation: sceneConstellation,
  attic: sceneAtticEase,
  chain: sceneChain,
  morph: sceneMorph,
  cine: sceneCine,
  beat: sceneBeat,
  hex: sceneHex,
  aurora: sceneAurora,
  liss:sceneLissajousSymph, ribn:sceneRibbonTracer, gcore:sceneGalacticCore, pdcl:scenePendulumCasc, bfrst:sceneBifrost, ktnl:sceneKaleidoTunnel,
  lissajousSymph: sceneLissajousSymph,
  ribbonTracer: sceneRibbonTracer,
  galacticCore: sceneGalacticCore,
  pendulumCasc: scenePendulumCasc,
  bifrost: sceneBifrost,
  kaleidoTunnel: sceneKaleidoTunnel,
  mandala: sceneMandala,
  phantasm: scenePhantasm,
  honeycombTide: sceneHoneycombTide,
  equalCathedral: sceneEqualCathedral,
  phonograph: scenePhonograph,
  cymaticsPlate: sceneCymaticsPlate,
  aurora3D: sceneAurora3D,
  soundWave3D: sceneSoundWave3D,
  cloth: sceneCloth,
  teslaCoil: sceneTeslaCoil,
  plasmaGlobe: scenePlasmaGlobe,
  solarFlare: sceneSolarFlare,
  kleinFilm: sceneKleinFilm,
  starForge: sceneStarForge,
  voronoiBubbles: sceneVoronoiBubbles,
  windstream: sceneWindstream,
  siliconSkin: sceneSiliconSkin,
  viscoelastic: sceneViscoelastic,
  balloonPress: sceneBalloonPress,
  membraneDrum: sceneMembraneDrum,
  punchBag: scenePunchBag,
  siliconeFace: sceneSiliconeFace,
  liquidSkin: sceneLiquidSkin,
  clayPress: sceneClayPress,
  drumHead3D: sceneDrumHead3D,
  siliconeVeil: sceneSiliconeVeil,
  liveVideo: sceneLiveVideoLocal,
  myceliumDom: sceneMyceliumDom,
  dislocationField: sceneDislocationField,
  dislocationFieldLogos: sceneDislocationFieldLogos,
  marangoniInk: sceneMarangoniInk,
  mcCollough: sceneMcCollough,
  hopfFibration: sceneHopfFibration,
  slitScanChrono: sceneSlitScanChrono,
  glass: sceneGlass,
  vortex: sceneVortex,
  supernova: sceneSupernova,
  plasma: scenePlasma,
  drift: sceneDrift,
  latticePulse: sceneLatticePulse,
  chronoStrand: sceneChronoStrand,
  riverPaths: sceneRiverPaths,
  embroidery: sceneEmbroidery,
  kaleidoscopeTouch: sceneKaleidoscopeTouch,
  cracklePattern: sceneCracklePattern,
  vineGrowth: sceneVineGrowth,
  paperFold: scenePaperFold,
  lichenGrowth: sceneLichenGrowth,
  waveInterference: sceneWaveInterference,
  stainedGlassTouch: sceneStainedGlassTouch,
  webSpinning: sceneWebSpinning,
  plasmaTouch: scenePlasmaTouch,
  rustGrowth: sceneRustGrowth,
  bubbleChamber: sceneBubbleChamber,
  tidePool: sceneTidePool,
  sugarCrystal: sceneSugarCrystal,
  windBlownGrass: sceneWindBlownGrass,
  auroraTouch: sceneAuroraTouch,
  sandPile: sceneSandPile,
  bubbleBlow: sceneBubbleBlow,
  smokeRing: sceneSmokeRing,
  crystalGrowth: sceneCrystalGrowth,
  lightningWeb: sceneLightningWeb,
  crackedGlass: sceneCrackedGlass,
  petrifiedBloom: scenePetrifiedBloom,
  magneticFilings: sceneMagneticFilings,
  halo: sceneHalo,
  lissajousTube: sceneLissajousTube,
  snowflake: sceneSnowflake,
  negativeSpace: sceneNegativeSpace,
  trichromatic: sceneTrichromatic,
  solarCorona: sceneSolarCorona,
  dendriticCrystal: sceneDendriticCrystal,
  tyndallBeam: sceneTyndallBeam,
  baroclinicVortex: sceneBaroclinicVortex,
  dislocationWeb: sceneDislocationWeb,
  chladniCells: sceneChladniCells,
  crookesRadiometer: sceneCrookesRadiometer,
  granularFlow: sceneGranularFlow,
  savartWheel: sceneSavartWheel,
  rijkeTube: sceneRijkeTube,
  ferrofluidSculpture: sceneFerrofluidSculpture,
  sigils: sceneSigils,
  magneticMonopole: sceneUmagneticMonopole,
  timeCrystal: sceneUtimeCrystal,
  penroseStairs: sceneUpenroseStairs,
  quantumFoam: sceneUquantumFoam,
  fractalSmoke: sceneUfractalSmoke,
  anyonicBraiding: sceneAnyonicBraiding,
  hydrodynamicShock: sceneHydrodynamicShock,
  schwarzschildCrossing: sceneSchwarzschildCrossing,
  dynamo: sceneDynamo,
  phaseConjugate: scenePhaseConjugate,
  spectralDecomposition: sceneSpectralDecomposition,
  anamorphic: sceneAnamorphic,
  feynmanPath: sceneFeynmanPath,
  photonicCrystal: scenePhotonicCrystal,
  tokamakPlasma: sceneTokamakPlasma,
  mobiusLattice: sceneMobiusLattice,
  tesseractShadow: sceneTesseractShadow,
  twinflame: sceneTwinflame,
  mulberry: sceneMulberry,
  bifrostMirror: sceneBifrostMirror,
  stalactitePulse: sceneStalactitePulse,
  catenaryLoom: sceneCatenaryLoom,
  lightwing: sceneLightwing,
  cataract: sceneCataract,
  aspic: sceneAspic,
  moirefall: sceneMoirefall,
  tideglass: sceneTideglass,
  sunTongue: sceneSunTongue,
  tessellationMelt: sceneTessellationMelt,
  tessellationMeltTouch: sceneTessellationMeltTouch,
  magneticMonopoleTouch: sceneMagneticMonopoleTouch,
  fractalSmokeTouch: sceneFractalSmokeTouch,
  tesseractShadowTouch: sceneTesseractShadowTouch,
  resonance: sceneResonance,
  fontainefeed: sceneFontainefeed,
  pendantBloom: scenePendantBloom,
  bloom: sceneBloom,
  mesh: sceneMesh,
  orbits: sceneOrbits,
  cymatics: sceneCymatics,
  mandelbrot: sceneMandelbrot,
  kaleidoscope: sceneKaleidoscope,
  lorenz: sceneLorenz,
  phyllotaxis: scenePhyllotaxis,
  quasicrystal: sceneQuasicrystal,
  tunnel: sceneTunnel,
  ferrofluid: sceneFerrofluid,
  polyhedron: scenePolyhedron,
  lightning: sceneLightning,
  caustics: sceneCaustics,
  worleyTerrain: sceneWorleyTerrain,
  magnetic: sceneMagnetic,
  lSystem: sceneLSystem,
  truchet: sceneTruchet,
  sunburst: sceneSunburst,
  spirograph: sceneSpirograph,
  dna: sceneDNA,
  lavaLamp: sceneLavaLamp,
  bezier: sceneBezier,
  stereographic: sceneStereographic,
  contour: sceneContour,
  cosmos: sceneCosmos,
  galaxy: sceneGalaxy,
  oceanStorm: sceneOceanStorm,
  cyberpunk: sceneCyberpunk,
  crystalCave: sceneCrystalCave,
  soapBubble: sceneSoapBubble,
  voronoiGlass: sceneVoronoiGlass,
  pixelSort: scenePixelSort,
  wireframeSphere: sceneWireframeSphere,
  blackHole: sceneBlackHole,
  liquidMetal: sceneLiquidMetal,
  stainedGlass: sceneStainedGlass,
  marbleStatue: sceneMarbleStatue,
  psychedelic: scenePsychedelic,
  glitch: sceneGlitch,
  quantumField: sceneQuantumField,
  particleConstellation: sceneParticleConstellation,
  inkInWater: sceneInkInWater,
  wormholeTunnel: sceneWormholeTunnel,
  ascii: sceneAscii,
  inkDrop: sceneInkDrop,
  gyroid: sceneGyroid,
  schwarzP: sceneSchwarzP,
  mengerSponge: sceneMengerSponge,
  kleinBottle: sceneKleinBottle,
  conwayLife: sceneConwayLife,
  stableFluid: sceneStableFluid,
  doublePendulum: sceneDoublePendulum,
  sphericalHarmonics: sceneSphericalHarmonics,
  bec: sceneBEC,
  beatNebula: sceneBeatNebula,
  phononLattice: scenePhononLattice,
  magneticPendulum: sceneMagneticPendulum,
  schwarzD: sceneSchwarzD,
  liquidCrystal: sceneLiquidCrystal,
  sincWaveField: sceneSincWaveField,
  sineGordon: sceneSineGordon,
  skyrmion: sceneSkyrmion,
  hessianEigenvector: sceneHessianEigenvector,
  braggDiffraction: sceneBraggDiffraction,
  curvatureFlow: sceneCurvatureFlow,
  cellularPotts: sceneCellularPotts,
  waveFunctionCollapse: sceneWaveFunctionCollapse,
  phaseFieldCrystal: scenePhaseFieldCrystal,
  lab: sceneLab,
  coral: sceneCoral,
  liquidMetalVol: sceneLiquidMetalVol,
  metaballVol: sceneMetaballVol,
  neuralNet: sceneNeuralNet,
  smokeColumns: sceneSmokeColumns,
  vines: sceneVines,
  voronoiCrystal: sceneVoronoiCrystal,
};

export function frame(engine: Engine): void {
  if (!engine.ctx || !engine.rectPipe || !engine.postPipe) return;
  const gl = engine.ctx.gl;
  const { t, dt } = tickTiming(engine.timing);

  // 1. Snapshot prev BEFORE updates (for velocity)
  const nodes = Array.from(engine.scene.nodes.values());
  snapshotPrev(nodes);

  // 2a. Drive scroll-tracked animations from window.scrollY. Sets
  // elapsedInIteration on every scrollTrack animation in the scene; the
  // dt-accumulator in tickAnimation is skipped for those (see animation.ts).
  tickScroll(engine.scene, t, dt);

  // 2a.5. Feed pointer state to scenes that opt in via __labPointer.
  // This avoids the engine→scene circular import for the Lab scene's
  // chase/paint/orbit modes.
  const labPointer = (engine.scene as unknown as {
    __labPointer?: (x: number, y: number, down: boolean) => void;
  }).__labPointer;
  if (labPointer) {
    labPointer(
      engine.pointer.x,
      engine.pointer.y,
      engine.pointer.active && engine.pointer.dragging == null,
    );
  }

  // 2a.6. Per-frame scene tick hook — for scenes that need gl access
  // beyond what their per-node update() closures capture (e.g. VIDEO WALL
  // re-uploads a live canvas to a texture every frame).
  const labTick = (engine.scene as unknown as {
    __labTick?: (gl: WebGL2RenderingContext, t: number, dt: number) => void;
  }).__labTick;
  if (labTick) {
    labTick(gl, t, dt);
  }

  // 2a.7. Capture-mode hook — returns the canvas's toDataURL() when the
  //       scene's capture side-door fires. Used by the lab's Capture mode
  //       to actually persist the frame as a PNG download.
  const labCapture = (engine.scene as unknown as {
    __labCapture?: (n: number) => void;
  }).__labCapture;
  if (labCapture && engine.sceneName === "lab") {
    // Re-publish each frame so the closure always has the latest fn —
    // the scene prefers this over static binding because we want fresh
    // canvas elements when the scene swaps mid-recording.
    (globalThis as unknown as {
      __nsppCapture?: (n: number) => void;
      __nsppCanvasEl?: HTMLCanvasElement;
    }).__nsppCapture = labCapture;
    // Expose the live canvas element so the capture hook can call
    // toDataURL on it. CORS-tainted canvas would throw a SecurityError
    // on toDataURL — the lab handles that gracefully.
    (globalThis as unknown as { __nsppCanvasEl?: HTMLCanvasElement }).__nsppCanvasEl = engine.canvas;
  }

  // 2. Tick CSS keyframe animations.
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (!n) continue;
    if (n.animation && !n.animation.paused) {
      tickAnimation(n, n.animation, dt);
    }
  }

  // 3. Run per-node updates (skip dragged node — its position is set by pointer)
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (!n) continue;
    if (n.update && engine.pointer.dragging !== n.id) {
      n.update(n, t, dt);
    }
  }

  // 4. Apply inertia — physics-style fling after pointer release.
  if (engine.inertia && engine.inertia.nodeId != null) {
    const n = engine.scene.nodes.get(engine.inertia.nodeId);
    if (n) {
      const vw = engine.canvas.clientWidth;
      const vh = engine.canvas.clientHeight;
      const step = stepInertia(n, engine.inertia.vx, engine.inertia.vy, dt, vw, vh);
      n.x = step.x;
      n.y = step.y;
      engine.inertia.vx = step.vx;
      engine.inertia.vy = step.vy;
      if (step.done) {
        engine.inertia = null;
        engine.pointer.inertia = false;
      }
    } else {
      engine.inertia = null;
    }
  }

  // 5. Track long frames for dynamic DPR scaling.
  if (dt > 0.022) {
    engine.longFrameCount++;
  } else if (dt < 0.014) {
    engine.longFrameCount = Math.max(0, engine.longFrameCount - 1);
  }
  if (
    engine.longFrameCount > 30 &&
    engine.ctx.renderScale > 0.5 &&
    performance.now() - engine.lastScaleAdjust > 2000
  ) {
    engine.ctx.renderScale = Math.max(0.5, engine.ctx.renderScale - 0.1);
    resizeCanvas(engine.canvas, gl, engine.ctx.dpr * engine.ctx.renderScale);
    engine.sceneFbo = null;
    engine.scratchFbo = null;
    engine.backdropFbo = null;
    engine.velocityFbo = null;
    engine.longFrameCount = 0;
    engine.lastScaleAdjust = performance.now();
  } else if (
    engine.longFrameCount === 0 &&
    engine.ctx.renderScale < engine.ctx.targetRenderScale &&
    performance.now() - engine.lastScaleAdjust > 5000 &&
    engine.timing.fps > 58
  ) {
    engine.ctx.renderScale = Math.min(engine.ctx.targetRenderScale, engine.ctx.renderScale + 0.05);
    resizeCanvas(engine.canvas, gl, engine.ctx.dpr * engine.ctx.renderScale);
    engine.sceneFbo = null;
    engine.scratchFbo = null;
    engine.backdropFbo = null;
    engine.velocityFbo = null;
    engine.longFrameCount = 0;
    engine.lastScaleAdjust = performance.now();
  }

  // 6. Compile
  const t0 = performance.now();
  engine.compiled = compile(engine.scene);
  engine.compileMs = performance.now() - t0;

  if (typeof window !== "undefined" && (window as { __nsppDebug?: boolean }).__nsppDebug) {
    const dump: Array<{ id: number; type: string; x: number; y: number; w: number; h: number; fill: number[] }> = [];
    for (const [id, n] of engine.scene.nodes) {
      dump.push({ id, type: n.type, x: +n.x.toFixed(1), y: +n.y.toFixed(1), w: +n.w.toFixed(1), h: +n.h.toFixed(1), fill: [n.fill.r, n.fill.g, n.fill.b, n.fill.a] });
    }
    console.log("[NSPP SCENE] " + JSON.stringify(dump, null, 2));
  }

  // 7a. Scene color pass — TWO sub-passes to break the WebGL feedback loop
  // when backdrop-filter is in use.
  //
  //   Pass A (scene base): draw all NON-backdrop nodes into sceneFbo with
  //     NO_BACKDROP. The framebuffer here contains the unmodified scene that
  //     backdrop nodes will sample.
  //   Pass B (backdrop):   draw backdrop-filter nodes into backdropFbo, sampling
  //     sceneFbo.tex as the blurred-scene source. backdropFbo is a DIFFERENT
  //     FBO from the one being read, so the read/write is well-defined per
  //     WebGL spec (no feedback loop stall).
  //   Pass C (composite):  blit backdropFbo on top of sceneFbo using
  //     premultiplied "over" blending via drawBackdropComposite.
  //
  // Mix-blend-mode (Layer 6) is orthogonal — handled inside Pass A's draw
  // callback as before.
  rebuildSceneFBO(engine);
  if (engine.sceneFbo && engine.rectPipe && engine.compiled) {
    const stride = FLOATS_PER_INSTANCE;
    const canvasCss: [number, number] = [engine.canvas.clientWidth, engine.canvas.clientHeight];
    const dprScale = engine.ctx!.dpr * engine.ctx!.renderScale;

    // --- Pass A: scene base ---
    gl.bindFramebuffer(gl.FRAMEBUFFER, engine.sceneFbo.fbo);
    gl.viewport(0, 0, engine.sceneFbo.width, engine.sceneFbo.height);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    gl.clearBufferfv(gl.COLOR, 0, [0.04, 0.04, 0.05, 1]);

    drawScenePartitioned(
      engine,
      gl,
      0,
      1.0,
      (start, count, tex, useFillTex, blendMode) => {
        const segData = engine.compiled!.data.subarray(start * stride, (start + count) * stride);
        const sceneFbo = engine.sceneFbo!;
        const scratchFbo = engine.scratchFbo;

        if (blendMode === 0) {
          // Normal: draw straight to sceneFbo with default alpha blending.
          gl.bindFramebuffer(gl.FRAMEBUFFER, sceneFbo.fbo);
          gl.viewport(0, 0, sceneFbo.width, sceneFbo.height);
          drawRects(
            gl,
            engine.rectPipe!,
            { ...engine.compiled!, count, data: segData as Float32Array },
            canvasCss,
            dprScale,
            0,
            1.0,
            tex,
            useFillTex,
            NO_BACKDROP,
            NO_MIX_BLEND,
          );
          return;
        }

        // Layer 6: blend segment. Two-pass via scratchFbo.
        //
        // The "behind" texture for blend math is sampled from sceneFbo.tex
        // (NOT scratchFbo.tex) to avoid the WebGL feedback loop — sampling
        // a texture attached to the currently-bound draw framebuffer is
        // undefined behavior. The copy sceneFbo → scratchFbo at the start of
        // this segment gives scratchFbo the unmodified scene, which is also
        // what's in sceneFbo, so the FS sample (sceneFbo.tex) is the right
        // "behind" color. After the draw, we blit scratchFbo → sceneFbo to
        // publish the blend result.
        if (!scratchFbo) return;
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, sceneFbo.fbo);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, scratchFbo.fbo);
        gl.blitFramebuffer(
          0, 0, sceneFbo.width, sceneFbo.height,
          0, 0, scratchFbo.width, scratchFbo.height,
          gl.COLOR_BUFFER_BIT, gl.NEAREST,
        );
        gl.bindFramebuffer(gl.FRAMEBUFFER, scratchFbo.fbo);
        gl.viewport(0, 0, scratchFbo.width, scratchFbo.height);
        drawRects(
          gl,
          engine.rectPipe!,
          { ...engine.compiled!, count, data: segData as Float32Array },
          canvasCss,
          dprScale,
          0,
          1.0,
          tex,
          useFillTex,
          NO_BACKDROP,
          // Sample from sceneFbo.tex (NOT scratchFbo.tex) to avoid the
          // feedback loop. sceneFbo has the unmodified scene at this point.
          { tex: sceneFbo.tex, mode: blendMode, opacity: 1 },
        );
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, scratchFbo.fbo);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, sceneFbo.fbo);
        gl.blitFramebuffer(
          0, 0, scratchFbo.width, scratchFbo.height,
          0, 0, sceneFbo.width, sceneFbo.height,
          gl.COLOR_BUFFER_BIT, gl.NEAREST,
        );
      },
      (n) => (n.filter?.backdropBlur ?? 0) === 0, // skip backdrop nodes here
    );

    // --- Pass B: backdrop-filter nodes drawn into backdropFbo ---
    if (engine.backdropFbo && engine.backdropPipe) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, engine.backdropFbo.fbo);
      gl.viewport(0, 0, engine.backdropFbo.width, engine.backdropFbo.height);
      gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
      gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]);

      drawScenePartitioned(
        engine,
        gl,
        0,
        1.0,
        (start, count, tex, useFillTex, _blendMode) => {
          const segData = engine.compiled!.data.subarray(start * stride, (start + count) * stride);
          const backdropFbo = engine.backdropFbo!;
          const sceneFbo = engine.sceneFbo!;
          // Pick a representative backdropBlur radius for the segment (uniform —
          // single value per draw call). If the segment contains multiple nodes
          // with different radii we use the first non-zero value, which is
          // close enough for the PoC.
          const sn = sortedNodes(engine.scene);
          let backdropBlur = 0;
          for (let k = start; k < start + count; k++) {
            const node = sn[k];
            if (node && (node.filter?.backdropBlur ?? 0) > 0) {
              backdropBlur = node.filter!.backdropBlur!;
              break;
            }
          }
          gl.bindFramebuffer(gl.FRAMEBUFFER, backdropFbo.fbo);
          gl.viewport(0, 0, backdropFbo.width, backdropFbo.height);
          drawRects(
            gl,
            engine.rectPipe!,
            { ...engine.compiled!, count, data: segData as Float32Array },
            canvasCss,
            dprScale,
            0,
            1.0,
            tex,
            useFillTex,
            {
              tex: sceneFbo.tex,
              width: sceneFbo.width,
              height: sceneFbo.height,
              blur: backdropBlur,
            },
            NO_MIX_BLEND,
          );
        },
        (n) => (n.filter?.backdropBlur ?? 0) > 0, // only backdrop nodes
      );

      // --- Pass C: composite backdropFbo onto sceneFbo ---
      // The compositor shader writes premultiplied color straight from
      // backdropFbo.tex; with GL_BLEND enabled and blendFunc(ONE,
      // ONE_MINUS_SRC_ALPHA) the blend unit computes the "over" operator
      // automatically. backdropFbo pixels with alpha=0 (outside any backdrop
      // rect) leave sceneFbo untouched.
      gl.bindFramebuffer(gl.FRAMEBUFFER, engine.sceneFbo.fbo);
      gl.viewport(0, 0, engine.sceneFbo.width, engine.sceneFbo.height);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      drawBackdropComposite(gl, engine.backdropPipe, engine.backdropFbo.tex);
    }
  }

  // 7b. Velocity pass
  if (engine.velocityFbo && engine.rectPipe && engine.compiled) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, engine.velocityFbo.fbo);
    gl.viewport(0, 0, engine.velocityFbo.width, engine.velocityFbo.height);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]);
    const velFormat: 0 | 1 | 2 =
      engine.velocityInfo?.format === "RG16F" ? 2 : 1;
    const stride = FLOATS_PER_INSTANCE;
    // Velocity scale is tied to the blur slider so the slider has a visible
    // effect even on slow motion: at slider=0, motion is recorded at its
    // real speed (no extra amplification); at slider=100, motion is
    // amplified 4× before being blurred. Combined with the postprocess
    // uBlurStrength multiplier, the slider now controls "how blurry the
    // world looks" end-to-end.
    const velocityScale = 8.0 * (0.25 + 0.75 * engine.blurStrength);
    drawScenePartitioned(engine, gl, velFormat, velocityScale, (start, count, tex, useFillTex, _blendMode) => {
      const segData = engine.compiled!.data.subarray(start * stride, (start + count) * stride);
      drawRects(
        gl,
        engine.rectPipe!,
        { ...engine.compiled!, count, data: segData as Float32Array },
        [engine.canvas.clientWidth, engine.canvas.clientHeight],
        engine.ctx!.dpr * engine.ctx!.renderScale,
        velFormat,
        30.0,
        tex,
        useFillTex,
        // Velocity pass writes to the velocity FBO, not sceneFbo, so backdrop
        // sampling would pick up wrong content. Backdrop-filter stays off here.
        // Mix-blend-mode also stays off — velocity is a separate concern.
        NO_BACKDROP,
        NO_MIX_BLEND,
      );
    });
  }

  // 8. Postprocess → default framebuffer. Custom-draw scenes (chain)
  // bypass this entirely — they manage their own canvas-side rendering.
  if (engine.customDraw) {
    engine.customDraw(engine, engine.timing.t);
  } else if (engine.sceneFbo) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, engine.canvas.width, engine.canvas.height);
    gl.drawBuffers([gl.BACK]);
    gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
    const velFormat: 0 | 1 | 2 =
      engine.velocityInfo?.format === "RG16F" ? 2 :
      engine.velocityInfo?.format === "RG8" ? 1 : 0;
    if (engine.cinematicEnabled && engine.cosmicDust) {
      // Macro-Cosmic Dust and Optics cinematic pass. Replaces the
      // standard velocity-blur post with a two-pass render:
      // (a) 8x8 hot-pixel locator finds the global brightest pixel on
      // screen, (b) cinematic grade applies filmic tone map + halation
      // + volumetric light shaft + 3-layer parallax dust + anchored
      // lens flare + chromatic aberration + film grain. Defaults match
      // the published design doc — bright hero areas get subtle flare
      // and shimmer, dark sections automatically reduce bloom.
      // Master amounts can be dialed in here (or exposed via HUD).
      drawCosmicDust(
        gl,
        engine.cosmicDust,
        engine.sceneFbo.tex,
        engine.velocityFbo?.tex ?? null,
        engine.sceneFbo.width,
        engine.sceneFbo.height,
        {
          blurStrength: engine.blurStrength,
          velocityFormat: velFormat,
          time: engine.timing.t,
          seed: Math.random() * 1000,
          toneMap: 1.0,      // ACES
          halation: 0.55,
          shaft:    0.45,
          dust:     0.65,
          flare:    0.55,
          ca:       0.30,
          grain:    0.04,
          exposure: 0.15,    // slight +0.15 stop exposure for cinematic feel
          warmth:   0.18,    // mild warm-shadow / cool-highlight
        },
      );
    } else {
      drawPostProcess(
        gl,
        engine.postPipe,
        engine.sceneFbo.tex,
        engine.velocityFbo?.tex ?? null,
        engine.blurStrength,
        velFormat,
        engine.sceneFbo.width,
        engine.sceneFbo.height,
      );
    }
  }

  // 9. HUD update
  updateHud(engine.hud, {
    fps: engine.timing.fps,
    nodes: engine.compiled?.count ?? 0,
    scale: engine.ctx.renderScale,
    dpr: engine.ctx.dpr,
    w: engine.canvas.width,
    h: engine.canvas.height,
    compileMs: engine.compileMs,
  });
}

// Re-export FLOATS_PER_INSTANCE for the partitioned draw above.
import { FLOATS_PER_INSTANCE } from "./compiler";

function loop(engine: Engine): void {
  frame(engine);
  renderStudioOverlay(engine);
  requestAnimationFrame(() => loop(engine));
}

function attachInput(engine: Engine): void {
  const canvas = engine.canvas;
  setViewportSize(engine.pointer, engine.canvas.clientWidth, engine.canvas.clientHeight);

  const updateHover = (clientX: number, clientY: number) => {
    const hit = hitTest(engine.scene, clientX, clientY);
    if (hit) {
      setHovered(engine.scene, hit.id);
    } else {
      setHovered(engine.scene, null);
    }
  };

  const onDown = (clientX: number, clientY: number, id: number) => {
    const p = { x: clientX, y: clientY };
    engine.pointer.active = true;
    engine.pointer.id = id;
    engine.pointer.x = p.x;
    engine.pointer.y = p.y;
    engine.pointer.dx = 0;
    engine.pointer.dy = 0;
    engine.pointer.samples = [];
    engine.pointer.inertia = false;
    engine.inertia = null;
    // Touch-aware scenes: register a click for whichever touch
    // pipes are active. Hoist w/h/tNow so each subsequent if
    // block doesn't have to redeclare them.
    const w = engine.canvas.clientWidth || engine.canvas.width;
    const h = engine.canvas.clientHeight || engine.canvas.height;
    const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
    const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
    const tNow = (engine.timing as TimingState & { t?: number }).t
      ?? (performance.now() / 1000);
    // tessellationMeltTouch: shockwave
    if (engine.tessellationMeltTouchPipe) {
      touchPushClick(engine.tessellationMeltTouchPipe, px, py, tNow);
    }
    // fractalSmokeTouch: turbulence burst
    if (engine.fractalSmokeTouchPipe) {
      const pipe = engine.fractalSmokeTouchPipe;
      pipe.clicks.push({ x: px, y: py, t: tNow });
      if (pipe.clicks.length > 2) pipe.clicks.shift();
    }
    // tesseractShadowTouch: breathe pulse
    if (engine.tesseractShadowTouchPipe) {
      const pipe = engine.tesseractShadowTouchPipe;
      pipe.clicks.push({ x: px, y: py, t: tNow });
      if (pipe.clicks.length > 2) pipe.clicks.shift();
    }
    // magneticMonopoleTouch: flip polarity
    if (engine.magneticMonopoleTouchPipe) {
      engine.magneticMonopoleTouchPipe.polarity =
        -engine.magneticMonopoleTouchPipe.polarity;
    }
    if (engine.magneticFilingsPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      magneticFilingsPushClick(engine.magneticFilingsPipe, px, py, tNow);
    }
    if (engine.petrifiedBloomPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      petrifiedBloomPushClick(engine.petrifiedBloomPipe, px, py, tNow);
    }
    if (engine.crackedGlassPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      crackedGlassPushClick(engine.crackedGlassPipe, px, py, tNow);
    }
    if (engine.lightningWebPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      lightningWebPushClick(engine.lightningWebPipe, px, py, tNow);
    }
    if (engine.crystalGrowthPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      crystalGrowthPushClick(engine.crystalGrowthPipe, px, py, tNow);
    }
    if (engine.smokeRingPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      smokeRingPushClick(engine.smokeRingPipe, px, py, tNow);
    }
    if (engine.bubbleBlowPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      bubbleBlowPushClick(engine.bubbleBlowPipe, px, py, tNow);
    }
    if (engine.sandPilePipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      sandPilePushClick(engine.sandPilePipe, px, py, tNow);
    }
    if (engine.auroraTouchPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      auroraTouchPushClick(engine.auroraTouchPipe, px, py, tNow);
    }
    if (engine.windBlownGrassPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      windBlownGrassPushClick(engine.windBlownGrassPipe, px, py, tNow);
    }
    if (engine.sugarCrystalPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      sugarCrystalPushClick(engine.sugarCrystalPipe, px, py, tNow);
    }
    if (engine.tidePoolPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      tidePoolPushClick(engine.tidePoolPipe, px, py, tNow);
    }
    if (engine.bubbleChamberPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      bubbleChamberPushClick(engine.bubbleChamberPipe, px, py, tNow);
    }
    if (engine.rustGrowthPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      rustGrowthPushClick(engine.rustGrowthPipe, px, py, tNow);
    }
    if (engine.plasmaTouchPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      plasmaTouchPushClick(engine.plasmaTouchPipe, px, py, tNow);
    }
    if (engine.webSpinningPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      webSpinningPushClick(engine.webSpinningPipe, px, py, tNow);
    }
    if (engine.stainedGlassTouchPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      stainedGlassTouchPushClick(engine.stainedGlassTouchPipe, px, py, tNow);
    }
    if (engine.waveInterferencePipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      waveInterferencePushClick(engine.waveInterferencePipe, px, py, tNow);
    }
    if (engine.lichenGrowthPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      lichenGrowthPushClick(engine.lichenGrowthPipe, px, py, tNow);
    }
    if (engine.paperFoldPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      paperFoldPushClick(engine.paperFoldPipe, px, py, tNow);
    }
    if (engine.vineGrowthPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      vineGrowthPushClick(engine.vineGrowthPipe, px, py, tNow);
    }
    if (engine.cracklePatternPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      cracklePatternPushClick(engine.cracklePatternPipe, px, py, tNow);
    }
    if (engine.kaleidoscopeTouchPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      kaleidoscopeTouchPushClick(engine.kaleidoscopeTouchPipe, px, py, tNow);
    }
    if (engine.embroideryPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      embroideryPushClick(engine.embroideryPipe, px, py, tNow);
    }
    if (engine.riverPathsPipe) {
      const px = (clientX / Math.max(w, 1)) * engine.canvas.width;
      const py = (clientY / Math.max(h, 1)) * engine.canvas.height;
      riverPathsPushClick(engine.riverPathsPipe, px, py, tNow);
    }
    // Per-scene pointer hook fires first. If it returns true, the event is
    // swallowed (the Studio handler is skipped). This lets scenes like the
    // product page interpret clicks on thumbnails as image-picker, without
    // fighting with the Studio drag/resize system.
    if (engine.scene?.onPointerDown) {
      const consumed = engine.scene.onPointerDown(p.x, p.y);
      if (consumed === true) {
        engine.pointer.dragging = null;
        return;
      }
    }
    // Studio editing is gated behind the Edit toggle in the toolbar. When
    // editing is off, all pointer interactions pass through untouched — clicks
    // hit-test normally, drag does nothing. When editing is on, the existing
    // select/drag/resize behavior applies.
    if (!engine.studio.editing) {
      engine.pointer.dragging = null;
      engine.studio.dragMode = null;
      engine.studio.dragHandle = null;
      return;
    }
    // Studio hit-test: handles take priority, then nodes. Affects drag/move.
    const studioHit = hitTestStudio(engine.scene, engine.studio, p.x, p.y);
    if (studioHit.kind === "handle") {
      // Resize mode — no inertia, capture start state for delta math.
      const sel = engine.studio.selected != null
        ? engine.scene.nodes.get(engine.studio.selected) : null;
      if (sel) {
        engine.pointer.dragging = sel.id;
        engine.studio.dragMode = "resize";
        engine.studio.dragHandle = studioHit.handle;
        engine.studio.dragStartX = p.x;
        engine.studio.dragStartY = p.y;
        engine.studio.dragStartNodeX = sel.x;
        engine.studio.dragStartNodeY = sel.y;
        engine.studio.dragStartNodeW = sel.w;
        engine.studio.dragStartNodeH = sel.h;
      }
    } else if (studioHit.kind === "node") {
      const hit = studioHit.node;
      // Select this node (or keep selection if already selected).
      engine.studio.selected = hit.id;
      engine.pointer.dragging = hit.id;
      engine.pointer.dragOffsetX = p.x - hit.x;
      engine.pointer.dragOffsetY = p.y - hit.y;
      engine.studio.dragMode = "move";
      hit.zIndex = 10000 + Date.now() % 1000;
      engine.scene.sorted = null;
    } else {
      // Empty area — deselect.
      engine.pointer.dragging = null;
      engine.studio.selected = null;
      engine.studio.dragMode = null;
      engine.studio.dragHandle = null;
    }
  };

  const onMove = (clientX: number, clientY: number) => {
    updateHover(clientX, clientY);
    if (!engine.pointer.active) return;
    const p = { x: clientX, y: clientY };
    const dx = p.x - engine.pointer.x;
    const dy = p.y - engine.pointer.y;
    engine.pointer.dx = dx;
    engine.pointer.dy = dy;
    engine.pointer.x = p.x;
    engine.pointer.y = p.y;
    recordPointerSample(engine.pointer, dx, dy, performance.now());
    if (engine.pointer.dragging != null) {
      const n = engine.scene.nodes.get(engine.pointer.dragging);
      if (n) {
        if (engine.studio.dragMode === "resize") {
          applyHandleResize(engine.studio, p.x, p.y, n);
          // Don't update prevX/prevY — that would clobber motion blur trails.
        } else {
          n.x = p.x - engine.pointer.dragOffsetX;
          n.y = p.y - engine.pointer.dragOffsetY;
          n.prevX = n.x;
          n.prevY = n.y;
        }
      }
    }
  };

  const onUp = () => {
    engine.pointer.active = false;
    engine.pointer.id = null;
    const draggingId = engine.pointer.dragging;
    engine.pointer.dragging = null;

    if (draggingId != null) {
      const n = engine.scene.nodes.get(draggingId);
      // Studio resize: no inertia, just persist.
      if (engine.studio.dragMode === "resize") {
        engine.studio.dragMode = null;
        engine.studio.dragHandle = null;
        persistScene(engine.currentScene, engine.scene);
      } else if (n) {
        // Studio move: persist on up so positions survive reload.
        persistScene(engine.currentScene, engine.scene);
        // Bounce dampening for big objects.
        const release = computeReleaseVelocity(engine.pointer);
        const speed = Math.hypot(release.vx, release.vy);
        if (speed > 20) {
          const dampened = dampenForMass(release.vx, release.vy, n);
          if (Math.hypot(dampened.vx, dampened.vy) > 20) {
            engine.inertia = {
              nodeId: draggingId,
              vx: dampened.vx,
              vy: dampened.vy,
            };
            engine.pointer.inertia = true;
          }
        }
      }
    }
  };

  canvas.addEventListener(
    "pointerdown",
    (e) => {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      onDown(e.clientX, e.clientY, e.pointerId);
    },
    { passive: false },
  );
  canvas.addEventListener("pointermove", (e) => onMove(e.clientX, e.clientY), { passive: true });
  canvas.addEventListener("pointerup", () => onUp(), { passive: true });
  canvas.addEventListener("pointercancel", () => onUp(), { passive: true });
}

/** Mount the studio toolbar (BG toggle, Reset, Save) into the page. Idempotent. */
function mountStudioToolbar(engine: Engine): void {
  if (engine.studio.toolbar) return;
  const root = (document.getElementById("studio-toolbar") as HTMLDivElement | null) ?? (() => {
    const el = document.createElement("div");
    el.id = "studio-toolbar";
    el.style.cssText = [
      "position:fixed",
      "top:80px",
      "right:24px",
      "z-index:10",
      "display:flex",
      "flex-direction:column",
      "gap:8px",
      "padding:10px",
      "background:rgba(15,23,42,0.72)",
      "border:1px solid rgba(255,255,255,0.15)",
      "border-radius:12px",
      "backdrop-filter:blur(12px)",
      "color:white",
      "font-family:system-ui,sans-serif",
      "font-size:12px",
      "min-width:160px",
    ].join(";");
    document.body.appendChild(el);
    return el;
  })();
  root.innerHTML = `
    <div style="font-weight:600;font-size:13px;letter-spacing:0.04em;opacity:0.85">STUDIO</div>
    <div id="studio-sel" style="opacity:0.6">no selection</div>
    <button id="studio-edit" style="margin-top:6px">Edit (off)</button>
    <button id="studio-bg">Toggle BG</button>
    <button id="studio-save">Save layout</button>
    <button id="studio-reset">Reset</button>
    <div style="font-size:10px;opacity:0.5;margin-top:4px">Edit on → tap card to select, drag to move, drag corner to resize</div>
  `;
  const selEl = root.querySelector("#studio-sel") as HTMLDivElement;
  const editBtn = root.querySelector("#studio-edit") as HTMLButtonElement;
  const bgBtn = root.querySelector("#studio-bg") as HTMLButtonElement;
  const saveBtn = root.querySelector("#studio-save") as HTMLButtonElement;
  const resetBtn = root.querySelector("#studio-reset") as HTMLButtonElement;
  const refreshEditBtn = () => {
    if (engine.studio.editing) {
      editBtn.textContent = "Edit (on)";
      editBtn.style.background = "rgba(251, 146, 60, 0.4)";
      editBtn.style.borderColor = "rgba(251, 146, 60, 0.7)";
    } else {
      editBtn.textContent = "Edit (off)";
      editBtn.style.background = "";
      editBtn.style.borderColor = "";
    }
  };
  editBtn.onclick = () => {
    engine.studio.editing = !engine.studio.editing;
    if (!engine.studio.editing) {
      // Leaving edit mode clears selection so re-entering starts clean.
      engine.studio.selected = null;
      engine.studio.dragMode = null;
      engine.studio.dragHandle = null;
      engine.pointer.dragging = null;
    }
    refreshEditBtn();
  };
  refreshEditBtn();
  bgBtn.onclick = () => {
    const id = engine.studio.selected;
    if (id == null) return;
    const n = engine.scene.nodes.get(id);
    if (!n) return;
    if (n.fill.a > 0.001) {
      // Persist the visible alpha so Reset restores to the same value.
      n._origAlpha = n.fill.a;
      n.fill = { ...n.fill, a: 0 };
    } else {
      n.fill = { ...n.fill, a: (n as { _origAlpha?: number })._origAlpha ?? 0.92 };
    }
    persistScene(engine.currentScene, engine.scene);
  };
  saveBtn.onclick = () => {
    persistScene(engine.currentScene, engine.scene);
    saveBtn.textContent = "Saved ✓";
    setTimeout(() => (saveBtn.textContent = "Save layout"), 1200);
  };
  resetBtn.onclick = () => {
    clearPersistedState(engine.currentScene);
    switchScene(engine, engine.currentScene); // re-init from scratch
    resetBtn.textContent = "Reset ✓";
    setTimeout(() => (resetBtn.textContent = "Reset"), 1200);
  };
  engine.studio.toolbar = root;
  // Update selection display whenever it changes.
  const tick = () => {
    if (engine.studio.selected != null) {
      const n = engine.scene.nodes.get(engine.studio.selected);
      selEl.textContent = n ? `card #${n.id} · ${Math.round(n.w)}×${Math.round(n.h)}` : "—";
    } else {
      selEl.textContent = "no selection";
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** Remove the studio toolbar from the DOM if mounted. Idempotent. */
function unmountStudioToolbar(engine: Engine): void {
  const existing = document.getElementById("studio-toolbar");
  if (existing) existing.remove();
  // Clear the engine-side reference too, otherwise mountStudioToolbar's
  // early-out check (`if (engine.studio.toolbar) return`) prevents
  // re-mounting when the user switches back to the Studio scene.
  engine.studio.toolbar = null;
  // The selection overlay is fine to leave behind — renderStudioOverlay
  // skips drawing when there's no selection. But clearing the selection is
  // good hygiene when leaving the studio scene.
  const overlay = document.getElementById("studio-overlay");
  if (overlay) overlay.innerHTML = "";
}

/** Mount a tiny, always-visible Edit toggle (bottom-right) plus a Reset
 *  button. Lives outside any single scene so it's reachable from the
 *  Landing, Advanced, Chase, etc. The Edit toggle flips
 *  `engine.studio.editing`; the Reset button clears the persisted state
 *  for the current scene and re-creates the scene so nodes snap back
 *  to their original (pre-edit) positions. */
function mountEditToggle(engine: Engine): void {
  // Wrapper positions both controls together at bottom-right (above the
  // scene picker) so they share layout & visual style.
  const wrap = (document.getElementById("edit-cluster") as HTMLDivElement | null) ?? (() => {
    const d = document.createElement("div");
    d.id = "edit-cluster";
    d.style.cssText = [
      "position:fixed",
      "right:14px",
      "bottom:54px",
      "z-index:12",
      "display:flex",
      "align-items:center",
      "gap:6px",
    ].join(";");
    document.body.appendChild(d);
    return d;
  })();
  const el = (document.getElementById("edit-toggle") as HTMLButtonElement | null) ?? (() => {
    const b = document.createElement("button");
    b.id = "edit-toggle";
    b.title = "Toggle edit mode (drag/resize any element on the canvas)";
    b.style.cssText = [
      "display:inline-flex",
      "align-items:center",
      "gap:6px",
      "height:30px",
      "padding:0 12px 0 10px",
      "border-radius:999px",
      "border:1px solid rgba(255,255,255,0.18)",
      "background:rgba(15,23,42,0.65)",
      "backdrop-filter:blur(10px)",
      "color:rgba(255,255,255,0.65)",
      "font-family:system-ui,-apple-system,sans-serif",
      "font-size:11px",
      "font-weight:600",
      "letter-spacing:0.06em",
      "text-transform:uppercase",
      "cursor:pointer",
      "user-select:none",
      "transition:background 120ms,color 120ms,border-color 120ms",
    ].join(";");
    wrap.appendChild(b);
    return b;
  })();
  const dot = (() => {
    const s = document.createElement("span");
    s.style.cssText = [
      "display:inline-block",
      "width:6px",
      "height:6px",
      "border-radius:50%",
      "background:currentColor",
    ].join(";");
    return s;
  })();
  // Reset button — clears localStorage for the current scene and
  // re-creates the scene so nodes snap back to their initial positions.
  const resetBtn = (document.getElementById("edit-reset") as HTMLButtonElement | null) ?? (() => {
    const b = document.createElement("button");
    b.id = "edit-reset";
    b.title = "Reset scene to its original layout (clears saved edits)";
    b.style.cssText = [
      "display:inline-flex",
      "align-items:center",
      "justify-content:center",
      "width:30px",
      "height:30px",
      "border-radius:999px",
      "border:1px solid rgba(255,255,255,0.18)",
      "background:rgba(15,23,42,0.65)",
      "backdrop-filter:blur(10px)",
      "color:rgba(255,255,255,0.65)",
      "font-family:system-ui,-apple-system,sans-serif",
      "font-size:14px",
      "cursor:pointer",
      "user-select:none",
      "transition:background 120ms,color 120ms",
    ].join(";");
    b.textContent = "↺";
    wrap.appendChild(b);
    return b;
  })();
  const paint = () => {
    el.textContent = "";
    el.appendChild(dot);
    el.appendChild(document.createTextNode(engine.studio.editing ? "Edit · on" : "Edit"));
    if (engine.studio.editing) {
      el.style.background = "rgba(251,146,60,0.18)";
      el.style.borderColor = "rgba(251,146,60,0.55)";
      el.style.color = "rgba(254,215,170,1)";
    } else {
      el.style.background = "rgba(15,23,42,0.65)";
      el.style.borderColor = "rgba(255,255,255,0.18)";
      el.style.color = "rgba(255,255,255,0.65)";
    }
  };
  paint();
  el.onclick = () => {
    engine.studio.editing = !engine.studio.editing;
    if (!engine.studio.editing) {
      engine.studio.selected = null;
      engine.studio.dragMode = null;
      engine.studio.dragHandle = null;
    }
    paint();
    // Sync the existing Studio toolbar's button text if it's mounted.
    const studioEdit = document.getElementById("studio-edit");
    if (studioEdit) studioEdit.textContent = engine.studio.editing ? "Edit (on)" : "Edit (off)";
  };
  resetBtn.onclick = () => {
    const name = engine.currentScene;
    clearPersistedState(name);
    // Re-create the scene. With the persisted state cleared,
    // loadSceneState() returns null and nodes start at their initial
    // positions defined in the scene factory.
    switchScene(engine, name);
    // Clear any in-flight selection so a stale node id doesn't appear as
    // still-selected after the swap.
    engine.studio.selected = null;
    engine.studio.dragMode = null;
    engine.studio.dragHandle = null;
    paint();
    // Visual feedback — quick orange flash so the user knows it worked.
    const orig = resetBtn.style.background;
    resetBtn.style.background = "rgba(251,146,60,0.45)";
    setTimeout(() => { resetBtn.style.background = orig; }, 220);
  };
}

/** Render the selection outline + 8 resize handles around the currently
 *  selected node using DOM elements overlaid on the canvas. Pure presentation
 *  — pointer events are handled by the canvas hit-test system. */
function renderStudioOverlay(engine: Engine): void {
  let overlay = document.getElementById("studio-overlay") as HTMLDivElement | null;
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "studio-overlay";
    overlay.style.cssText = "position:fixed;left:0;top:0;pointer-events:none;z-index:5";
    document.body.appendChild(overlay);
  }
  const sel = engine.studio.selected != null
    ? engine.scene.nodes.get(engine.studio.selected) : null;

  // Hover ring: only when nothing is selected (we don't double-up outlines)
  // and pointer is currently over a node in the Studio scene.
  const hoveredId = engine.scene.hoveredNode;
  const isStudioScene = engine.currentScene === "studio";
  const hovered = !sel && isStudioScene && hoveredId != null
    ? engine.scene.nodes.get(hoveredId) : null;

  if (!sel && !hovered) {
    overlay.innerHTML = "";
    return;
  }

  const parts: string[] = [];

  if (hovered) {
    // Soft cyan outline for hovered (non-selected) cards.
    const w = 1.5;
    parts.push(`<div style="
      position:absolute;
      left:${hovered.x - w}px;
      top:${hovered.y - w}px;
      width:${hovered.w + w * 2}px;
      height:${hovered.h + w * 2}px;
      border:${w}px solid rgba(125, 211, 252, 0.7);
      border-radius:4px;
      box-shadow:0 0 0 1px rgba(125, 211, 252, 0.2) inset;
      pointer-events:none;
    "></div>`);
  }

  if (sel) {
    const outlineW = 2;
    parts.push(`<div style="
      position:absolute;
      left:${sel.x - outlineW}px;
      top:${sel.y - outlineW}px;
      width:${sel.w + outlineW * 2}px;
      height:${sel.h + outlineW * 2}px;
      border:${outlineW}px dashed #fde047;
      pointer-events:none;
    "></div>`);
    const handles: Array<[string, number, number]> = [
      ["nw", sel.x - 7, sel.y - 7],
      ["n",  sel.x + sel.w * 0.5 - 7, sel.y - 7],
      ["ne", sel.x + sel.w - 7, sel.y - 7],
      ["e",  sel.x + sel.w - 7, sel.y + sel.h * 0.5 - 7],
      ["se", sel.x + sel.w - 7, sel.y + sel.h - 7],
      ["s",  sel.x + sel.w * 0.5 - 7, sel.y + sel.h - 7],
      ["sw", sel.x - 7, sel.y + sel.h - 7],
      ["w",  sel.x - 7, sel.y + sel.h * 0.5 - 7],
    ];
    for (const [id, hx, hy] of handles) {
      parts.push(`<div data-h="${id}" style="
        position:absolute;
        left:${hx}px;
        top:${hy}px;
        width:14px;
        height:14px;
        background:white;
        border:1.5px solid #fde047;
        border-radius:3px;
        pointer-events:none;
      "></div>`);
    }
  }

  overlay.innerHTML = parts.join("");
}

function attachScenePicker(engine: Engine): void {
  const row = document.getElementById("scene-row");
  if (!row) return;
  // Buttons live inside the .scene-row-tilt wrapper — that's the element
  // that gets the perspective/rotateX. The outer #scene-row is the flat
  // scroll container; keeping them separate is what makes native horizontal
  // scroll work in BOTH directions on iOS Safari (a transformed
  // overflow-scroller breaks the gesture model on WebKit — only one
  // direction responds). See index.html comment on the picker.
  const tilt = document.getElementById("scene-row-tilt") ?? row;
  tilt.innerHTML = "";
  // NSPP v2 — curated scene picker. 118 entries spanning raymarched 3D,
  // domain warping, real-time cloth, SDF catalogs, and gallery pieces.
  // Inspired by the varex / cineShader / blobmixer design language.
  const builtin: Array<{ key: string; label: string; title: string; sub: string }> = [
    { key: "landing", label: "landing", title: "Land", sub: "NSPP · Curated" },
    { key: "advanced", label: "advanced", title: "Advance", sub: "Hover · drag · scroll" },
    { key: "intro", label: "reel", title: "Reel", sub: "5-second intro" },
    { key: "chase", label: "chase", title: "Chase", sub: "AI narration" },
    { key: "paint-visualizer", label: "paint", title: "Paint", sub: "Visualizer" },
    { key: "womens-fashion", label: "fashion", title: "Fashion", sub: "Shopify PDP" },
    { key: "sdf-library", label: "sdf", title: "SDF", sub: "Geometry catalog" },
    { key: "constellation", label: "stars", title: "Stars", sub: "Interactive system" },
    { key: "attic", label: "attic", title: "Attic", sub: "Home decor PDP" },
    { key: "chain", label: "chain", title: "Chain", sub: "Raymarched links" },
    { key: "morph", label: "morph", title: "Morph", sub: "5-shape morphing" },
    { key: "cine", label: "cine", title: "Cine", sub: "Domain warping" },
    { key: "beat", label: "beat", title: "Beat", sub: "Volumetric nebula" },
    { key: "hex", label: "hex", title: "Hex", sub: "Bioluminescent cells" },
    { key: "aurora", label: "aurora", title: "Aurora", sub: "Northern lights" },
    { key: "vortex", label: "vortex", title: "Vortex", sub: "Spiral singularity" },
    { key: "supernova", label: "nova", title: "Supernova", sub: "Expanding shockwave" },
    { key: "plasma", label: "plasma", title: "Plasma", sub: "Fire field" },
    { key: "drift", label: "drift", title: "Drift", sub: "Particle flow field" },
    { key: "bloom", label: "bloom", title: "Bloom", sub: "Reaction-diffusion" },
    { key: "mesh", label: "mesh", title: "Mesh", sub: "Network graph" },
    { key: "orbits", label: "orbits", title: "Orbits", sub: "Keplerian orbits" },
    { key: "cymatics", label: "cym", title: "Cymatics", sub: "Chladni figures" },
    { key: "mandelbrot", label: "mndl", title: "Mandelbrot", sub: "Fractal zoom" },
    { key: "kaleidoscope", label: "kale", title: "Kaleidoscope", sub: "6-fold mirror" },
    { key: "lorenz", label: "lrenz", title: "Lorenz", sub: "Strange attractor" },
    { key: "phyllotaxis", label: "phyl", title: "Phyllotaxis", sub: "Fibonacci spirals" },
    { key: "quasicrystal", label: "qcrs", title: "Quasicrystal", sub: "Penrose tiling" },
    { key: "tunnel", label: "tunl", title: "Tunnel", sub: "Light rings" },
    { key: "ferrofluid", label: "ferr", title: "Ferrofluid", sub: "Spiky liquid metal" },
    { key: "polyhedron", label: "poly", title: "Polyhedron", sub: "Morphing 3D shapes" },
    { key: "lightning", label: "ltng", title: "Lightning", sub: "Fractal bolts" },
    { key: "caustics", label: "caus", title: "Caustics", sub: "Underwater light" },
    { key: "worleyTerrain", label: "wter", title: "Terrain", sub: "Voronoi mountains" },
    { key: "magnetic", label: "magn", title: "Magnetic", sub: "Iron filings" },
    { key: "lSystem", label: "lsys", title: "L-System", sub: "Plant growth" },
    { key: "truchet", label: "trch", title: "Truchet", sub: "Tile patterns" },
    { key: "sunburst", label: "snbr", title: "Sunburst", sub: "Radiating rays" },
    { key: "spirograph", label: "spir", title: "Spirograph", sub: "Hypotrochoid" },
    { key: "lissajousSymph", label: "liss", title: "Lissajous Symphony", sub: "Interfering oscillators" },
    { key: "ribbonTracer", label: "ribn", title: "Ribbon Tracer", sub: "3D Lissajous ribbons" },
    { key: "galacticCore", label: "gcor", title: "Galactic Core", sub: "Binary orbit" },
    { key: "pendulumCasc", label: "pndc", title: "Pendulum Cascade", sub: "Coupled chaos" },
    { key: "bifrost", label: "bfrs", title: "Bifrost", sub: "Iridescent thin-film" },
    { key: "kaleidoTunnel", label: "ktnl", title: "Kaleido Tunnel", sub: "Prismatic tunnel" },
    { key: "mandala", label: "mndl", title: "Mandala", sub: "Sacred pattern" },
    { key: "phantasm", label: "phnt", title: "Phantasm", sub: "Spectral silhouette" },
    { key: "honeycombTide", label: "hcnt", title: "Honeycomb Tide", sub: "Hexagonal wave field" },
    { key: "equalCathedral", label: "ecat", title: "Equal Cathedral", sub: "Stained glass" },
    { key: "phonograph", label: "phno", title: "Phonograph", sub: "Spiral record groove" },
    { key: "cymaticsPlate", label: "cplt", title: "Cymatics Plate", sub: "Standing waves on plate" },
    { key: "aurora3D", label: "a3d", title: "Aurora 3D", sub: "Volumetric ribbons" },
    { key: "soundWave3D", label: "sw3d", title: "Sound Wave 3D", sub: "Spatial pressure field" },
    { key: "cloth", label: "clth", title: "Cloth", sub: "GPU physics simulation" },
    { key: "coral", label: "crl", title: "Coral", sub: "Branching wireframe" },
    { key: "liquidMetalVol", label: "lmvl", title: "Liquid Metal Vol", sub: "Volumetric blobs" },
    { key: "vines", label: "vns", title: "Vines", sub: "Twisting growth" },
    { key: "neuralNet", label: "nnet", title: "Neural Net", sub: "Network dynamics" },
    { key: "smokeColumns", label: "smkc", title: "Smoke Columns", sub: "Rising particles" },
    { key: "metaballVol", label: "mbvl", title: "Metaball Vol", sub: "Smooth-min blobs" },
    { key: "voronoiCrystal", label: "vrxc", title: "Voronoi Crystal", sub: "Crystal cells" },
    { key: "teslaCoil", label: "tslc", title: "Tesla Coil", sub: "Sparks + magnetic field" },
    { key: "plasmaGlobe", label: "pglo", title: "Plasma Globe", sub: "Filament tendrils" },
    { key: "solarFlare", label: "sflr", title: "Solar Flare", sub: "CME eruption" },
    { key: "kleinFilm", label: "klfm", title: "Klein Film", sub: "Thin-film interference" },
    { key: "starForge", label: "stfg", title: "Star Forge", sub: "Stellar nursery" },
    { key: "voronoiBubbles", label: "vrbl", title: "Voronoi Bubbles", sub: "Cellular foam" },
    { key: "windstream", label: "wnds", title: "Wind Stream", sub: "Trailing flow" },
    { key: "siliconSkin", label: "skn", title: "Silicon Skin", sub: "Sphere pressed by fingers" },
    { key: "viscoelastic", label: "vel", title: "Viscoelastic", sub: "Morphing 5-shape blob" },
    { key: "balloonPress", label: "bln", title: "Balloon Press", sub: "Squash + dimple impact" },
    { key: "membraneDrum", label: "mdr", title: "Membrane Drum", sub: "Drum head w/ ripple hits" },
    { key: "punchBag", label: "pbg", title: "Punch Bag", sub: "Sack being struck" },
    { key: "siliconeFace", label: "srf", title: "Silicone Face", sub: "Face pressed from inside" },
    { key: "liquidSkin", label: "lsk", title: "Liquid Skin", sub: "Torus rippling like water" },
    { key: "clayPress", label: "clp", title: "Clay Press", sub: "Voxel grid deformed by pokes" },
    { key: "drumHead3D", label: "drh", title: "Drum Head 3D", sub: "Ring of chasing dimples" },
    { key: "siliconeVeil", label: "svl", title: "Silicone Veil", sub: "Sheet pressed against face" },
    { key: "liveVideo", label: "vid", title: "Live Video", sub: "Selfie with audio FX" },
    { key: "myceliumDom", label: "myc", title: "Mycelium DOM", sub: "Hyphae frame content" },
    { key: "dislocationField", label: "dis", title: "Dislocation Field", sub: "Crystal grain boundaries" },
    { key: "dislocationFieldLogos", label: "logos", title: "Grain Brands", sub: "FB · IG · X in cores" },
    { key: "marangoniInk", label: "mrk", title: "Marangoni Ink", sub: "Surface-tension-driven dye" },
    { key: "mcCollough", label: "mcc", title: "McCollough", sub: "Afterimage op-art" },
    { key: "hopfFibration", label: "hop", title: "Hopf Fibration", sub: "S³ fibered by S²" },
  { key: "slitScanChrono", label: "sltS", title: "Slit-Scan Chrono", sub: "Time unrolled" },
  { key: "glass", label: "gls", title: "Glass", sub: "Refractive DOM" },
    { key: "dna", label: "dna", title: "DNA", sub: "Double helix" },
    { key: "lavaLamp", label: "lava", title: "Lava Lamp", sub: "Metaballs" },
    { key: "bezier", label: "bzr", title: "Bezier", sub: "Flowing ribbon" },
    { key: "stereographic", label: "strg", title: "Stereographic", sub: "Sphere projection" },
    { key: "contour", label: "cntr", title: "Contour", sub: "Topographic lines" },
    { key: "cosmos", label: "csms", title: "Cosmos", sub: "Volumetric nebula" },
    { key: "galaxy", label: "glxy", title: "Galaxy", sub: "Spiral arms" },
    { key: "oceanStorm", label: "ocn", title: "Ocean Storm", sub: "Gerstner waves" },
    { key: "cyberpunk", label: "cybr", title: "Cyberpunk", sub: "Neon city" },
    { key: "crystalCave", label: "crys", title: "Crystal Cave", sub: "Octahedra" },
    { key: "soapBubble", label: "bubl", title: "Soap Bubble", sub: "Thin-film iridescence" },
    { key: "voronoiGlass", label: "vgls", title: "Voronoi Glass", sub: "Cellular stained" },
    { key: "pixelSort", label: "psrt", title: "Pixel Sort", sub: "Glitch art" },
    { key: "wireframeSphere", label: "wire", title: "Wireframe", sub: "Lat/lon sphere" },
    { key: "blackHole", label: "bhl", title: "Black Hole", sub: "Einstein ring" },
    { key: "liquidMetal", label: "metl", title: "Liquid Metal", sub: "Mercury blob" },
    { key: "stainedGlass", label: "stgl", title: "Stained Glass", sub: "Rose window" },
    { key: "marbleStatue", label: "mrbl", title: "Marble Statue", sub: "Veined stone" },
    { key: "psychedelic", label: "psyc", title: "Psychedelic", sub: "RGB warp" },
    { key: "glitch", label: "glch", title: "Glitch", sub: "Datamosh" },
    { key: "quantumField", label: "qntm", title: "Quantum Field", sub: "HO probability" },
    { key: "particleConstellation", label: "pcst", title: "Constellation", sub: "Particles + trails" },
    { key: "inkInWater", label: "inkw", title: "Ink in Water", sub: "5 plumes" },
    { key: "wormholeTunnel", label: "wmhl", title: "Wormhole", sub: "Tunnel of light" },
    { key: "ascii", label: "asci", title: "ASCII", sub: "Terminal aesthetic" },
    { key: "inkDrop", label: "idrp", title: "Ink Drop", sub: "Falling with ripples" },
    { key: "gyroid", label: "gyrd", title: "Gyroid", sub: "TPMS surface" },
    { key: "schwarzP", label: "schp", title: "Schwarz P", sub: "TPMS surface" },
    { key: "mengerSponge", label: "mngr", title: "Menger Sponge", sub: "Fractal cube" },
    { key: "kleinBottle", label: "klbt", title: "Klein Bottle", sub: "Non-orientable" },
    { key: "conwayLife", label: "cnwy", title: "Conway's Life", sub: "CA simulation" },
    { key: "stableFluid", label: "flud", title: "Stable Fluid", sub: "Stam simulation" },
    { key: "doublePendulum", label: "2pnd", title: "Double Pendulum", sub: "Chaos system" },
    { key: "sphericalHarmonics", label: "shrm", title: "Spherical Harmonics", sub: "Y_l^m" },
    { key: "bec", label: "bec", title: "BEC", sub: "Condensate wave" },
    { key: "phononLattice", label: "phnl", title: "Phonon Lattice", sub: "Spring-mass" },
    { key: "magneticPendulum", label: "mpnd", title: "Magnetic Pendulum", sub: "3-magnet chaos" },
    { key: "schwarzD", label: "schd", title: "Schwarz D", sub: "Diamond TPMS" },
    { key: "liquidCrystal", label: "liqc", title: "Liquid Crystal", sub: "Nematic director" },
    { key: "sincWaveField", label: "sinc", title: "Sinc Field", sub: "sin(x)/x" },
    { key: "sineGordon", label: "sngl", title: "Sine-Gordon", sub: "Soliton PDE" },
    { key: "skyrmion", label: "skrm", title: "Skyrmion", sub: "Topological spin" },
    { key: "hessianEigenvector", label: "hesv", title: "Hessian Field", sub: "Eigenvectors" },
    { key: "braggDiffraction", label: "brgg", title: "Bragg Diffraction", sub: "Reciprocal lattice" },
    { key: "curvatureFlow", label: "curf", title: "Curvature Flow", sub: "Mean curvature PDE" },
    { key: "cellularPotts", label: "cpts", title: "Cellular Potts", sub: "Cell Monte Carlo" },
    { key: "waveFunctionCollapse", label: "wfc", title: "WFC", sub: "Tile generation" },
    { key: "phaseFieldCrystal", label: "pfc", title: "Phase Field Crystal", sub: "Density crystal" },
    { key: "lab", label: "lab", title: "Lab", sub: "Hands-on sandbox" },
  ];
  const css: Array<{ key: string; label: string; title: string; sub: string }> = [];

  const metaByKey = new Map(builtin.concat(css).map((s) => [s.key, s]));

  const makeBtn = (key: string, label: string, group: string) => {
    const btn = document.createElement("button");
    btn.textContent = label;
    btn.className = engine.sceneName === key ? "active" : "";
    btn.dataset.group = group;
    btn.addEventListener("click", () => {
      for (const c of Array.from(tilt.children)) c.classList.remove("active");
      btn.classList.add("active");
      switchScene(engine, key);
      // Update the title overlay.
      const m = metaByKey.get(key);
      showSceneTitle(m?.title ?? key, m?.sub ?? "");
      requestAnimationFrame(() => {
        // Scroll the OUTER #scene-row (the flat scroll container) so the
        // active button is centered. scrollIntoView on a child of a
        // transformed wrapper behaves inconsistently on iOS.
        row.scrollTo({
          left: btn.offsetLeft - row.clientWidth / 2 + btn.clientWidth / 2,
          behavior: "smooth",
        });
      });
    });
    tilt.appendChild(btn);
    if (engine.sceneName === key) {
      requestAnimationFrame(() => {
        row.scrollTo({
          left: btn.offsetLeft - row.clientWidth / 2 + btn.clientWidth / 2,
          behavior: "auto",
        });
      });
    }
  };

  // No group labels — single row of cards.
  for (const { key, label } of builtin) makeBtn(key, label, "scene");
}

/** Fade the scene title overlay in/out. */
function showSceneTitle(title: string, sub: string): void {
  const tEl = document.getElementById("scene-title");
  const sEl = document.getElementById("scene-sub");
  if (!tEl || !sEl) return;
  // Hide first, swap content, fade back in.
  tEl.classList.remove("visible");
  sEl.classList.remove("visible");
  setTimeout(() => {
    tEl.innerHTML = title.replace(/(\w+)/, "<em>$1</em>");
    sEl.textContent = sub;
    tEl.classList.add("visible");
    sEl.classList.add("visible");
    // Auto-hide after a few seconds so the scene takes over.
    setTimeout(() => {
      tEl.classList.remove("visible");
      sEl.classList.remove("visible");
    }, 3000);
  }, 250);
}

/**
 * CATEGORY MENU — top-right dropdown with grouped scenes + search.
 *
 * Categories are data-driven: each scene is assigned to a category
 * by key. The menu renders the categories, wires click handlers
 * to switchScene(), and supports keyboard navigation (M to open,
 * / to focus search, Esc to close).
 */
const CATEGORIES: Array<{ id: string; label: string; keys: string[] }> = [
  {
    id: "hero",
    label: "Hero",
    keys: ["landing", "advanced", "intro", "chase", "paint-visualizer", "womens-fashion", "sdf-library", "constellation", "attic", "pendantBloom", "aspic"]
  },
  {
    id: "raymarched",
    label: "Raymarched 3D",
    keys: ["chain", "morph", "cine", "polyhedron", "ferrofluid", "voronoiBubbles", "lSystem", "plasma", "lavaLamp", "mengerSponge", "kleinBottle", "worleyTerrain", "catenaryLoom"]
  },
  {
    id: "audio",
    label: "Audio-Reactive",
    keys: ["beat", "hex", "lissajousSymph", "ribbonTracer", "galacticCore", "pendulumCasc", "bifrost", "kaleidoTunnel", "mandala", "phantasm", "honeycombTide", "equalCathedral", "sunburst", "mesh", "marangoniInk", "mcCollough","beatNebula"],
  },
  {
    id: "surface",
    label: "Surface / Squish",
    keys: ["siliconSkin", "viscoelastic", "balloonPress", "membraneDrum", "punchBag", "siliconeFace", "liquidSkin", "clayPress", "drumHead3D", "siliconeVeil", "cloth"],
  },
  {
    id: "particles",
    label: "Particles & Volume",
    keys: ["drift", "smokeColumns", "metaballVol", "liquidMetalVol", "voronoiCrystal", "plasmaGlobe", "solarFlare", "teslaCoil", "starForge", "kleinFilm", "inkInWater", "inkDrop", "lightwing", "particleConstellation", "windstream"]
  },
  {
    id: "patterns",
    label: "Patterns & Tessellation",
    keys: ["phyllotaxis", "quasicrystal", "cymatics", "mandelbrot", "lorenz", "truchet", "spirograph", "kaleidoscope", "tunnel", "marbleStatue", "stainedGlass", "crystalCave", "soapBubble", "voronoiGlass", "magnetic", "icyTower", "fractal", "stereographic", "contour", "ascii", "wireframeSphere", "latticePulse", "fontainefeed", "tessellationMelt", "tessellationMeltTouch", "moirefall"]
  },
  {
    id: "physics",
    label: "Physics & Simulation",
    keys: ["stableFluid", "doublePendulum", "magneticPendulum", "phononLattice", "phonograph", "soundWave3D", "cymaticsPlate", "aurora3D", "lightning", "caustics", "conwayLife", "cellularPotts", "waveFunctionCollapse", "phaseFieldCrystal", "resonance", "tokamakPlasma", "photonicCrystal", "bec", "dynamo", "schwarzschildCrossing", "hydrodynamicShock", "fractalSmoke", "quantumFoam", "penroseStairs", "timeCrystal", "magneticMonopole", "rijkeTube", "savartWheel", "granularFlow", "crookesRadiometer", "chladniCells", "baroclinicVortex", "tyndallBeam", "dendriticCrystal", "solarCorona", "snowflake", "halo", "orbits", "quantumField", "sandPile", "windBlownGrass", "tidePool", "bubbleChamber", "waveInterference"]
  },
  {
    id: "math",
    label: "Mathematics",
    keys: ["myceliumDom", "hopfFibration", "slitScanChrono", "dislocationField", "glass", "gyroid", "schwarzP", "schwarzD", "sphericalHarmonics", "sincWaveField", "sineGordon", "skyrmion", "hessianEigenvector", "braggDiffraction", "curvatureFlow", "liquidCrystal", "bec", "mulberry", "tesseractShadow", "mobiusLattice", "feynmanPath", "spectralDecomposition", "anyonicBraiding", "sigils", "dislocationWeb", "lissajousTube", "chronoStrand", "dislocationFieldLogos"]
  },
  {
    id: "stylized",
    label: "Color & Light",
    keys: ["aurora", "vortex", "supernova", "cyberpunk", "oceanStorm", "galaxy", "cosmos", "bezier", "stereographic", "cymaticsPlate", "blackHole", "liquidMetal", "bifrost", "lmvl", "metl", "psychedelic", "glitch", "pixelSort", "wormholeTunnel", "dna", "coral", "neuralNet", "vines", "sunTongue", "tideglass", "cataract", "stalactitePulse", "bifrostMirror", "twinflame", "anamorphic", "phaseConjugate", "ferrofluidSculpture", "trichromatic", "negativeSpace", "bloom", "magneticFilings", "petrifiedBloom", "crackedGlass", "lightningWeb", "crystalGrowth", "smokeRing", "bubbleBlow", "auroraTouch", "sugarCrystal", "rustGrowth", "plasmaTouch", "webSpinning", "stainedGlassTouch", "lichenGrowth", "paperFold", "vineGrowth", "cracklePattern", "kaleidoscopeTouch", "embroidery", "riverPaths"]
  },
  {
    id: "live",
    label: "Live & Special",
    keys: ["liveVideo", "lab", "dna", "gallery"],
  },
];
function attachCategoryMenu(engine: Engine): void {
  const btn = document.getElementById("menu-btn");
  const panel = document.getElementById("menu-panel");
  const search = document.getElementById("menu-search") as HTMLInputElement | null;
  const list = document.getElementById("menu-list");
  const closeBtn = document.getElementById("menu-close");
  if (!btn || !panel || !search || !list || !closeBtn) return;

  // Update the count badge.
  const countEl = btn.querySelector(".menu-count");
  if (countEl) {
    const total = CATEGORIES.reduce((s, c) => s + c.keys.length, 0);
    countEl.textContent = String(total);
  }

  // Build a flat lookup of scene meta by key. Use the same picker
  // metadata table the picker row uses.
  const metaByKey = new Map<string, { title: string; sub: string; label: string }>();
  for (const cat of CATEGORIES) {
    for (const key of cat.keys) {
      const meta = SCENE_META[key];
      if (meta) {
        // The "label" used in the picker row is in the picker list
        // itself; we look it up via a reverse map of ADVANCED_SCENES
        // keys. Simpler: just lowercase the title for the chip.
        metaByKey.set(key, {
          title: meta.title,
          sub: meta.sub,
          label: key.replace(/([A-Z])/g, "-$1").toLowerCase().slice(0, 6),
        });
      }
    }
  }
  // Render the menu once with all categories. Filtering re-renders
 // the inner list only.
const NEW_KEYS = new Set([
  "tesseractShadow",
  "mobiusLattice",
  "tokamakPlasma",
  "photonicCrystal",
  "dynamo",
  "feynmanPath",
  "anamorphic",
  "spectralDecomposition",
  "phaseConjugate",
  "schwarzschildCrossing",
  "hydrodynamicShock",
  "anyonicBraiding",
  "fractalSmoke",
  "quantumFoam",
  "penroseStairs",
  "timeCrystal",
  "magneticMonopole",
  "sigils",
  "ferrofluidSculpture",
  "rijkeTube",
  "savartWheel",
  "granularFlow",
  "crookesRadiometer",
  "chladniCells",
  "dislocationWeb",
  "baroclinicVortex",
  "tyndallBeam",
  "dendriticCrystal",
  "solarCorona",
  "trichromatic",
  "negativeSpace",
  "snowflake",
  "lissajousTube",
  "halo",
  "magneticFilings",
  "petrifiedBloom",
  "crackedGlass",
  "lightningWeb",
  "crystalGrowth",
  "smokeRing",
  "bubbleBlow",
  "sandPile",
  "auroraTouch",
  "windBlownGrass",
  "sugarCrystal",
  "tidePool",
  "bubbleChamber",
  "rustGrowth",
  "plasmaTouch",
  "webSpinning",
  "stainedGlassTouch",
  "waveInterference",
  "lichenGrowth",
  "paperFold",
  "vineGrowth",
  "cracklePattern",
  "kaleidoscopeTouch",
  "embroidery",
  "riverPaths",
]);

    function render(filter: string): void {
    if (!list) return;
    list.innerHTML = "";
    const q = filter.trim().toLowerCase();
    let totalShown = 0;
    for (const cat of CATEGORIES) {
      const matches = cat.keys.filter((k) => {
        if (!q) return true;
        const m = metaByKey.get(k);
        return (
          k.toLowerCase().includes(q) ||
          (m?.title.toLowerCase().includes(q) ?? false) ||
          (m?.sub.toLowerCase().includes(q) ?? false)
        );
      });
      if (matches.length === 0) continue;
      totalShown += matches.length;
      const group = document.createElement("div");
      group.className = "menu-group";
      const header = document.createElement("div");
      header.className = "menu-group-header";
      header.innerHTML = `<span><span class="arrow">▾</span>${cat.label}</span><span class="menu-group-count">${matches.length}</span>`;
      header.addEventListener("click", () => group.classList.toggle("collapsed"));
      group.appendChild(header);
      const items = document.createElement("div");
      items.className = "menu-items";
      for (const key of matches) {
        const m = metaByKey.get(key);
        if (!m) continue;
        const btn = document.createElement("button");
        btn.className = "menu-item" + (engine.sceneName === key ? " active" : "") + (NEW_KEYS.has(key) ? " is-new" : "");
        btn.dataset.key = key;
        const newBadge = NEW_KEYS.has(key) ? '<span class="new-badge">NEW</span>' : '';
        btn.innerHTML = `<span class="label">${key}</span><span class="title">${m.title}${newBadge}</span><span class="sub">${m.sub}</span>`;
        btn.addEventListener("click", () => {
          switchScene(engine, key);
          showSceneTitle(m.title, m.sub);
          closeMenu();
        });
        items.appendChild(btn);
      }
      group.appendChild(items);
      list.appendChild(group);
    }
    if (totalShown === 0) {
      const empty = document.createElement("div");
      empty.className = "menu-empty";
      empty.textContent = `no scenes match "${filter}"`;
      list.appendChild(empty);
    }
  }

  function openMenu(): void {
    panel.removeAttribute("hidden");
    btn.classList.add("open");
    setTimeout(() => search.focus(), 50);
  }
  function closeMenu(): void {
    panel.setAttribute("hidden", "");
    btn.classList.remove("open");
  }
  function toggleMenu(): void {
    if (panel.hasAttribute("hidden")) openMenu();
    else closeMenu();
  }
  // Initial render.
  render("");

  // Wire interactions.
  btn.addEventListener("click", toggleMenu);
  closeBtn.addEventListener("click", closeMenu);
  search.addEventListener("input", () => render(search.value));
  document.addEventListener("keydown", (e) => {
    // Ignore key events when the user is typing in another input.
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (e.key === "m" || e.key === "M") { toggleMenu(); }
    else if (e.key === "/") { openMenu(); e.preventDefault(); }
    else if (e.key === "Escape" && !panel.hasAttribute("hidden")) { closeMenu(); }
  });
  // Click outside the panel closes it.
  document.addEventListener("click", (e) => {
    if (panel.hasAttribute("hidden")) return;
    const t = e.target as Node | null;
    if (!t) return;
    if (panel.contains(t) || btn.contains(t)) return;
    closeMenu();
  });

  // Update the active highlight when the scene changes.
  const observer = new MutationObserver(() => {
    if (!list) return;
    for (const el of Array.from(list.querySelectorAll(".menu-item"))) {
      const item = el as HTMLElement;
      item.classList.toggle("active", item.dataset.key === engine.sceneName);
    }
  });
  // Wait for first render then observe.
  setTimeout(() => {
    if (list) observer.observe(list, { childList: true, subtree: true });
  }, 0);
}

/** Wire the HTML controls (motion slider, rec button) to the engine. */
function attachControls(engine: Engine): void {
  const slider = document.getElementById("motion-slider") as HTMLInputElement | null;
  if (slider) {
    engine.blurStrength = Number(slider.value) / 100;
    slider.addEventListener("input", () => {
      engine.blurStrength = Number(slider.value) / 100;
    });
  }
  const recBtn = document.getElementById("rec-btn");
  if (recBtn) {
    let recording = false;
    recBtn.addEventListener("click", async () => {
      if (recording) return;
      recording = true;
      const orig = recBtn.textContent;
      recBtn.textContent = "● recording...";
      recBtn.classList.add("recording");
      try {
        const sceneFactory = (w: number, h: number) => {
          const name = engine.sceneName;
          const factory = SCENES[name] ?? PAGE_SCENES[name] ?? ADVANCED_SCENES[name];
          return factory ? factory(w, h) : engine.scene;
        };
        let width = 1280, height = 720, durationSec = 5;
        if (engine.sceneName === "intro") {
          width = 1080;
          height = 1920;
          durationSec = 5;
        }
        const blob = await recordScene({
          width, height, fps: 30, durationSec,
          sceneFactory,
          onProgress: (p) => {
            recBtn.textContent = `● ${Math.round(p * 100)}%`;
          },
        });
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        downloadBlob(blob, `nspp-${engine.sceneName}-${stamp}.mp4`);
        recBtn.textContent = "✓ saved";
        recBtn.classList.remove("recording");
        recBtn.classList.add("saved");
        setTimeout(() => {
          recBtn.textContent = orig;
          recBtn.classList.remove("saved");
          recording = false;
        }, 2000);
      } catch (err) {
        console.error("[nspp] record failed:", err);
        recBtn.textContent = "✗ error";
        recBtn.classList.remove("recording");
        recBtn.classList.add("error");
        setTimeout(() => {
          recBtn.textContent = orig;
          recBtn.classList.remove("error");
          recording = false;
        }, 5000);
      }
    });
  }
}

/**
 * Enable microphone capture for the beat scene. Creates an
 * AudioContext + AnalyserNode + getUserMedia stream, and stores them
 * on `engine.beatAudio`. The beat scene's customDraw reads from
 * `engine.beatAudio.freqData` each frame.
 *
 * Must be called from a user gesture (click handler). Returns a
 * promise that resolves when capture is active (or rejects with the
 * underlying error if permission is denied).
 */
async function enableBeatAudio(engine: Engine): Promise<void> {
  if (engine.beatAudio) return; // already enabled
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    const freqData = new Uint8Array(analyser.frequencyBinCount);
    engine.beatAudio = { ctx, analyser, stream, freqData };
  } catch (err) {
    console.error("[nspp] mic capture failed:", err);
    throw err;
  }
}

/** Tear down mic capture. */
function disableBeatAudio(engine: Engine): void {
  if (!engine.beatAudio) return;
  engine.beatAudio.stream.getTracks().forEach((t) => t.stop());
  void engine.beatAudio.ctx.close().catch(() => undefined);
  engine.beatAudio = null;
}

/**
 * Wire the beat scene's mic toggle. The toggle is only shown when
 * the beat scene is active — we show it via the showAudioToggle()
 * helper called from switchScene() when name === "beat".
 */
function attachBeatMicToggle(engine: Engine): void {
  const btn = document.getElementById("beat-mic-btn");
  if (!btn) return;
  btn.addEventListener("click", async () => {
    if (engine.beatAudio) {
      disableBeatAudio(engine);
      btn.textContent = "mic";
      btn.classList.remove("active");
    } else {
      btn.textContent = "…";
      btn.classList.add("loading");
      try {
        await enableBeatAudio(engine);
        btn.textContent = "mic on";
        btn.classList.remove("loading");
        btn.classList.add("active");
      } catch (err) {
        btn.textContent = "mic";
        btn.classList.remove("loading");
        btn.classList.add("error");
        setTimeout(() => btn.classList.remove("error"), 2000);
      }
    }
  });
}

/** Show or hide the beat mic toggle based on the current scene. */
function showBeatMicToggle(engine: Engine, show: boolean): void {
  const btn = document.getElementById("beat-mic-btn");
  if (!btn) return;
  btn.classList.toggle("visible", show);
}

/**
 * Generic audio-reactive scene drawer. Builds the standard customDraw
 * closure for any of the audio-reactive scenes: lazy-creates the pipe,
 * reads audio bands (mic if active, synthetic otherwise), clears the
 * back buffer, and dispatches to the scene-specific draw function.
 *
 * This consolidates the boilerplate that was repeated 17 times.
 */
function makeAudioReactiveDraw<T extends { bands: Float32Array }>(
  engine: Engine,
  ensurePipe: () => T | null,
  computeSynthetic: (t: number, out: Float32Array) => void,
  computeRealtime: (freq: Uint8Array, out: Float32Array) => void,
  draw: (
    gl: WebGL2RenderingContext,
    pipe: T,
    width: number,
    height: number,
    t: number,
  ) => void,
): (e: Engine, t: number) => void {
  return (e: Engine, t: number) => {
    if (!e.ctx) return;
    const pipe = ensurePipe();
    if (!pipe) return;
    const gl = e.ctx.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, e.canvas.width, e.canvas.height);
    gl.drawBuffers([gl.BACK]);
    gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 1]);
    if (e.beatAudio) {
      e.beatAudio.analyser.getByteFrequencyData(e.beatAudio.freqData);
      computeRealtime(e.beatAudio.freqData, pipe.bands);
    } else {
      computeSynthetic(t, pipe.bands);
    }
    draw(gl, pipe, e.canvas.width, e.canvas.height, t);
  };
}

/** Wire the splash screen Enter button. */
function attachSplash(engine: Engine): void {
  const splash = document.getElementById("splash");
  const enterBtn = document.getElementById("splash-enter");
  const controls = document.getElementById("controls-bar");
  if (!splash || !enterBtn) return;
  let entered = false;
  const enter = () => {
    if (entered) return;
    entered = true;
    document.body.classList.remove("splash-active");
    splash.classList.add("hidden");
    controls?.classList.add("visible");
    // Show the initial scene title.
    const initial = engine.sceneName;
    const m = SCENE_META[initial];
    if (m) showSceneTitle(m.title, m.sub);
    setTimeout(() => splash.remove(), 800);
  };
  enterBtn.addEventListener("click", enter);
  // Allow Enter key.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Enter") enter();
  });
  // Allow the keydown listener above to be triggered by automated embed
  // loaders (e.g. Sylently) via postMessage or by synthetic events.
  // Auto-skip splash when ?embed=1 is in the URL. This is the consumer
  // path: the iframe is just a background renderer, no splash needed.
  // Consumers can also pass ?splash=skip to skip without affecting other
  // embed flags.
  if (typeof location !== "undefined") {
    const _eq = new URLSearchParams(location.search);
    const isEmbed = _eq.get("embed") === "1" || _eq.get("splash") === "skip";
    if (isEmbed) {
      // Defer one tick so the engine scene-name is initialized.
      // Override enter() to skip the controls-bar/scene-row reveal too.
      const embedEnter = () => {
        if (entered) return;
        entered = true;
        document.body.classList.remove("splash-active");
        splash.classList.add("hidden");
        // In embed mode, do NOT show the controls bar / scene picker.
        // The embedder is responsible for their own scene switching UI.
        if (controls) controls.style.display = "none";
        const sceneRow = document.getElementById("scene-row");
        if (sceneRow) sceneRow.style.display = "none";
        const sceneRowTilt = document.getElementById("scene-row-tilt");
        if (sceneRowTilt) sceneRowTilt.style.display = "none";
        document.body.classList.add("embed-mode");
        const initial = engine.sceneName;
        const m = SCENE_META[initial];
        if (m) showSceneTitle(m.title, m.sub);
        setTimeout(() => splash.remove(), 800);
      };
      setTimeout(embedEnter, 50);
    }
  }
  // Expose enter() on window so embedders can call it programmatically
  // if they want to control the splash themselves.
  (window as unknown as { __sylentlySplashEnter?: () => void }).__sylentlySplashEnter = enter;
  // C toggles the cinematic camera pipeline (cinematicEnabled).
  document.addEventListener("keydown", (e) => {
    if (e.key === "c" || e.key === "C") {
      engine.cinematicEnabled = !engine.cinematicEnabled;
      console.log(
        "[cosmicDust] cinematic camera:",
        engine.cinematicEnabled ? "ON" : "off (plain velocity blur)",
      );
    }
  });
}

const SCENE_META: Record<string, { title: string; sub: string }> = {
  liss:{title:"Lissajous Symphony",sub:"Interfering oscillators"}, sgph:{title:"Spirograph Wheels",sub:"Nested gear patterns"}, ribn:{title:"Ribbon Tracer",sub:"3D Lissajous ribbons"}, gcore:{title:"Galactic Core",sub:"Binary orbit"}, pdcl:{title:"Pendulum Cascade",sub:"Coupled chaos"}, bfrst:{title:"Bifrost",sub:"Iridescent thin-film"}, ktnl:{title:"Kaleido Tunnel",sub:"Prismatic tunnel"},

  landing: { title: "Land", sub: "NSPP · Curated" },
  advanced: { title: "Advance", sub: "Hover · drag · scroll" },
  intro: { title: "Reel", sub: "5-second intro" },
  chase: { title: "Chase", sub: "AI narration" },
  "paint-visualizer": { title: "Paint", sub: "Visualizer" },
  "womens-fashion": { title: "Fashion", sub: "Shopify PDP" },
  "sdf-library": { title: "SDF", sub: "Geometry catalog" },
  constellation: { title: "Stars", sub: "Interactive system" },
  attic: { title: "Attic", sub: "Home decor PDP" },
  chain: { title: "Chain", sub: "Raymarched links" },
  morph: { title: "Morph", sub: "5-shape morphing" },
  cine: { title: "Cine", sub: "Domain warping" },
  beat: { title: "Beat", sub: "Volumetric nebula" },
  hex: { title: "Hex", sub: "Bioluminescent cells" },
  aurora: { title: "Aurora", sub: "Northern lights" },
  vortex: { title: "Vortex", sub: "Spiral singularity" },
  supernova: { title: "Supernova", sub: "Expanding shockwave" },
  plasma: { title: "Plasma", sub: "Fire field" },
  drift: { title: "Drift", sub: "Particle flow field" },
  latticePulse: { title: "Lattice Pulse", sub: "Phonon wavefront" },
  chronoStrand: { title: "Chrono Strand", sub: "4D worldlines" },
  riverPaths: { title: "River Paths", sub: "Touch erodes rivers" },
  embroidery: { title: "Embroidery", sub: "Touch stitches thread" },
  kaleidoscopeTouch: { title: "Kaleidoscope Touch", sub: "Touch mirror kaleidoscope" },
  cracklePattern: { title: "Crackle Pattern", sub: "Lichtenberg tree" },
  vineGrowth: { title: "Vine Growth", sub: "Touch vines grow" },
  paperFold: { title: "Paper Fold", sub: "Touch folds paper" },
  lichenGrowth: { title: "Lichen Growth", sub: "Touch lichen growth" },
  waveInterference: { title: "Wave Interference", sub: "Touch ripples wave" },
  stainedGlassTouch: { title: "Stained Glass Touch", sub: "Touch stained glass" },
  webSpinning: { title: "Web Spinning", sub: "Touch spins web" },
  plasmaTouch: { title: "Plasma Touch", sub: "Plasma ball touch" },
  rustGrowth: { title: "Rust Growth", sub: "Touch rusts metal" },
  bubbleChamber: { title: "Bubble Chamber", sub: "Touch particle tracks" },
  tidePool: { title: "Tide Pool", sub: "Touch ripples water" },
  sugarCrystal: { title: "Sugar Crystal", sub: "Touch seeds crystals" },
  windBlownGrass: { title: "Wind Blown Grass", sub: "Touch bends grass" },
  auroraTouch: { title: "Aurora Touch", sub: "Touch bends aurora" },
  sandPile: { title: "Sand Pile", sub: "Touch piles sand" },
  bubbleBlow: { title: "Bubble Blow", sub: "Touch blows bubbles" },
  smokeRing: { title: "Smoke Ring", sub: "Puff smoke ring" },
  crystalGrowth: { title: "Crystal Growth", sub: "Touch grows crystals" },
  lightningWeb: { title: "Lightning Web", sub: "Cloud arc touch" },
  crackedGlass: { title: "Cracked Glass", sub: "Touch cracks glass" },
  petrifiedBloom: { title: "Petrified Bloom", sub: "Touch blooms flowers" },
  magneticFilings: { title: "Magnetic Filings", sub: "Iron filings touch magnet" },
  halo: { title: "Halo", sub: "Atmospheric optical rings" },
  lissajousTube: { title: "Lissajous Tube", sub: "3D Lissajous curve" },
  snowflake: { title: "Snowflake", sub: "Hexagonal dendritic" },
  negativeSpace: { title: "Negative Space", sub: "Figure-ground illusion" },
  trichromatic: { title: "Trichromatic", sub: "RGB decomposition" },
  solarCorona: { title: "Solar Corona", sub: "Eclipse streamers" },
  dendriticCrystal: { title: "Dendritic Crystal", sub: "Branching growth" },
  tyndallBeam: { title: "Tyndall Beam", sub: "Light scattering in dust" },
  baroclinicVortex: { title: "Baroclinic Vortex", sub: "Stacked rotating disks" },
  dislocationWeb: { title: "Dislocation Web", sub: "Crystal line defects" },
  chladniCells: { title: "Chladni Cells", sub: "Superimposed nodal patterns" },
  crookesRadiometer: { title: "Crookes Radiometer", sub: "Light-driven vane" },
  granularFlow: { title: "Granular Flow", sub: "Sand grains pouring" },
  savartWheel: { title: "Savart Wheel", sub: "Toothed wheel sound source" },
  rijkeTube: { title: "Rijke Tube", sub: "Thermoacoustic standing wave" },
  ferrofluidSculpture: { title: "Ferrofluid Sculpture", sub: "Magnetic spikes" },
  sigils: { title: "Sigils", sub: "Sacred geometry" },
  magneticMonopole: { title: "Magnetic Monopole", sub: "Isolated magnetic charge" },
  timeCrystal: { title: "Time Crystal", sub: "Time-translation breaking" },
  penroseStairs: { title: "Penrose Stairs", sub: "Impossible staircase" },
  quantumFoam: { title: "Quantum Foam", sub: "Spacetime foam" },
  fractalSmoke: { title: "Fractal Smoke", sub: "Self-similar smoke" },
  anyonicBraiding: { title: "Anyonic Braiding", sub: "2D topological braids" },
  hydrodynamicShock: { title: "Hydrodynamic Shock", sub: "Mach cone" },
  schwarzschildCrossing: { title: "Schwarzschild Crossing", sub: "Event horizon" },
  dynamo: { title: "Dynamo", sub: "Magnetic dynamo" },
  phaseConjugate: { title: "Phase Conjugate", sub: "Time-reversed light" },
  spectralDecomposition: { title: "Spectral Decomposition", sub: "Fourier modes" },
  anamorphic: { title: "Anamorphic Mirror", sub: "Cylindrical projection" },
  feynmanPath: { title: "Feynman Paths", sub: "Quantum paths" },

  photonicCrystal: { title: "Photonic Crystal", sub: "Dielectric band gap" },
  tokamakPlasma: { title: "Tokamak Plasma", sub: "Magnetic confinement" },
  mobiusLattice: { title: "Mobius Lattice", sub: "Non-orientable lattice" },
  tesseractShadow: { title: "Tesseract Shadow", sub: "4D hypercube" },
  twinflame: { title: "Twinflame", sub: "Opposing forces" },
  mulberry: { title: "Mulberry", sub: "Cellular branching" },
  bifrostMirror: { title: "Bifrost Mirror", sub: "Aurora reflected" },
  stalactitePulse: { title: "Stalactite Pulse", sub: "Growing minerals" },
  catenaryLoom: { title: "Catenary Loom", sub: "Woven chains" },
  lightwing: { title: "Lightwing", sub: "Flocking swarm" },
  cataract: { title: "Cataract", sub: "Vertical water walls" },
  aspic: { title: "Aspic", sub: "Trapped-in-jelly" },
  moirefall: { title: "Moirefall", sub: "Moiré interference" },
  tideglass: { title: "Tideglass", sub: "Sand falls up" },
  sunTongue: { title: "Sun Tongue", sub: "Solar prominence" },
  tessellationMelt: { title: "Tessellation Melt", sub: "Voronoi relaxation" },
  resonance: { title: "Resonance", sub: "Coupled oscillators" },
  fontainefeed: { title: "Fontainefeed", sub: "Sky-river inversion" },
  pendantBloom: { title: "Pendant Bloom", sub: "Zero-G droplet" },
  bloom: { title: "Bloom", sub: "Reaction-diffusion" },
  mesh: { title: "Mesh", sub: "Network graph" },
  orbits: { title: "Orbits", sub: "Keplerian orbits" },
  cymatics: { title: "Cymatics", sub: "Chladni figures" },
  mandelbrot: { title: "Mandelbrot", sub: "Fractal zoom" },
  kaleidoscope: { title: "Kaleidoscope", sub: "6-fold mirror" },
  lorenz: { title: "Lorenz", sub: "Strange attractor" },
  phyllotaxis: { title: "Phyllotaxis", sub: "Fibonacci spirals" },
  quasicrystal: { title: "Quasicrystal", sub: "Penrose tiling" },
  tunnel: { title: "Tunnel", sub: "Light rings" },
  ferrofluid: { title: "Ferrofluid", sub: "Spiky liquid metal" },
  polyhedron: { title: "Polyhedron", sub: "Morphing 3D shapes" },
  lightning: { title: "Lightning", sub: "Fractal bolts" },
  caustics: { title: "Caustics", sub: "Underwater light" },
  worleyTerrain: { title: "Terrain", sub: "Voronoi mountains" },
  magnetic: { title: "Magnetic", sub: "Iron filings" },
  lSystem: { title: "L-System", sub: "Plant growth" },
  truchet: { title: "Truchet", sub: "Tile patterns" },
  sunburst: { title: "Sunburst", sub: "Radiating rays" },
  spirograph: { title: "Spirograph", sub: "Hypotrochoid" },
  lissajousSymph: { title: "Lissajous Symphony", sub: "Interfering oscillators" },
  ribbonTracer: { title: "Ribbon Tracer", sub: "3D Lissajous ribbons" },
  galacticCore: { title: "Galactic Core", sub: "Binary orbit" },
  pendulumCasc: { title: "Pendulum Cascade", sub: "Coupled chaos" },
  bifrost: { title: "Bifrost", sub: "Iridescent thin-film" },
  kaleidoTunnel: { title: "Kaleido Tunnel", sub: "Prismatic tunnel" },
  mandala: { title: "Mandala", sub: "Sacred pattern" },
  phantasm: { title: "Phantasm", sub: "Spectral silhouette" },
  honeycombTide: { title: "Honeycomb Tide", sub: "Hexagonal wave field" },
  equalCathedral: { title: "Equal Cathedral", sub: "Stained glass" },
  phonograph: { title: "Phonograph", sub: "Spiral record groove" },
  cymaticsPlate: { title: "Cymatics Plate", sub: "Standing waves on plate" },
  aurora3D: { title: "Aurora 3D", sub: "Volumetric ribbons" },
  soundWave3D: { title: "Sound Wave 3D", sub: "Spatial pressure field" },
  cloth: { title: "Cloth", sub: "GPU physics simulation" },
  coral: { title: "Coral", sub: "Branching wireframe" },
  liquidMetalVol: { title: "Liquid Metal Vol", sub: "Volumetric blobs" },
  vines: { title: "Vines", sub: "Twisting growth" },
  neuralNet: { title: "Neural Net", sub: "Network dynamics" },
  smokeColumns: { title: "Smoke Columns", sub: "Rising particles" },
  metaballVol: { title: "Metaball Vol", sub: "Smooth-min blobs" },
  voronoiCrystal: { title: "Voronoi Crystal", sub: "Crystal cells" },
  teslaCoil: { title: "Tesla Coil", sub: "Sparks + magnetic field" },
  plasmaGlobe: { title: "Plasma Globe", sub: "Filament tendrils" },
  solarFlare: { title: "Solar Flare", sub: "CME eruption" },
  kleinFilm: { title: "Klein Film", sub: "Thin-film interference" },
  starForge: { title: "Star Forge", sub: "Stellar nursery" },
  voronoiBubbles: { title: "Voronoi Bubbles", sub: "Cellular foam" },
  windstream: { title: "Wind Stream", sub: "Trailing flow" },
  siliconSkin: { title: "Silicon Skin", sub: "Sphere pressed by fingers" },
  viscoelastic: { title: "Viscoelastic", sub: "Morphing 5-shape blob" },
  balloonPress: { title: "Balloon Press", sub: "Squash + dimple impact" },
  membraneDrum: { title: "Membrane Drum", sub: "Drum head w/ ripple hits" },
  punchBag: { title: "Punch Bag", sub: "Sack being struck" },
  siliconeFace: { title: "Silicone Face", sub: "Face pressed from inside" },
  liquidSkin: { title: "Liquid Skin", sub: "Torus rippling like water" },
  clayPress: { title: "Clay Press", sub: "Voxel grid deformed by pokes" },
  drumHead3D: { title: "Drum Head 3D", sub: "Ring of chasing dimples" },
  siliconeVeil: { title: "Silicone Veil", sub: "Sheet pressed against face" },
  liveVideo: { title: "Live Video", sub: "Selfie with audio FX" },
  myceliumDom: { title: "Mycelium DOM", sub: "Hyphae frame content" },
  dislocationField: { title: "Dislocation Field", sub: "Crystal grain boundaries" },
  marangoniInk: { title: "Marangoni Ink", sub: "Surface-tension-driven dye" },
  mcCollough: { title: "McCollough", sub: "Afterimage op-art" },
  hopfFibration: { title: "Hopf Fibration", sub: "S³ fibered by S²" },
  slitScanChrono: { title: "Slit-Scan Chrono", sub: "Time unrolled" },
  glass: { title: "Glass", sub: "Refractive DOM" },
  dna: { title: "DNA", sub: "Double helix" },
  lavaLamp: { title: "Lava Lamp", sub: "Metaballs" },
  bezier: { title: "Bezier", sub: "Flowing ribbon" },
  stereographic: { title: "Stereographic", sub: "Sphere projection" },
  contour: { title: "Contour", sub: "Topographic lines" },
  cosmos: { title: "Cosmos", sub: "Volumetric nebula" },
  galaxy: { title: "Galaxy", sub: "Spiral arms" },
  oceanStorm: { title: "Ocean Storm", sub: "Gerstner waves" },
  cyberpunk: { title: "Cyberpunk", sub: "Neon city" },
  crystalCave: { title: "Crystal Cave", sub: "Octahedra" },
  soapBubble: { title: "Soap Bubble", sub: "Thin-film iridescence" },
  voronoiGlass: { title: "Voronoi Glass", sub: "Cellular stained" },
  pixelSort: { title: "Pixel Sort", sub: "Glitch art" },
  wireframeSphere: { title: "Wireframe", sub: "Lat/lon sphere" },
  blackHole: { title: "Black Hole", sub: "Einstein ring" },
  liquidMetal: { title: "Liquid Metal", sub: "Mercury blob" },
  stainedGlass: { title: "Stained Glass", sub: "Rose window" },
  marbleStatue: { title: "Marble Statue", sub: "Veined stone" },
  psychedelic: { title: "Psychedelic", sub: "RGB warp" },
  glitch: { title: "Glitch", sub: "Datamosh" },
  quantumField: { title: "Quantum Field", sub: "HO probability" },
  particleConstellation: { title: "Constellation", sub: "Particles + trails" },
  inkInWater: { title: "Ink in Water", sub: "5 plumes" },
  wormholeTunnel: { title: "Wormhole", sub: "Tunnel of light" },
  ascii: { title: "ASCII", sub: "Terminal aesthetic" },
  inkDrop: { title: "Ink Drop", sub: "Falling with ripples" },
  gyroid: { title: "Gyroid", sub: "TPMS surface" },
  schwarzP: { title: "Schwarz P", sub: "TPMS surface" },
  mengerSponge: { title: "Menger Sponge", sub: "Fractal cube" },
  kleinBottle: { title: "Klein Bottle", sub: "Non-orientable" },
  conwayLife: { title: "Conway's Life", sub: "CA simulation" },
  stableFluid: { title: "Stable Fluid", sub: "Stam simulation" },
  doublePendulum: { title: "Double Pendulum", sub: "Chaos system" },
  sphericalHarmonics: { title: "Spherical Harmonics", sub: "Y_l^m" },
  bec: { title: "BEC", sub: "Condensate wave" },
  beatNebula: { title: "Beat Nebula", sub: "Beat-driven nebula" },
  phononLattice: { title: "Phonon Lattice", sub: "Spring-mass" },
  magneticPendulum: { title: "Magnetic Pendulum", sub: "3-magnet chaos" },
  schwarzD: { title: "Schwarz D", sub: "Diamond TPMS" },
  liquidCrystal: { title: "Liquid Crystal", sub: "Nematic director" },
  sincWaveField: { title: "Sinc Field", sub: "sin(x)/x" },
  sineGordon: { title: "Sine-Gordon", sub: "Soliton PDE" },
  skyrmion: { title: "Skyrmion", sub: "Topological spin" },
  hessianEigenvector: { title: "Hessian Field", sub: "Eigenvectors" },
  braggDiffraction: { title: "Bragg Diffraction", sub: "Reciprocal lattice" },
  curvatureFlow: { title: "Curvature Flow", sub: "Mean curvature PDE" },
  cellularPotts: { title: "Cellular Potts", sub: "Cell Monte Carlo" },
  waveFunctionCollapse: { title: "WFC", sub: "Tile generation" },
  phaseFieldCrystal: { title: "Phase Field Crystal", sub: "Density crystal" },
  lab: { title: "Lab", sub: "Hands-on sandbox" },
};


function attachSceneCardClicks(engine: Engine): void {
  // Wire canvas taps to navigate when the user taps a scene card. Scene
  // nodes that opt into this declare `clickScene: "<scene-key>"` on the
  // node itself; the landing's dock icons + CTAs use this to jump into
  // the underlying scene demos. We resolve at click time so the wiring
  // is automatic regardless of which scene is active.
  const canvas = engine.canvas;
  let downAt: { x: number; y: number; t: number } | null = null;
  canvas.addEventListener("pointerdown", (e) => {
    downAt = { x: e.clientX, y: e.clientY, t: performance.now() };
  }, { passive: true });
  canvas.addEventListener("pointerup", (e) => {
    if (!downAt) return;
    const dx = e.clientX - downAt.x;
    const dy = e.clientY - downAt.y;
    const dt = performance.now() - downAt.t;
    downAt = null;
    // Only register taps, not drags.
    if (Math.hypot(dx, dy) > 8 || dt > 500) return;
    const hit = hitTest(engine.scene, e.clientX, e.clientY);
    if (!hit) return;
    // Generic tap handler takes precedence — it's the most flexible.
    if (hit.onTap) {
      hit.onTap();
      return;
    }
    const target = (hit as { clickScene?: string }).clickScene;
    if (!target) return;
    switchScene(engine, target);
    // Sync the picker UI to the new scene.
    const row = document.getElementById("scene-row");
    if (row) {
      const wantLabel = target.toLowerCase();
      for (const c of Array.from(row.children)) {
        if (c instanceof HTMLButtonElement) {
          c.classList.toggle("active", c.textContent?.toLowerCase() === wantLabel);
        }
      }
    }
  }, { passive: true });
}

function attachResize(engine: Engine): void {
  const obs = new ResizeObserver(() => {
    if (!engine.ctx) return;
    resizeCanvas(engine.canvas, engine.ctx.gl, engine.ctx.dpr * engine.ctx.renderScale);
  });
  obs.observe(engine.canvas);
}

function main(): void {
  const canvas = document.getElementById("stage") as HTMLCanvasElement | null;
  if (!canvas) {
    document.body.innerHTML = "<pre style='color:red;padding:20px'>canvas missing</pre>";
    return;
  }
  // Diagnostic — show why WebGL2 is unavailable if it fails.
  const probe = canvas.getContext("webgl2");
  if (!probe) {
    const webgl1 = canvas.getContext("webgl");
    const dbg = [
      "WebGL2 unavailable. NSPP requires WebGL2.",
      "",
      "UA: " + navigator.userAgent,
      "Platform: " + navigator.platform,
      "WebGL1 available: " + (webgl1 ? "yes" : "no"),
    ];
    if (webgl1) {
      const ext = (webgl1 as WebGLRenderingContext).getExtension("WEBGL_debug_renderer_info");
      if (ext) {
        const gl1 = webgl1 as WebGLRenderingContext;
        dbg.push("WebGL1 renderer: " + gl1.getParameter(ext.UNMASKED_RENDERER_WEBGL));
        dbg.push("WebGL1 vendor: " + gl1.getParameter(ext.UNMASKED_VENDOR_WEBGL));
      }
    }
    dbg.push("");
    dbg.push("Try: chrome://gpu → check 'WebGL2: Hardware accelerated'");
    dbg.push("Or:  System Settings → Privacy & Security → Security → App Management");
    document.body.innerHTML = "<pre style='color:#f88;padding:20px;white-space:pre-wrap;font-size:12px;line-height:1.4'>" + dbg.join("\n") + "</pre>";
    return;
  }
  // Release the probe; setupEngine creates a fresh context.
  // (canvas.getContext is a singleton — same context is reused.)
  // Tap errors so we can show them on screen instead of hiding in console.
  const errs: string[] = [];
  const origErr = console.error;
  console.error = function (...args: unknown[]) {
    errs.push(args.map((a) => {
      if (typeof a === "string") return a;
      try { return JSON.stringify(a); } catch { return String(a); }
    }).join(" ").slice(0, 4000));
    origErr.apply(console, args);
  };
  window.addEventListener("error", (e) => {
    errs.push("ERROR: " + e.message + " at " + (e.filename || "?") + ":" + (e.lineno || "?"));
  });
  window.addEventListener("unhandledrejection", (e) => {
    errs.push("PROMISE: " + String((e as PromiseRejectionEvent).reason).slice(0, 2000));
  });
  const engine = setupEngine(canvas);
  if (!engine) {
    const msg = errs.length
      ? errs.join("\n\n---\n\n")
      : "setupEngine returned null. Check console for shader compile errors.";
    document.body.innerHTML =
      "<pre style='color:#f88;padding:20px;white-space:pre-wrap;font-size:11px;line-height:1.4;max-height:90vh;overflow:auto'>Pipeline init failed:\n\n" + msg + "</pre>";
    return;
  }

  // Initial scene from URL. Two equivalent forms are supported:
  //   /nspp/#drift        — bare hash
  //   /nspp/?scene=drift  — query string (used by the embed proxy)
  // Falls back to "landing" — a beautiful NSPP-rendered landing page
  // that explains the project.
  const _q = new URLSearchParams(location.search);
  const _sceneFromQuery = _q.get("scene");
  const _sceneFromHash = location.hash.replace(/^#/, "");
  const initialScene = _sceneFromHash || _sceneFromQuery || "landing";
  // Optional query-string flags for embed-mode tweaks:
  //   ?cinematic=1  — start with the cosmicDust cinematic camera on
  //                   (halation, lens flare, parallax dust, ACES tonemap).
  // We read this after engine construction (so we can mutate cinematicEnabled)
  // but apply it before the first frame renders.
  if (_q.get("cinematic") === "1") engine.cinematicEnabled = true;
  switchScene(engine, initialScene);
  attachInput(engine);
  attachResize(engine);
  attachScenePicker(engine);
  attachControls(engine);
  attachBeatMicToggle(engine);
  attachSplash(engine);
  attachSceneCardClicks(engine);
  attachCategoryMenu(engine);
  loop(engine);

  /**
   * Capture a single deterministic frame at virtual time `t` (seconds).
   * Used by the demo harness to fast-forward a scene without waiting on
   * requestAnimationFrame. Returns a Promise<Blob> with PNG-encoded pixels.
   *
   * The harness flow:
   *   1. Call `engine.__loadScene(jsonString)` to set the scene from JSON.
   *   2. For i in 0..N: await engine.__captureAt(i / fps).
   *   3. Feed resulting Blobs to MediaRecorder / WebCodecs encoder.
   *   4. Hand the resulting blob URL to a <video> for instant playback.
   *
   * Implementation rewinds `engine.timing` so that the next tickTiming()
   * call inside frame() returns the requested (t, 1/fps). After frame()
   * runs, we read the canvas via canvas.toBlob().
   */
  (engine as unknown as Record<string, unknown>).__loadScene = (json: string) => {
    try {
      const spec = JSON.parse(json) as unknown;
      const factory = (SCENES as Record<string, (w: number, h: number) => Scene>)[
        typeof spec === "object" && spec && (spec as { scene?: unknown }).scene
          ? String((spec as { scene: string }).scene)
          : "A"
      ];
      if (factory) {
        engine.scene = factory(engine.canvas.clientWidth, engine.canvas.clientHeight);
        engine.sceneName = String((spec as { scene: string }).scene ?? "A");
        engine.currentScene = engine.sceneName;
        engine.studio.selected = null;
      }
    } catch (e) {
      console.warn("[nspp] __loadScene parse failed:", e);
    }
  };

  (engine as unknown as Record<string, unknown>).__captureAt = (t: number, fps: number = 60): Promise<Blob> => {
    // Rewind the timing state so tickTiming() in frame() returns our values.
    const now = performance.now();
    engine.timing.startTime = now - t * 1000;
    engine.timing.lastTime = now - 1000 / fps;
    engine.timing.frames = Math.round(t * fps);
    return new Promise((resolve) => {
      // Run one frame with our desired (t, dt).
      frame(engine);
      // Read pixels back as PNG.
      engine.canvas.toBlob((b) => resolve(b ?? new Blob()), "image/png");
    });
  };

  // expose for tests + demo harness
  (window as unknown as { __nspp: unknown }).__nspp = engine;
}

// Avoid "value never read" warnings on SceneNode import in this file.
void (null as unknown as SceneNode);

main();